<template>
  <section id="dashboard-ecommerce">
    <b-modal
      id="modal-slip"
      title="Deposit Slip"
      centered
      cancel-variant="outline-secondary"
      hide-footer
      @hidden="resetSlipForm"
    >
      <template v-if="slipForm && slipObj">
        <b-form>
          <b-form-group
            label="Type"
            invalid-feedback="Type is required"
            ref="slip_type"
          >
            <v-select
              v-model="slipObj.type"
              :options="[
                { text: 'Deposit', value: 'deposit' },
                { text: 'Cash', value: 'cash' },
              ]"
              :clearable="false"
              label="text"
              :reduce="(val) => val.value"
            />
          </b-form-group>

          <b-form-group
            label="Previous Balance Received *"
            invalid-feedback="Amount is required"
            ref="slip_amount"
          >
            <b-form-input
              v-model="slipObj.amount"
              type="number"
              placeholder="Enter amount"
            />
          </b-form-group>

          <!-- <b-form-group
            label="Date *"
            invalid-feedback="Date is required"
            ref="slip_date"
          >
            <flat-pickr
              v-model="slipObj.date"
              :config="config"
              class="form-control"
              :placeholder="$t('Select Date')"
              disabled
            />
          </b-form-group> -->

          <b-form-group label="Note">
            <b-form-textarea
              v-model="slipObj.note"
              placeholder="Enter note"
              rows="3"
            />
          </b-form-group>

          <b-form-group
            label="Attachments *"
            invalid-feedback="Attachment is required"
            ref="slip_attach"
          >
            <b-button
              variant="primary"
              class="btn-icon"
              :disabled="slipSaving || docLoading"
              @click="$refs.slipDocs.click()"
            >
              <b-spinner v-if="docLoading" small variant="light" />
              <feather-icon v-else icon="PlusIcon" />
              <span> Upload</span>

              <!-- multiple -->
              <input
                type="file"
                id="slipDocs"
                hidden
                ref="slipDocs"
                multiple
                @change="addSlipImages"
                accept="image/*"
              />
            </b-button>
          </b-form-group>

          <div v-if="slipImages.length > 0" class="mt-1">
            <div class="d-flex flex-wrap" style="gap: 1rem 0.5rem">
              <div
                v-for="(img, index) in slipImages"
                :key="index"
                class="position-relative p-25 border"
              >
                <img
                  :src="getPicturePreview(img)"
                  width="100"
                  height="100"
                  style="object-fit: cover; cursor: pointer"
                  referrerpolicy="no-referrer"
                  alt="slip attachment"
                  @click="openAttachment(img)"
                  @error="onPicturePreviewError($event, img)"
                />
                <b-button
                  size="sm"
                  variant="danger"
                  class="position-absolute"
                  pill
                  style="top: -8px; right: -4px; padding: 2px 6px"
                  @click="removeSlipImage(index)"
                >
                  <feather-icon icon="XIcon" size="12" />
                </b-button>
              </div>
            </div>
          </div>
        </b-form>
        <b-row class="mt-2">
          <b-col class="d-flex justify-content-end" style="gap: 1rem">
            <b-button
              variant="outline-secondary"
              @click="resetSlipForm()"
              :disabled="slipSaving || docLoading"
            >
              <span class="align-middle">Cancel</span>
            </b-button>
            <b-button
              variant="primary"
              @click="saveSlip()"
              :disabled="slipSaving || docLoading"
            >
              <b-spinner v-if="slipSaving" small class="mx-1" />
              <span v-else class="align-middle">Save</span>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <b-row v-else-if="currentSlip">
        <b-col cols="12">
          <div class="d-flex flex-column">
            <div class="mb-1">
              <label class="font-weight-bold mb-0">Campus:</label>
              <b-badge variant="light-primary" class="ml-1">{{
                currentSlip.campusName
              }}</b-badge>
            </div>
            <div class="mb-1">
              <label class="font-weight-bold mb-0">Date:</label>
              <b-badge variant="light-primary" class="ml-1">{{
                new Date(currentSlip.date).toLocaleDateString("en-UK", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              }}</b-badge>
            </div>
            <div class="mb-1">
              <label class="font-weight-bold mb-0">Type:</label>
              <b-badge variant="light-primary" class="ml-1">{{
                currentSlip.type ? currentSlip.type : "-"
              }}</b-badge>
            </div>
            <div class="mb-1">
              <label class="font-weight-bold mb-0"
                >Previous Balance Received:</label
              >
              <b-badge variant="light-success" class="ml-1"
                >Rs. {{ currentSlip.amount.toLocaleString() }}</b-badge
              >
            </div>
            <div class="mb-1" v-if="currentSlip.note">
              <label class="font-weight-bold mb-0">Note:</label>
              <p class="mb-0 ml-1">{{ currentSlip.note }}</p>
            </div>
            <div v-if="currentSlip.attachment" class="mb-1">
              <label class="font-weight-bold mb-0">Attachment:</label>
              <div class="mt-1 d-flex flex-wrap" style="gap: 0.5rem">
                <div
                  v-for="(img, index) in slipAttachmentList(
                    currentSlip.attachment
                  )"
                  :key="'slip-view-' + index"
                  class="position-relative border rounded overflow-hidden"
                  style="width: 96px; height: 96px; cursor: pointer"
                  @click="openAttachment(img)"
                >
                  <img
                    :src="getPicturePreview(img)"
                    style="width: 100%; height: 100%; object-fit: cover"
                    referrerpolicy="no-referrer"
                    alt="slip attachment"
                    @error="onPicturePreviewError($event, img)"
                  />
                </div>
              </div>
            </div>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="d-flex justify-content-end border-top pt-1"
          style="gap: 1rem"
        >
          <b-button
            v-if="rights.edit"
            variant="primary"
            @click="openEditSlip()"
            :disabled="slipDeleting"
          >
            <feather-icon icon="EditIcon" />
            <span class="ml-50">Edit Slip</span>
          </b-button>
          <b-button
            v-if="rights.delete"
            variant="danger"
            @click="deleteSlip()"
            :disabled="slipDeleting"
          >
            <b-spinner v-if="slipDeleting" small variant="light" />
            <feather-icon v-else icon="TrashIcon" />
            <span class="ml-50">Delete Slip</span>
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <div class="dashboard-main">
      <dashboard-welcome-hero
        :loading="false"
        :user-name="dashboardUserName"
        :campus-name="dashboardCampusName"
        :profile-image="dashboardProfileImage"
        :academic-year="currentYear"
      />

      <template v-if="isTcfDomain">
        <template v-if="!settingLoading">
          <b-tabs v-if="isHeadOffice" pills class="mb-1">
            <b-tab :title="$t('Intermediate')" active>
              <ScholarshipOverview
                :campusID="$store.state.userData.cId"
                :isHeadOffice="isHeadOffice"
                tabValue="intermediate"
              />
            </b-tab>
            <b-tab
              :title="$t('PCP')"
              :lazy="isPcpLoaded"
              @click="isPcpLoaded = false"
            >
              <ScholarshipOverview
                :campusID="$store.state.userData.cId"
                :isHeadOffice="isHeadOffice"
                tabValue="pcp"
              />
            </b-tab>
            <b-tab
              :title="$t('UTP')"
              :lazy="isUtpLoaded"
              @click="isUtpLoaded = false"
            >
              <ScholarshipOverview
                :campusID="$store.state.userData.cId"
                :isHeadOffice="isHeadOffice"
                tabValue="utp"
              />
            </b-tab>
          </b-tabs>
          <b-tabs
            pills
            v-else-if="
              !utpCampusLoaded &&
              utpCampusIDs.includes($store.state.userData.cId)
            "
          >
            <b-tab :title="$t('Intermediate')" active>
              <ScholarshipOverview
                :campusID="$store.state.userData.cId"
                :isHeadOffice="isHeadOffice"
                tabValue="intermediate"
              />
            </b-tab>
            <b-tab
              :title="$t('UTP')"
              :lazy="isUtpLoaded"
              @click="isUtpLoaded = false"
            >
              <ScholarshipOverview
                :campusID="$store.state.userData.cId"
                :isHeadOffice="isHeadOffice"
                tabValue="utp"
              />
            </b-tab>
          </b-tabs>
          <ScholarshipOverview
            v-else
            :campusID="$store.state.userData.cId"
            :isHeadOffice="isHeadOffice"
            :tabValue="pcpOnly ? 'pcp' : 'intermediate'"
          />
        </template>
      </template>

      <statistics
        v-if="sectionVisible('statistics')"
        :loading="statsLoading && !dashHasCoreData"
        :data="dashHasCoreData ? dash : null"
        :otherData="otherData || {}"
        :visitorObj="showVisitor ? visitorObj : null"
        :show-collection-metrics="sectionVisible('collection')"
      />

      <Birthdays
        v-if="showBirthdaysAfterStatisticsFallback"
        class="mb-2"
        :campusID="$store.state.userData.cId"
        :show-donors="false"
      />

      <Birthdays
        v-if="isHappinessFoundationDomain"
        :campusID="$store.state.userData.cId"
        :show-donors="true"
      />
      <EventCalendar
        v-if="isHappinessFoundationDomain"
        class="mb-2"
      />
      <!-- <b-row class="match-height">
        <b-col lg="4" md="4">
          <ecommerce-earnings-chart
            :data="dash"
            :monthlyData="monthlyData"
            :monthlySeries="monthlySeries"
          />
        </b-col>
        <b-col lg="8" md="8">
          <ecommerce-statistics :data="dash" :otherData="otherData" />
        </b-col>
      </b-row> -->

      <b-row class="dash-dues-row match-height" v-if="sectionVisible('dues')">
        <b-col lg="7" md="12" class="dash-dues-col">
          <b-card no-body class="dash-panel dash-dues-panel dash-dues-panel--modern h-100">
            <b-card-header class="dash-panel__header dash-dues-panel__header border-0">
              <div class="dash-panel__heading dash-panel__heading--dues">
                <span class="dash-panel__icon dash-panel__icon--dues">
                  <feather-icon icon="PieChartIcon" size="18" />
                </span>
                <div class="dash-panel__heading-main">
                  <b-card-title class="dash-panel__title mb-0">
                    {{ $t("Outstanding Dues") }}
                  </b-card-title>
                  <small class="dash-panel__subtitle">{{
                    $t("Fee breakdown by type")
                  }}</small>
                </div>
                <div
                  v-if="feeItems.length && !feeLoading"
                  class="dash-dues-total-pill dash-dues-total-pill--dock"
                >
                  <span class="dash-dues-total-pill__label">{{ $t("Total") }}</span>
                  <strong>Rs. {{ feeDuesTotalFormatted }}</strong>
                </div>
              </div>
            </b-card-header>
            <b-card-body class="dash-dues-panel__body dash-dues-panel__body--modern">
              <div v-if="feeLoading" class="dash-dues-busy py-5">
                <b-spinner variant="primary" />
                <span class="ml-1 text-muted">{{ $t("Loading") }}...</span>
              </div>
              <div
                v-else-if="!feeItems.length"
                class="dash-dues-empty-modern"
              >
                <feather-icon icon="InboxIcon" size="36" class="text-muted mb-50" />
                <p class="mb-0 text-muted">{{ $t("No records found") }}</p>
              </div>
              <div
                v-else
                class="dash-dues-scroll"
                :style="{ minHeight: duesChartBodyHeight + 'px' }"
              >
                <div class="dash-dues-mosaic">
                  <article
                    v-for="(row, idx) in feeItems"
                    :key="row.feeType + '-' + idx"
                    class="dash-dues-tile"
                    :style="{ '--tile-delay': `${idx * 0.08}s` }"
                  >
                    <div class="dash-dues-tile__body">
                      <h3 class="dash-dues-tile__title">{{ row.feeType }}</h3>
                      <p class="dash-dues-tile__amount">
                        Rs. {{ formatFeeAmount(row.amount) }}
                      </p>
                      <span class="dash-dues-tile__students">
                        {{ row.students }} {{ $t("Students") }}
                      </span>
                    </div>
                    <div
                      class="dash-dues-tile__ring-wrap"
                      :aria-label="`${feeDuesSharePercent(row.amount)}%`"
                    >
                      <svg
                        class="dash-dues-tile__svg"
                        viewBox="0 0 44 44"
                        aria-hidden="true"
                      >
                        <circle
                          class="dash-dues-tile__track"
                          cx="22"
                          cy="22"
                          r="18"
                        />
                        <circle
                          class="dash-dues-tile__progress"
                          cx="22"
                          cy="22"
                          r="18"
                          :stroke="feeDuesAccentColor(idx)"
                          :style="{
                            '--pct': feeDuesSharePercent(row.amount),
                            '--ring-delay': `${idx * 0.1}s`,
                          }"
                        />
                      </svg>
                      <span class="dash-dues-tile__ring-inner">
                        {{ feeDuesSharePercent(row.amount) }}%
                      </span>
                    </div>
                  </article>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col lg="5" md="12" class="dash-dues-col">
          <b-card no-body v-if="subVisible.includes(reportDomain)" class="dash-panel h-100">
            <b-card-header class="dash-panel__header border-0">
              <b-card-title class="dash-panel__title mb-0"
                >Staff Student Statistics</b-card-title
              >
            </b-card-header>
            <b-table
              class=""
              sticky-header="350px"
              :busy="subLoading"
              :items="subData"
              :fields="subFields"
              show-empty
              responsive
            >
              <template #table-busy>
                <div class="text-center">
                  <b-spinner variant="primary" />
                </div>
              </template>

              <template #cell(subject)="data">
                <b-badge variant="light-primary">
                  {{ data.value }}
                </b-badge>
              </template>
              <template #cell(totalStudents)="data">
                <b-badge variant="light-primary">
                  {{ data.value }}
                </b-badge>
              </template>
            </b-table>
          </b-card>

          <fee-bar-chart v-else class="dash-dues-chart-wrap h-100" :items="feeItems" />
        </b-col>
      </b-row>

      <collection-insights-panel
        v-if="showCollectionInsights"
        :weekly-data="weeklyData"
        :today-collection="dash.collection"
        :today-receipts="dash.collectionCount"
      />

      <Birthdays
        v-if="showBirthdaysAboveStudentStrength"
        class="mb-2"
        :campusID="$store.state.userData.cId"
        :show-donors="false"
      />

      <dashboard-academics-hub
        v-if="showAcademicsHub"
        :loading="academicsHubLoading"
        :strength-rows="academicsData.strengthRows"
        :strength-totals="academicsData.strengthTotals"
        :leave-rows="academicsData.leaveRows"
        :student-req-rows="academicsData.studentReqRows"
        :sub-data="subData"
      />

      <dashboard-comms-trio v-if="showAcademicsHub" class="mb-1" />

      <b-row
        v-if="showCalendarDemographicsRow"
        class="match-height dash-calendar-demo-row mb-1"
      >
        <b-col
          v-if="showEventsCalendar"
          :lg="showColorsDemographics ? 6 : 12"
          cols="12"
          class="mb-1 d-flex dash-calendar-col"
        >
          <dashboard-events-calendar :visible="true" compact />
        </b-col>
        <b-col
          v-if="showColorsDemographics"
          :lg="showEventsCalendar ? 6 : 12"
          cols="12"
          class="mb-1"
        >
          <b-card
            no-body
            class="hcd-card h-100"
            :class="{ 'hcd-card--paired': colorsDemographicsPaired }"
          >
            <b-card-header class="hcd-card-header pb-0">
              <div
                class="d-flex align-items-center justify-content-between w-100 flex-wrap"
              >
                <div class="d-flex align-items-center mb-50 mb-md-0">
                  <div class="avatar bg-light-primary rounded mr-1 p-75">
                    <feather-icon
                      icon="PieChartIcon"
                      size="24"
                      class="text-primary"
                    />
                  </div>
                  <div>
                    <h3 class="mb-0 hcd-card__title">
                      House Colors Demographics
                    </h3>
                    <small class="text-muted"
                      >Student distribution by house &amp; class</small
                    >
                  </div>
                </div>
                <b-badge
                  v-if="
                    colorsDemographics &&
                    colorsDemographics.totalCount != null
                  "
                  variant="primary"
                  pill
                  class="px-1 hcd-total-badge"
                >
                  {{ colorsDemographics.totalCount.toLocaleString() }} Students
                </b-badge>
              </div>
            </b-card-header>

            <b-card-body class="pt-2 hcd-card__body">
              <div v-if="colorsDemographicsLoading" class="hcd-skeleton">
                <div class="hcd-skeleton__houses">
                  <div
                    v-for="i in 4"
                    :key="'hcd-sk-h-' + i"
                    class="dash-dash-skel-block hcd-skeleton__house"
                  />
                </div>
                <div class="hcd-skeleton__chart-wrap">
                  <div class="dash-dash-skel-block hcd-skeleton__chart-head" />
                  <div class="hcd-skeleton__bars">
                    <div
                      v-for="j in 7"
                      :key="'hcd-sk-bar-' + j"
                      class="hcd-skeleton__bar-row"
                    >
                      <span class="dash-dash-skel-block hcd-skeleton__bar-label" />
                      <span
                        class="dash-dash-skel-block hcd-skeleton__bar-fill"
                        :style="{ width: 28 + j * 7 + '%' }"
                      />
                    </div>
                  </div>
                </div>
                <div class="dash-dash-skel-block hcd-skeleton__table" />
              </div>

              <div
                v-else-if="
                  colorsDemographics && colorsDemographics.houses.length
                "
              >
                <b-row class="hcd-top-row match-height">
                  <b-col
                    :lg="colorsDemographicsPaired ? 12 : 4"
                    :md="colorsDemographicsPaired ? 12 : 5"
                    cols="12"
                    class="mb-1 mb-lg-0"
                  >
                    <div class="hcd-stats-panel">
                      <div
                        v-for="house in colorsDemographics.houses"
                        :key="'stat-' + house.house"
                        class="hcd-stat-row"
                        :class="{
                          'hcd-stat-row-active':
                            selectedColorsHouse === house.house,
                        }"
                        :style="{ '--house-color': houseHexColor(house.house) }"
                        @click="selectColorsHouse(house.house)"
                      >
                        <span class="hcd-stat-dot" />
                        <div class="hcd-stat-body">
                          <div
                            class="d-flex align-items-center justify-content-between"
                          >
                            <span class="hcd-stat-name">{{ house.house }}</span>
                            <span class="hcd-stat-count">{{
                              house.count.toLocaleString()
                            }}</span>
                          </div>
                          <b-progress
                            :value="houseCountPercent(house.count)"
                            height="3px"
                            class="hcd-stat-bar mt-25"
                          />
                          <small class="hcd-stat-pct"
                            >{{ houseCountPercent(house.count) }}%</small
                          >
                        </div>
                      </div>
                    </div>
                  </b-col>

                  <b-col
                    :lg="colorsDemographicsPaired ? 12 : 8"
                    :md="colorsDemographicsPaired ? 12 : 7"
                    cols="12"
                  >
                    <div
                      class="hcd-chart-panel"
                      :style="
                        activeColorsHouse
                          ? {
                              '--house-color': houseHexColor(
                                activeColorsHouse.house
                              ),
                              borderColor: houseHexColor(
                                activeColorsHouse.house
                              ),
                            }
                          : null
                      "
                    >
                      <div class="hcd-chart-head">
                        <div>
                          <span
                            class="hcd-chart-title"
                            :style="
                              activeColorsHouse
                                ? {
                                    color: houseHexColor(
                                      activeColorsHouse.house
                                    ),
                                  }
                                : null
                            "
                          >
                            {{ activeColorsHouse ? activeColorsHouse.house : "" }}
                            House
                          </span>
                          <small class="text-muted d-block"
                            >Class-wise distribution</small
                          >
                        </div>
                        <b-badge
                          v-if="activeColorsHouse"
                          :style="{
                            background: houseHexColor(activeColorsHouse.house),
                            border: 'none',
                          }"
                          pill
                        >
                          {{ activeColorsHouse.count }} students
                        </b-badge>
                      </div>
                      <vue-apex-charts
                        v-if="
                          activeColorsHouse &&
                          activeColorsHouse.classes.length &&
                          !colorsChartBooting
                        "
                        :key="
                          'hcd-bar-' +
                          activeColorsHouse.house +
                          '-' +
                          colorsChartEpoch
                        "
                        type="bar"
                        :height="colorsHouseClassBarHeight"
                        :options="colorsHouseClassBarOptions"
                        :series="colorsHouseClassBarSeries"
                      />
                      <div
                        v-else-if="
                          activeColorsHouse &&
                          activeColorsHouse.classes.length &&
                          colorsChartBooting
                        "
                        class="hcd-chart-skeleton"
                      >
                        <div
                          v-for="j in Math.min(
                            8,
                            activeColorsHouse.classes.length
                          )"
                          :key="'hcd-live-sk-' + j"
                          class="hcd-chart-skeleton__row"
                        >
                          <span
                            class="dash-dash-skel-block hcd-chart-skeleton__label"
                          />
                          <span
                            class="dash-dash-skel-block hcd-chart-skeleton__bar"
                            :style="{ width: 22 + j * 8 + '%' }"
                          />
                        </div>
                      </div>
                      <div v-else class="hcd-chart-empty">
                        No class data available.
                      </div>
                    </div>
                  </b-col>
                </b-row>

                <div
                  v-if="activeColorsHouse"
                  class="hcd-data-panel mt-2"
                  :class="{ 'hcd-data-panel--paired': colorsDemographicsPaired }"
                >
                  <div class="hcd-data-head">
                    <h6 class="mb-0">
                      <feather-icon
                        icon="UsersIcon"
                        size="16"
                        class="text-primary mr-50"
                      />
                      {{ activeColorsHouse.house }} House — Students
                    </h6>
                    <small class="text-muted"
                      >{{ activeColorsStudentRows.length }} records</small
                    >
                  </div>
                  <div class="hcd-table-wrap">
                    <b-table
                      v-if="activeColorsStudentRows.length"
                      :items="activeColorsStudentRows"
                      :fields="colorsStudentFields"
                      small
                      striped
                      hover
                      responsive
                      class="hcd-student-table mb-0"
                    >
                      <template #cell(gender)="data">
                        <b-badge
                          :variant="
                            data.value === 'Female'
                              ? 'light-danger'
                              : 'light-info'
                          "
                          pill
                          class="hcd-gender-badge"
                        >
                          {{ data.value }}
                        </b-badge>
                      </template>
                    </b-table>
                    <div v-else class="hcd-empty-state">
                      No students listed for this house.
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="text-center py-3">
                <feather-icon
                  icon="InboxIcon"
                  size="42"
                  class="text-muted mb-1"
                />
                <p class="mb-0 text-muted">
                  No house demographics data available.
                </p>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <hold-fees-stats-panel
        v-if="showHoldFeesPanel"
        :campusID="$store.state.userData.cId"
      />

      <!-- Legacy inline scholarship (TCF uses ScholarshipOverview near top) -->
      <template v-if="false">
        <b-card no-body v-if="isHeadOffice">
          <b-card-header
            class="p-1 d-flex align-items-center justify-content-between flex-wrap"
          >
            <div class="d-flex align-items-center flex-wrap">
              <h4 class="mb-0 mr-1">Scholarship Statistics</h4>
              <b-button
                variant="outline-primary"
                size="sm"
                :disabled="
                  scholarshipLoading ||
                  scholarshipPrinting ||
                  scholarshipExporting
                "
                @click="$bvModal.show('modal-scholarship-filter')"
              >
                <feather-icon icon="FilterIcon" class="mr-50" />
                {{ $t("Filter") }}
              </b-button>
            </div>
            <div class="d-flex align-items-center flex-wrap mt-25 mt-sm-0">
              <b-button
                variant="outline-primary"
                size="sm"
                :disabled="
                  scholarshipLoading ||
                  scholarshipPrinting ||
                  scholarshipExporting
                "
                @click="printScholarshipSummary(false)"
              >
                <b-spinner
                  v-if="scholarshipPrinting"
                  class="mr-50"
                  variant="primary"
                  small
                />
                <feather-icon
                  v-else
                  icon="PrinterIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t("Print") }}
              </b-button>
              <b-button
                variant="outline-success"
                size="sm"
                class="ml-50"
                :disabled="
                  scholarshipLoading ||
                  scholarshipPrinting ||
                  scholarshipExporting
                "
                @click="printScholarshipSummary(true)"
              >
                <b-spinner
                  v-if="scholarshipExporting"
                  class="mr-50"
                  variant="success"
                  small
                />
                <feather-icon
                  v-else
                  icon="DownloadIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t("Excel") }}
              </b-button>
            </div>
          </b-card-header>

          <b-card-body class="px-1 pb-1">
            <b-row v-if="scholarshipLoading">
              <b-col md="3" v-for="n in 4" :key="n">
                <div class="scholarship-skeleton-loader"></div>
              </b-col>
            </b-row>

            <b-row v-else class="scholarship-stats-card">
              <!-- Card 1: Total Assigned -->
              <b-col xl="3" md="6" cols="12">
                <div class="scholarship-card-new border-top-primary">
                  <div
                    class="d-flex justify-content-between align-items-center mb-25"
                  >
                    <span class="scholarship-card-title">Total Assigned</span>
                    <div
                      class="scholarship-card-icon-container bg-light-primary"
                    >
                      <feather-icon icon="UsersIcon" size="20" />
                    </div>
                  </div>
                  <h2
                    class="scholarship-card-value font-weight-bolder text-primary"
                  >
                    {{ scholarshipTotals.assigned }}
                  </h2>
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div v-for="(cls, cInd) in assignedStrength" :key="cInd">
                      <span>{{ cls.className }}:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        cls.strength
                      }}</span>
                    </div>
                  </div>
                  <hr class="scholarship-card-divider" />
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div>
                      <div>
                        Male:
                        <span class="font-weight-bolder text-dark"
                          >{{ genderStats.assignedMalePerc }}%</span
                        >
                      </div>
                      <div class="font-weight-bolder text-dark">
                        ({{ genderStats.assignedMale }})
                      </div>
                    </div>
                    <div class="text-right">
                      <div>
                        Female:
                        <span class="font-weight-bolder text-dark"
                          >{{ genderStats.assignedFemalePerc }}%</span
                        >
                      </div>
                      <div class="font-weight-bolder text-dark">
                        ({{ genderStats.assignedFemale }})
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>

              <!-- Card 2: Total Accepted -->
              <b-col xl="3" md="6" cols="12">
                <div class="scholarship-card-new border-top-success">
                  <div
                    class="d-flex justify-content-between align-items-center mb-25"
                  >
                    <span class="scholarship-card-title">Total Accepted</span>
                    <div
                      class="scholarship-card-icon-container bg-light-success"
                    >
                      <feather-icon icon="CheckCircleIcon" size="20" />
                    </div>
                  </div>
                  <h2
                    class="scholarship-card-value font-weight-bolder text-success"
                  >
                    {{ scholarshipTotals.accepted }}
                    <span class="font-small-3 text-dark"
                      >({{ scholarshipTotals.acceptedPerc }}%)</span
                    >
                  </h2>
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div v-for="(cls, cInd) in acceptedStrength" :key="cInd">
                      <span>{{ cls.className }}:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        cls.acceptedCount
                      }}</span>
                    </div>
                  </div>
                  <hr class="scholarship-card-divider" />
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div>
                      <div>
                        Male:
                        <span class="font-weight-bolder text-dark"
                          >{{ genderStats.acceptedMalePerc }}%</span
                        >
                      </div>
                      <div class="font-weight-bolder text-dark">
                        ({{ genderStats.acceptedMale }})
                      </div>
                    </div>
                    <div class="text-right">
                      <div>
                        Female:
                        <span class="font-weight-bolder text-dark"
                          >{{ genderStats.acceptedFemalePerc }}%</span
                        >
                      </div>
                      <div class="font-weight-bolder text-dark">
                        ({{ genderStats.acceptedFemale }})
                      </div>
                    </div>
                  </div>
                  <hr class="scholarship-card-divider" />
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div>
                      <span>App:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        scholarshipTotals.acceptedAppLogin
                      }}</span>
                    </div>
                    <div class="text-right">
                      <span>Admin:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        scholarshipTotals.acceptedAdminPortal
                      }}</span>
                    </div>
                  </div>
                </div>
              </b-col>

              <!-- Card 3: Not Accepted -->
              <b-col xl="3" md="6" cols="12">
                <div class="scholarship-card-new border-top-warning">
                  <div
                    class="d-flex justify-content-between align-items-center mb-25"
                  >
                    <span class="scholarship-card-title">Not Accepted</span>
                    <div
                      class="scholarship-card-icon-container bg-light-warning"
                    >
                      <feather-icon icon="XCircleIcon" size="20" />
                    </div>
                  </div>
                  <h2
                    class="scholarship-card-value font-weight-bolder text-warning"
                  >
                    {{ scholarshipTotals.notAccepted }}
                  </h2>
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div v-for="(cls, cInd) in notAcceptedStrength" :key="cInd">
                      <span>{{ cls.className }}:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        cls.stdCount
                      }}</span>
                    </div>
                  </div>
                  <hr class="scholarship-card-divider" />
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div>
                      <div>
                        Male:
                        <span class="font-weight-bolder text-dark"
                          >{{ genderStats.notAcceptedMalePerc }}%</span
                        >
                      </div>
                      <div class="font-weight-bolder text-dark">
                        ({{ genderStats.notAcceptedMale }})
                      </div>
                    </div>
                    <div class="text-right">
                      <div>
                        Female:
                        <span class="font-weight-bolder text-dark"
                          >{{ genderStats.notAcceptedFemalePerc }}%</span
                        >
                      </div>
                      <div class="font-weight-bolder text-dark">
                        ({{ genderStats.notAcceptedFemale }})
                      </div>
                    </div>
                  </div>
                  <hr class="scholarship-card-divider" />
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div>
                      <span>N/A:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        remainingNotAccepted
                      }}</span>
                    </div>
                    <div class="text-right">
                      <span>Left:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        scholarshipTotals.notAcceptedLeft
                      }}</span>
                    </div>
                  </div>
                </div>
              </b-col>

              <!-- Card 4: Student Status -->
              <b-col xl="3" md="6" cols="12">
                <div class="scholarship-card-new border-top-info">
                  <div
                    class="d-flex justify-content-between align-items-center mb-25"
                  >
                    <span class="scholarship-card-title">Student Status</span>
                    <div class="scholarship-card-icon-container bg-light-info">
                      <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        stroke="currentColor"
                        stroke-width="2"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-info"
                      >
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                        <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"></path>
                      </svg>
                    </div>
                  </div>
                  <h2
                    class="scholarship-card-value font-weight-bolder text-info"
                  >
                    {{ scholarshipTotals.active }}
                  </h2>
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div>
                      <span>Active:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        scholarshipTotals.active
                      }}</span>
                    </div>
                    <div class="text-right">
                      <span>Probation:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        scholarshipTotals.onProbation
                      }}</span>
                    </div>
                  </div>
                  <hr class="scholarship-card-divider" />
                  <div
                    class="d-flex justify-content-between text-dark font-small-3"
                  >
                    <div>
                      <span>Inactive:</span>
                      <span class="font-weight-bolder text-dark ml-25">{{
                        scholarshipTotals.inactive
                      }}</span>
                    </div>
                    <div class="text-right">
                      <span>Rate:</span>
                      <span class="font-weight-bolder text-dark ml-25">
                        {{
                          (
                            (scholarshipTotals.active /
                              (scholarshipTotals.active +
                                scholarshipTotals.onProbation +
                                scholarshipTotals.inactive)) *
                              100 || 0
                          ).toFixed(1)
                        }}%
                      </span>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <b-row class="mt-75 match-height">
              <b-col xl="6" md="12" cols="12">
                <b-card no-body class="mb-1">
                  <b-table
                    ref="facultyStatsTable"
                    class="mb-0 scholarship-card-table"
                    :items="classStats"
                    :fields="facultyStatsFields"
                    :busy="otherLoading"
                    responsive
                    outlined
                    show-empty
                  >
                    <template #table-busy>
                      <div class="text-center my-1">
                        <b-spinner variant="success" />
                      </div>
                    </template>

                    <template #cell(section)="{ item }">
                      {{ item.sectionName }}
                    </template>

                    <template
                      v-for="mid in facultyStatsDistinctClasses"
                      v-slot:[`cell(mdb_${mid})`]="{ item }"
                    >
                      <div v-if="getFacultyStatsForClass(item, mid)" class="">
                        <div>
                          {{
                            getFacultyStatsForClass(item, mid)
                              .acceptedRatioInSection
                          }}
                          %
                        </div>
                      </div>
                    </template>
                  </b-table>
                </b-card>
              </b-col>

              <b-col xl="6" md="12" cols="12">
                <b-card no-body class="mb-1">
                  <b-table
                    class="mb-0 h-100 scholarship-card-table"
                    table-class="h-100"
                    :items="[]"
                    :fields="[
                      {
                        key: 'result',
                        label: 'result',
                        thClass: 'bg-success text-white',
                      },
                    ]"
                    show-empty
                    responsive
                    outlined
                  >
                  </b-table>
                </b-card>
              </b-col>
            </b-row>
          </b-card-body>
          <b-table
            class=""
            sticky-header="350px"
            show-empty
            :items="scholarshipItems"
            :fields="scholarshipFields"
            responsive
            hover
            :busy="scholarshipLoading"
          >
            <template #table-busy>
              <div class="d-flex justify-content-center mb-3 mt-3">
                <b-spinner
                  style="width: 3rem; height: 3rem"
                  type="grow"
                  variant="primary"
                  label="Spinning"
                ></b-spinner>
              </div>
            </template>

            <template #head(accepted)="data">
              <div class="text-center">
                {{ data.label }}
                <div class="font-small-1 text-center">Total | App | Admin</div>
              </div>
            </template>

            <template #head(notAccepted)="data">
              <div class="text-center">
                {{ data.label }}
                <div class="font-small-1 text-center">Total | N/A | Left</div>
              </div>
            </template>

            <template #head(status)="data">
              <div class="text-center">
                {{ data.label }}
                <div class="font-small-1 text-center">
                  active | probation | inactive
                </div>
              </div>
            </template>

            <template #cell(campus)="data">
              <b-badge variant="light-primary">
                {{ data.item.campusName }}
              </b-badge>
            </template>
            <template #cell(assigned)="data">
              <b-badge variant="light-primary">
                {{ data.item.assigned }}
              </b-badge>
            </template>
            <template #cell(accepted)="data">
              <div class="text-center">
                <b-badge variant="light-success">
                  {{ data.item.accepted }}
                </b-badge>
                |
                <b-badge variant="light-warning" class="mr-25">
                  {{ data.item.acceptedAppLogin }}
                </b-badge>
                |
                <b-badge variant="light-info">
                  {{ data.item.acceptedAdminPortal }}
                </b-badge>
              </div>
            </template>
            <template #cell(notAccepted)="data">
              <div class="text-center">
                <b-badge variant="light-success">
                  {{ data.item.notAccepted }}
                </b-badge>
                |
                <b-badge variant="light-warning" class="mr-25">
                  {{ data.item.notAccepted - data.item.notAcceptedLeft }}
                </b-badge>
                |
                <b-badge variant="light-danger">
                  {{ data.item.notAcceptedLeft }}
                </b-badge>
              </div>
            </template>
            <template #cell(status)="data">
              <div class="text-center">
                <b-badge variant="light-success">
                  {{ data.item.active }}
                </b-badge>
                |
                <b-badge variant="light-warning" class="mr-25">
                  {{ data.item.onProbation }}
                </b-badge>
                |
                <b-badge variant="light-danger">
                  {{ data.item.inactive }}
                </b-badge>
              </div>
            </template>

            <template #cell(actions)="data">
              <b-button
                variant="outline-primary"
                class="btn-icon"
                @click="openScholarshipDetails(data.item)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
            </template>
          </b-table>
        </b-card>

        <!-- other campuses -->
        <ScholarshipCard
          v-else
          :campusID="selectedCampusId"
          :isHeadOffice="isHeadOffice"
          :scholarshipCategoryOptions="scholarshipCategoryOptions"
          :regionOptions="regionOptions"
        />
      </template>

      <PipelineInsights
        v-if="showPipelineInsights"
        :campusID="$store.state.userData.cId"
      />

      <ResultComparisonInsights
        v-if="sectionVisible('result-comparison')"
        :campusID="$store.state.userData.cId"
        :isHeadOffice="!!isHeadOffice"
        :campuses="campuses"
      />

      <b-row v-if="showAccountBalance" class="match-height">
        <b-col cols="12">
          <AccountBalanceCard class="mb-2" />
        </b-col>
      </b-row>

      <Birthdays
        v-if="showBirthdaysAboveAppDownloadStrength"
        class="mb-2 mt-2"
        :campusID="$store.state.userData.cId"
        :show-donors="false"
      />

      <!-- App Download Statistics -->
      <b-card
        no-body
        v-if="sectionVisible('app-downloads')"
        class="app-downloads-card mt-2 mb-2"
      >
        <b-card-header class="pb-0">
          <div class="d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center">
              <div class="avatar bg-light-primary rounded mr-1 p-75">
                <feather-icon icon="DownloadCloudIcon" size="24" />
              </div>
              <div>
                <h3 class="mb-0">App Download Statistics</h3>
                <small class="text-muted">Real-time download metrics</small>
              </div>
            </div>
            <div
              class="d-flex align-items-center flex-wrap"
              style="gap: 0.5rem"
            >
              <div
                v-if="showAppBundleSelector"
                class="app-bundle-select-wrapper"
              >
                <v-select
                  ref="appBundleSelect"
                  v-model="selectedAppBundleID"
                  :options="appBundleOptions"
                  :reduce="(val) => val.value"
                  label="label"
                  placeholder="Select App"
                  :searchable="false"
                  :clearable="false"
                  append-to-body
                  :calculate-position="appBundleDropdownPosition"
                  class="session-select-compact app-bundle-select"
                  @input="LoadAppDownloadStats()"
                />
              </div>
              <b-badge variant="light-success" pill class="badge-glow">
                <feather-icon icon="TrendingUpIcon" size="14" class="mr-25" />
                Active
              </b-badge>
            </div>
          </div>
        </b-card-header>

        <b-card-body v-if="downloadStats" class="pt-2">
          <!-- Summary Statistics Cards -->
          <b-row class="match-height mb-2">
            <b-col xl="3" md="6" cols="12">
              <div class="stats-card bg-gradient-primary">
                <div class="d-flex align-items-center">
                  <div class="avatar bg-white rounded p-50 mr-1">
                    <feather-icon
                      icon="DownloadIcon"
                      size="24"
                      class="text-primary"
                    />
                  </div>
                  <div class="flex-grow-1">
                    <small class="text-white d-block mb-25"
                      >Total Downloads</small
                    >
                    <h2 class="text-white mb-0 font-weight-bolder">
                      {{ downloadStats.totalDownloads.toLocaleString() }}
                    </h2>
                  </div>
                </div>
                <div class="mt-1">
                  <div class="d-flex justify-content-between text-white mb-25">
                    <small
                      >Students:
                      {{ downloadStats.activeStudents.toLocaleString() }}</small
                    >
                    <small
                      >Staff:
                      {{ downloadStats.activeStaff.toLocaleString() }}</small
                    >
                  </div>
                  <b-progress
                    :value="downloadPercentage.students"
                    variant="light"
                    height="4px"
                    class="mb-25"
                  />
                </div>
              </div>
            </b-col>

            <b-col xl="3" md="6" cols="12">
              <div class="stats-card bg-gradient-success">
                <div class="d-flex align-items-center">
                  <div class="avatar bg-white rounded p-50 mr-1">
                    <feather-icon
                      icon="UsersIcon"
                      size="24"
                      class="text-success"
                    />
                  </div>
                  <div class="flex-grow-1">
                    <small class="text-white d-block mb-25">Active Users</small>
                    <h2 class="text-white mb-0 font-weight-bolder">
                      {{ downloadStats.totalActiveUsers.toLocaleString() }}
                    </h2>
                  </div>
                </div>
                <div class="mt-1">
                  <small class="text-white d-block mb-25">
                    {{ activationRate }}% Activation Rate
                  </small>
                  <b-progress
                    :value="parseFloat(activationRate)"
                    variant="light"
                    height="4px"
                  />
                </div>
              </div>
            </b-col>

            <b-col xl="3" md="6" cols="12">
              <div class="stats-card bg-gradient-info">
                <div class="d-flex align-items-center">
                  <div class="avatar bg-white rounded p-50 mr-1">
                    <feather-icon
                      icon="BookOpenIcon"
                      size="24"
                      class="text-info"
                    />
                  </div>
                  <div class="flex-grow-1">
                    <small class="text-white d-block mb-25"
                      >Student Strength</small
                    >
                    <h2 class="text-white mb-0 font-weight-bolder">
                      {{ downloadStats.studentStrength.toLocaleString() }}
                    </h2>
                  </div>
                </div>
                <div class="mt-1">
                  <div class="d-flex justify-content-between text-white mb-25">
                    <small>{{ studentDownloadRate }}% Downloaded</small>
                    <div class="d-flex" style="gap: 10px" v-if="!isHeadOffice">
                      <div
                        class="stats-circle-btn"
                        :class="{
                          'stats-circle-btn--loading':
                            appStatsOpeningKey === 'student-active',
                        }"
                        v-b-tooltip.hover.top="'Downloaded Students'"
                        @click.stop="openUserList('student', 'active')"
                      >
                        <b-spinner
                          v-if="appStatsOpeningKey === 'student-active'"
                          small
                          variant="primary"
                        />
                        <feather-icon
                          v-else
                          icon="UserCheckIcon"
                          size="16"
                          class="text-success"
                        />
                      </div>
                      <div
                        class="stats-circle-btn"
                        :class="{
                          'stats-circle-btn--loading':
                            appStatsOpeningKey === 'student-inactive',
                        }"
                        v-b-tooltip.hover.top="'Pending Students'"
                        @click.stop="openUserList('student', 'inactive')"
                      >
                        <b-spinner
                          v-if="appStatsOpeningKey === 'student-inactive'"
                          small
                          variant="primary"
                        />
                        <feather-icon
                          v-else
                          icon="UserXIcon"
                          size="16"
                          class="text-danger"
                        />
                      </div>
                    </div>
                  </div>
                  <b-progress
                    :value="parseFloat(studentDownloadRate)"
                    variant="light"
                    height="4px"
                  />
                </div>
              </div>
            </b-col>

            <b-col xl="3" md="6" cols="12">
              <div class="stats-card bg-gradient-warning">
                <div class="d-flex align-items-center">
                  <div class="avatar bg-white rounded p-50 mr-1">
                    <feather-icon
                      icon="BriefcaseIcon"
                      size="24"
                      class="text-warning"
                    />
                  </div>
                  <div class="flex-grow-1">
                    <small class="text-white d-block mb-25"
                      >Staff Strength</small
                    >
                    <h2 class="text-white mb-0 font-weight-bolder">
                      {{ downloadStats.staffStrength.toLocaleString() }}
                    </h2>
                  </div>
                </div>
                <div class="mt-1">
                  <div class="d-flex justify-content-between text-white mb-25">
                    <small>{{ staffDownloadRate }}% Downloaded</small>
                    <div class="d-flex" style="gap: 10px" v-if="!isHeadOffice">
                      <div
                        class="stats-circle-btn"
                        :class="{
                          'stats-circle-btn--loading':
                            appStatsOpeningKey === 'staff-active',
                        }"
                        v-b-tooltip.hover.top="'Downloaded Staff'"
                        @click.stop="openUserList('staff', 'active')"
                      >
                        <b-spinner
                          v-if="appStatsOpeningKey === 'staff-active'"
                          small
                          variant="primary"
                        />
                        <feather-icon
                          v-else
                          icon="UserCheckIcon"
                          size="16"
                          class="text-success"
                        />
                      </div>
                      <div
                        class="stats-circle-btn"
                        :class="{
                          'stats-circle-btn--loading':
                            appStatsOpeningKey === 'staff-inactive',
                        }"
                        v-b-tooltip.hover.top="'Pending Staff'"
                        @click.stop="openUserList('staff', 'inactive')"
                      >
                        <b-spinner
                          v-if="appStatsOpeningKey === 'staff-inactive'"
                          small
                          variant="primary"
                        />
                        <feather-icon
                          v-else
                          icon="UserXIcon"
                          size="16"
                          class="text-danger"
                        />
                      </div>
                    </div>
                  </div>
                  <b-progress
                    :value="parseFloat(staffDownloadRate)"
                    variant="light"
                    height="4px"
                  />
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Campus-wise Table -->
          <div class="campus-section-header">
            <div
              class="d-flex align-items-center justify-content-between flex-wrap"
            >
              <div class="d-flex align-items-center mb-1">
                <div class="avatar bg-light-primary rounded mr-75 p-50">
                  <feather-icon icon="MapPinIcon" size="20" />
                </div>
                <div>
                  <h5 class="mb-0">Campus-wise Breakdown</h5>
                  <small class="text-muted"
                    >{{ filteredCampusData.length }} of
                    {{ downloadItems.length }} campuses</small
                  >
                </div>
              </div>

              <div class="d-flex align-items-center" style="gap: 0.5rem">
                <!-- Session Filter -->
                <div
                  v-if="reportDomain == 'myskoolfarabicoa'"
                  class="session-filter-wrapper"
                >
                  <v-select
                    v-model="sessionID"
                    :options="sessions"
                    :reduce="(val) => val.id"
                    label="session"
                    placeholder="Session"
                    :searchable="false"
                    :clearable="false"
                    append-to-body
                    class="session-select-compact"
                    @input="LoadAppDownloadStats()"
                  />
                </div>

                <!-- Search Input -->
                <div class="search-input-wrapper">
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                    class="search-icon"
                  />
                  <b-form-input
                    v-model="campusSearchQuery"
                    placeholder="Search campus..."
                    class="search-input"
                  />
                  <b-button
                    v-if="campusSearchQuery"
                    variant="flat-secondary"
                    size="sm"
                    class="clear-btn"
                    @click="campusSearchQuery = ''"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </div>

                <!-- Sort Dropdown -->
                <b-dropdown
                  variant="outline-primary"
                  right
                  size="sm"
                  class="sort-dropdown"
                >
                  <template #button-content>
                    <feather-icon icon="SlidersIcon" size="16" class="mr-50" />
                    <span class="d-none d-sm-inline">Sort</span>
                  </template>
                  <b-dropdown-item @click="sortDownloadsByDirection(false)">
                    <feather-icon
                      icon="ArrowDownIcon"
                      size="14"
                      class="mr-50"
                    />
                    Highest to Lowest
                  </b-dropdown-item>
                  <b-dropdown-item @click="sortDownloadsByDirection(true)">
                    <feather-icon icon="ArrowUpIcon" size="14" class="mr-50" />
                    Lowest to Highest
                  </b-dropdown-item>
                  <b-dropdown-divider />
                  <b-dropdown-item @click="sortCampusData('studentDownloads')">
                    <feather-icon icon="BookOpenIcon" size="14" class="mr-50" />
                    Student Downloads
                  </b-dropdown-item>
                  <b-dropdown-item @click="sortCampusData('staffDownloads')">
                    <feather-icon
                      icon="BriefcaseIcon"
                      size="14"
                      class="mr-50"
                    />
                    Staff Downloads
                  </b-dropdown-item>
                  <b-dropdown-item @click="sortCampusData('branch')">
                    <feather-icon icon="MapPinIcon" size="14" class="mr-50" />
                    Campus Name
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </div>

          <b-table
            sticky-header="450px"
            :busy="downloadLoading"
            :items="filteredCampusData"
            :fields="campusFields"
            show-empty
            responsive
            hover
            class="campus-download-table mb-0"
          >
            <template #table-busy>
              <div class="d-flex justify-content-center my-3">
                <b-spinner
                  style="width: 3rem; height: 3rem"
                  type="grow"
                  variant="primary"
                  label="Loading"
                />
              </div>
            </template>

            <!-- Campus Column -->
            <template #cell(branch)="data">
              <div class="campus-cell">
                <div class="d-flex align-items-center">
                  <!-- Rank Badge -->
                  <div
                    class="rank-badge"
                    :class="getRankClass(data.index + 1)"
                    v-b-tooltip.hover.right
                    :title="`Rank #${data.index + 1} by Total Downloads`"
                  >
                    <feather-icon
                      v-if="data.index === 0"
                      icon="AwardIcon"
                      size="14"
                    />
                    <feather-icon
                      v-else-if="data.index === 1"
                      icon="StarIcon"
                      size="14"
                    />
                    <feather-icon
                      v-else-if="data.index === 2"
                      icon="TrendingUpIcon"
                      size="14"
                    />
                    <span v-else class="rank-number">{{ data.index + 1 }}</span>
                  </div>

                  <!-- Campus Info -->
                  <div class="campus-info ml-1">
                    <div class="d-flex align-items-center">
                      <div class="campus-icon-wrapper">
                        <feather-icon
                          icon="MapPinIcon"
                          size="18"
                          class="campus-icon"
                        />
                      </div>
                      <div>
                        <div class="campus-name">{{ data.item.branch }}</div>
                        <small class="campus-subtitle">{{
                          data.item.campusName
                        }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Total Downloads Column -->
            <template #cell(totalDownloads)="data">
              <div class="download-cell">
                <div class="download-main">
                  <div
                    class="download-icon-badge"
                    :class="getDownloadClass(data.value)"
                  >
                    <feather-icon icon="DownloadCloudIcon" size="16" />
                  </div>
                  <div class="download-info">
                    <div class="download-count">{{ data.value }}</div>
                    <div class="download-label">Downloads</div>
                  </div>
                </div>

                <!-- Performance Indicator -->
                <div
                  class="performance-tag"
                  :class="getPerformanceClass(data.value)"
                >
                  <feather-icon
                    :icon="getPerformanceIcon(data.value)"
                    size="12"
                    class="mr-25"
                  />
                  <span>{{ getPerformanceText(data.value) }}</span>
                </div>
              </div>
            </template>

            <!-- Student Downloads Column -->
            <template #cell(studentDownloads)="data">
              <div class="progress-cell">
                <div class="progress-header">
                  <div class="progress-stats">
                    <div class="stat-icon student-icon">
                      <feather-icon icon="UserIcon" size="14" />
                    </div>
                    <div class="stat-numbers">
                      <span class="current-value">{{ data.value }}</span>
                      <span class="separator">/</span>
                      <span class="total-value">{{
                        data.item.studentStrength
                      }}</span>
                    </div>
                  </div>
                  <div
                    class="progress-percentage"
                    :class="
                      getPercentageClass(
                        calculatePercentage(
                          data.value,
                          data.item.studentStrength
                        )
                      )
                    "
                  >
                    {{
                      calculatePercentage(
                        data.value,
                        data.item.studentStrength
                      )
                    }}%
                  </div>
                </div>

                <div
                  class="progress-bar-container"
                  v-b-tooltip.hover.top
                  :title="`${data.value} out of ${data.item.studentStrength} students have downloaded the app`"
                >
                  <b-progress
                    :value="
                      calculatePercentage(data.value, data.item.studentStrength)
                    "
                    :variant="
                      getProgressVariant(
                        calculatePercentage(
                          data.value,
                          data.item.studentStrength
                        )
                      )
                    "
                    height="6px"
                    class="modern-progress"
                  />
                </div>
              </div>
            </template>

            <!-- Staff Downloads Column -->
            <template #cell(staffDownloads)="data">
              <div class="progress-cell">
                <div class="progress-header">
                  <div class="progress-stats">
                    <div class="stat-icon staff-icon">
                      <feather-icon icon="BriefcaseIcon" size="14" />
                    </div>
                    <div class="stat-numbers">
                      <span class="current-value">{{ data.value }}</span>
                      <span class="separator">/</span>
                      <span class="total-value">{{
                        data.item.staffStrength
                      }}</span>
                    </div>
                  </div>
                  <div
                    class="progress-percentage"
                    :class="
                      getPercentageClass(
                        calculatePercentage(data.value, data.item.staffStrength)
                      )
                    "
                  >
                    {{
                      calculatePercentage(data.value, data.item.staffStrength)
                    }}%
                  </div>
                </div>

                <div
                  class="progress-bar-container"
                  v-b-tooltip.hover.top
                  :title="`${data.value} out of ${data.item.staffStrength} staff members have downloaded the app`"
                >
                  <b-progress
                    :value="
                      calculatePercentage(data.value, data.item.staffStrength)
                    "
                    :variant="
                      getProgressVariant(
                        calculatePercentage(data.value, data.item.staffStrength)
                      )
                    "
                    height="6px"
                    class="modern-progress"
                  />
                </div>
              </div>
            </template>

            <!-- Active Users Column -->
            <template #cell(activeUsers)="data">
              <div class="active-users-cell">
                <div class="active-total">
                  <div class="active-icon-wrapper">
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="16"
                      class="active-icon"
                    />
                  </div>
                  <div class="active-count">
                    {{ data.item.totalActiveUsers }}
                  </div>
                </div>

                <div class="active-breakdown">
                  <div class="breakdown-item students">
                    <feather-icon icon="UserIcon" size="12" />
                    <span>{{ data.item.activeStudents }}</span>
                  </div>
                  <div class="breakdown-separator">•</div>
                  <div class="breakdown-item staff">
                    <feather-icon icon="BriefcaseIcon" size="12" />
                    <span>{{ data.item.activeStaff }}</span>
                  </div>
                </div>
              </div>
            </template>

            <!-- Total Strength Column -->
            <template #cell(totalStrength)="data">
              <div class="strength-cell">
                <div class="strength-main" :class="getSizeClass(data.value)">
                  <div class="strength-icon-wrapper">
                    <feather-icon icon="UsersIcon" size="16" />
                  </div>
                  <div class="strength-info">
                    <div class="strength-count">{{ data.value }}</div>
                    <div class="strength-label">
                      {{ getSizeLabel(data.value) }}
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Empty State -->
            <template #empty>
              <div class="empty-state">
                <feather-icon icon="InboxIcon" size="48" class="empty-icon" />
                <p class="empty-text">No campuses found matching your search</p>
                <small class="empty-hint"
                  >Try adjusting your search criteria</small
                >
              </div>
            </template>
          </b-table>
        </b-card-body>

        <!-- Loading State -->
        <div
          v-else-if="downloadLoading"
          class="d-flex justify-content-center align-items-center"
          style="min-height: 400px"
        >
          <div class="text-center">
            <b-spinner
              style="width: 3rem; height: 3rem"
              type="grow"
              variant="primary"
            />
            <p class="mt-1 text-muted">Loading download statistics...</p>
          </div>
        </div>

        <!-- Error/No Data State -->
        <div
          v-else
          class="d-flex justify-content-center align-items-center"
          style="min-height: 400px"
        >
          <div class="text-center">
            <feather-icon
              icon="AlertCircleIcon"
              size="48"
              class="text-muted mb-1"
            />
            <p class="text-muted">No download statistics available</p>
          </div>
        </div>
      </b-card>

      <!-- pos stats -->
      <PosStats
        v-if="posVisible.includes(reportDomain)"
        :campusID="selectedCampusId"
      />

      <!-- Staff designation + gender stats (HO = all campuses, else single campus) -->
      <StaffStatsCard
        v-if="isHeadOffice !== null && staffDesignationStatsVisible"
        :is-head-office="!!isHeadOffice"
        :campus-id="$store.state.userData.cId"
      />

      <template v-if="isHeadOffice || attVisible.includes(reportDomain)">
        <!-- Student Statistics -->
        <b-card
          no-body
          v-if="isHeadOffice && sectionVisible('student-statistics')"
        >
          <b-card-header class="p-1">
            <div>
              <b-card-title
                >{{
                  reportDomain == "myskoolspell"
                    ? "Admission Statistics"
                    : "Student Statistics"
                }}
                <template v-if="amountVisible.includes(reportDomain)">
                  <b-button
                    variant="primary"
                    class="btn-icon ml-1"
                    size="sm"
                    @click="printSummary('Cashlog-Summary')"
                  >
                    Cashlog Register
                  </b-button>
                  <b-button
                    variant="primary"
                    class="btn-icon ml-1"
                    size="sm"
                    @click="printSummary('Cashlog-Classwise-Summary')"
                  >
                    Cashlog Class Summary
                  </b-button>
                </template>
              </b-card-title>
            </div>
            <div class="d-flex align-items-center">
              <feather-icon icon="CalendarIcon" size="16" />
              <flat-pickr
                v-model="rangeDate"
                :config="config"
                class="form-control flat-picker bg-transparent border-0 shadow-none cursor-pointer"
                :placeholder="$t('Select Date')"
                @on-change="setDate"
              />
            </div>
          </b-card-header>

          <b-card-body class="px-1 pb-1">
            <b-row>
              <b-col
                lg="7"
                md="12"
                class="d-flex flex-wrap order-1 order-lg-0"
                style="gap: 1rem 0.5rem"
              >
                <div class="border-right-secondary pr-50">
                  <div class="mb-25">
                    <span class="font-weight-bold">Inquiries: </span>
                    <b-badge variant="light-primary">
                      {{ summaryObj.inqTotal }}
                    </b-badge>
                  </div>
                  <div>
                    <span class="font-weight-bold">Completed: </span>
                    <b-badge variant="light-primary">
                      {{ summaryObj.inqCompTotal }}
                    </b-badge>
                  </div>
                </div>
                <div class="border-right-secondary pr-50">
                  <div class="mb-25">
                    <span class="font-weight-bold">Registrations: </span>
                    <b-badge variant="light-primary">
                      {{ summaryObj.regTotal }}
                    </b-badge>
                  </div>
                  <div>
                    <span class="font-weight-bold">Pending: </span>
                    <b-badge variant="light-primary">
                      {{ summaryObj.regPendingTotal }}
                    </b-badge>
                  </div>
                </div>
                <div class="border-right-secondary pr-50">
                  <div class="mb-25">
                    <span class="font-weight-bold">Admissions: </span>
                    <b-badge variant="light-primary">
                      {{ summaryObj.admTotal }}
                    </b-badge>
                  </div>
                  <div>
                    <span class="font-weight-bold">Pending: </span>
                    <b-badge variant="light-primary">
                      {{ summaryObj.admPendingTotal }}
                    </b-badge>
                  </div>
                </div>
                <div v-if="amountVisible.includes(reportDomain)">
                  <span class="font-weight-bold">Amount: </span>
                  <b-badge variant="light-primary">
                    {{ summaryObj.amountTotal + summaryObj.depositAmountTotal }}
                  </b-badge>
                </div>
              </b-col>
              <b-col
                lg="5"
                md="12"
                class="d-flex align-items-center justify-content-between pl-lg-0 mb-1 mb-lg-0"
              >
                <b-form-group class="flex-grow-1 pr-50 mb-0">
                  <v-select
                    multiple
                    :closeOnSelect="false"
                    v-model="campIDs"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="[{ id: 0, branch: 'All Campuses' }, ...campuses]"
                    :clearable="false"
                    :reduce="(val) => val.id"
                    label="branch"
                    placeholder="Select campus"
                    @input="setCamp()"
                  />
                </b-form-group>
                <b-button
                  variant="primary"
                  class="btn-icon"
                  :disabled="stLoading"
                  @click="LoadSummary()"
                  >show</b-button
                >
              </b-col>
            </b-row>
          </b-card-body>
          <b-table
            class=""
            sticky-header="650px"
            :busy="stLoading"
            :items="items"
            :fields="fields"
            show-empty
            responsive
            hover
          >
            <template #table-busy>
              <div class="d-flex justify-content-center my-3">
                <b-spinner
                  style="width: 3rem; height: 3rem"
                  type="grow"
                  variant="primary"
                  label="Spinning"
                ></b-spinner>
              </div>
            </template>

            <template #head(inquiries)="data">
              <div class="text-center">
                {{ data.label }}
                <div class="font-small-1 text-center">Total | Completed</div>
              </div>
            </template>
            <template #head(registrations)="data">
              <div class="text-center">
                {{ data.label }}
                <div class="font-small-1 text-center">Total | Pending</div>
              </div>
            </template>
            <template #head(admissions)="data">
              <div class="text-center">
                {{ data.label }}
                <div class="font-small-1 text-center">Total | Pending</div>
              </div>
            </template>
            <template #head(amount)="data">
              <div class="text-center">
                {{ data.label }}
                <div class="font-small-1 text-center">
                  Adm. amount | Prev bal
                </div>
              </div>
            </template>

            <template #cell(campus)="data">
              <b-badge variant="light-primary">
                {{ data.item.campusName }}
              </b-badge>
            </template>
            <template #cell(inquiries)="data">
              <div class="text-center">
                <b-badge variant="light-primary">
                  {{ data.value }}
                </b-badge>
                |
                <b-badge variant="light-success">
                  {{ data.item.inquiriesCompleted }}
                </b-badge>
              </div>
            </template>
            <template #cell(registrations)="data">
              <div class="text-center">
                <b-badge variant="light-primary">
                  {{ data.value }}
                </b-badge>
                |
                <b-badge variant="light-danger">
                  {{ data.item.registrationPending }}
                </b-badge>
              </div>
            </template>
            <template #cell(admissions)="data">
              <div class="text-center">
                <b-badge variant="light-primary">
                  {{ data.value }}
                </b-badge>
                |
                <b-badge variant="light-danger">
                  {{ data.item.admissionPending }}
                </b-badge>
              </div>
            </template>
            <template #cell(amount)="data">
              <b-badge variant="light-primary"> Rs. {{ data.value }} </b-badge>
              |
              <b-badge variant="light-primary">
                Rs.{{ data.item.depositAmount }}
              </b-badge>
            </template>
            <template #cell(total)="data">
              <b-badge variant="light-primary">
                Rs. {{ data.item.amount + data.item.depositAmount }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <div class="d-flex">
                <b-button
                  v-if="
                    slipData.some(
                      (slip) => slip.campusID === data.item.campusID
                    )
                  "
                  variant="outline-success"
                  class="btn-icon mr-50"
                  @click="openSlip(data.item)"
                >
                  <feather-icon icon="FileTextIcon" />
                </b-button>
                <!-- <b-button
                  variant="outline-primary"
                  class="btn-icon"
                  @click="openCashlog(data.item.campusID)"
                >
                  <feather-icon icon="PrinterIcon" />
                </b-button> -->
                <b-dropdown
                  toggle-class="btn-icon"
                  variant="outline-primary"
                  right
                  no-caret
                >
                  <template #button-content>
                    <feather-icon icon="PrinterIcon" size="16" />
                  </template>
                  <b-dropdown-item
                    @click="openCashlog(data.item.campusID, 'Cashlog')"
                  >
                    Cashlog
                  </b-dropdown-item>
                  <b-dropdown-item
                    @click="
                      openCashlog(data.item.campusID, 'Cashlog-Campus-Summary')
                    "
                  >
                    Summary
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>
        </b-card>

        <!-- Attendance -->
        <b-card no-body v-if="sectionVisible('attendance')" class="dash-panel ho-att-panel">
          <b-card-header class="dash-panel__header ho-att-panel__header border-0">
            <div class="ho-att-panel__top">
              <div class="dash-panel__heading">
                <span class="dash-panel__icon ho-att-panel__icon">
                  <feather-icon icon="UserCheckIcon" size="18" />
                </span>
                <div>
                  <b-card-title class="dash-panel__title mb-0">{{
                    $t("Attendance")
                  }}</b-card-title>
                  <small class="dash-panel__subtitle">{{
                    $t("Campus-wise summary")
                  }}</small>
                </div>
              </div>

              <div class="ho-att-panel__actions">
                <label class="ho-att-date">
                  <feather-icon icon="CalendarIcon" size="15" />
                  <flat-pickr
                    v-model="attDate"
                    :config="config2"
                    class="form-control flat-picker ho-att-date__input"
                    :placeholder="$t('Select Date')"
                    @on-change="LoadAttendance()"
                  />
                </label>
                <div class="ho-att-campuses">
                  <v-select
                    multiple
                    :closeOnSelect="false"
                    v-model="attCampIDs"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="[{ id: 0, branch: 'All Campuses' }, ...campuses]"
                    :clearable="false"
                    :reduce="(val) => val.id"
                    label="branch"
                    :placeholder="$t('Select campus')"
                    @input="setAttCamp()"
                  />
                </div>
                <b-button
                  variant="primary"
                  class="ho-att-show"
                  :disabled="attLoading"
                  @click="LoadAttendance()"
                >
                  <b-spinner v-if="attLoading" small class="mr-50" />
                  <feather-icon v-else icon="RefreshCwIcon" size="15" class="mr-50" />
                  {{ $t("Show") }}
                </b-button>
              </div>
            </div>
          </b-card-header>

          <!-- Overall completion -->
          <div class="ho-att-progress-wrap">
            <div class="ho-att-progress__head">
              <span class="ho-att-progress__title">{{ $t("Campuses marked") }}</span>
              <span class="ho-att-progress__value">
                <strong>{{ attSummary.markedCampus || 0 }}</strong>
                / {{ attSummary.totalCampus || 0 }}
              </span>
            </div>
            <div
              class="ho-att-progress"
              :title="`${campusMarkedPercent}% ${$t('marked')}`"
            >
              <div
                class="ho-att-progress__fill"
                :style="{ width: `${campusMarkedPercent}%` }"
              />
            </div>
          </div>

          <!-- Student & staff summary -->
          <div class="ho-att-summaries">
            <div class="ho-att-summary">
              <div class="ho-att-summary__head">
                <span class="ho-att-summary__badge ho-att-summary__badge--std">
                  <feather-icon icon="UsersIcon" size="13" />
                </span>
                <span class="ho-att-summary__title">{{ $t("Students") }}</span>
                <span class="ho-att-summary__total">{{
                  attSummary.stdTotal || 0
                }}</span>
              </div>
              <div class="ho-att-summary__metrics">
                <span class="ho-att-metric ho-att-metric--ok"
                  ><i>P</i>{{ attSummary.stdPTotal || 0 }}</span
                >
                <span class="ho-att-metric ho-att-metric--bad"
                  ><i>A</i>{{ attSummary.stdATotal || 0 }}</span
                >
                <span class="ho-att-metric ho-att-metric--info"
                  ><i>L</i>{{ attSummary.stdLvTotal || 0 }}</span
                >
                <span class="ho-att-metric ho-att-metric--warn"
                  ><i>Late</i>{{ attSummary.stdLtTotal || 0 }}</span
                >
                <span class="ho-att-metric ho-att-metric--muted"
                  ><i>—</i>{{ attSummary.stdUnmarkedTotal || 0 }}</span
                >
              </div>
            </div>

            <div class="ho-att-summary">
              <div class="ho-att-summary__head">
                <span class="ho-att-summary__badge ho-att-summary__badge--staff">
                  <feather-icon icon="BriefcaseIcon" size="13" />
                </span>
                <span class="ho-att-summary__title">{{ $t("Staff") }}</span>
                <span class="ho-att-summary__total">{{
                  attSummary.stfTotal || 0
                }}</span>
              </div>
              <div class="ho-att-summary__metrics">
                <span class="ho-att-metric ho-att-metric--ok"
                  ><i>P</i>{{ attSummary.stfPTotal || 0 }}</span
                >
                <span class="ho-att-metric ho-att-metric--bad"
                  ><i>A</i>{{ attSummary.stfATotal || 0 }}</span
                >
                <span class="ho-att-metric ho-att-metric--info"
                  ><i>L</i>{{ attSummary.stfLvTotal || 0 }}</span
                >
                <span class="ho-att-metric ho-att-metric--warn"
                  ><i>Late</i>{{ attSummary.stfLtTotal || 0 }}</span
                >
              </div>
            </div>
          </div>

          <div class="ho-att-table-wrap">
            <b-table
              class="ho-att-table mb-0"
              sticky-header="400px"
              show-empty
              :items="attItems"
              :fields="attFields"
              hover
              :busy="attLoading"
              thead-class="ho-att-table__head"
            >
              <template #table-busy>
                <div class="ho-att-skeleton">
                  <div
                    v-for="n in 6"
                    :key="`ho-att-sk-${n}`"
                    class="ho-att-skeleton__row"
                  >
                    <span class="ho-att-skeleton__campus" />
                    <span
                      v-for="c in 4"
                      :key="`ho-att-sk-${n}-${c}`"
                      class="ho-att-skeleton__num"
                    />
                  </div>
                </div>
              </template>

              <template #head(details)>
                <div class="ho-att-th-group">
                  <span>{{ $t("Students") }}</span>
                  <small>P · A · L · Late · —</small>
                </div>
              </template>
              <template #head(st_details)>
                <div class="ho-att-th-group">
                  <span>{{ $t("Staff detail") }}</span>
                  <small>P · A · L · Late</small>
                </div>
              </template>

              <template #cell(campus)="data">
                <div class="ho-att-campus">
                  <span class="ho-att-campus__dot" aria-hidden="true" />
                  <span class="ho-att-campus__name">{{
                    data.item.campusName
                  }}</span>
                </div>
              </template>
              <template #cell(totalStudents)="data">
                <span class="ho-att-pill ho-att-pill--neutral">{{
                  data.item.studentCount
                }}</span>
              </template>
              <template #cell(details)="data">
                <div class="ho-att-metrics">
                  <span class="ho-att-pill ho-att-pill--ok">{{
                    data.item.stdP
                  }}</span>
                  <span class="ho-att-pill ho-att-pill--bad">{{
                    data.item.stdAb
                  }}</span>
                  <span class="ho-att-pill ho-att-pill--info">{{
                    data.item.stdLv
                  }}</span>
                  <span class="ho-att-pill ho-att-pill--warn">{{
                    data.item.stdLt
                  }}</span>
                  <span class="ho-att-pill ho-att-pill--muted">{{
                    data.item.stdUnmarked
                  }}</span>
                </div>
              </template>
              <template #cell(st_total)="data">
                <span class="ho-att-pill ho-att-pill--neutral">{{
                  data.item.staffCount
                }}</span>
              </template>
              <template #cell(st_details)="data">
                <div class="ho-att-metrics">
                  <span class="ho-att-pill ho-att-pill--ok">{{
                    data.item.staffP
                  }}</span>
                  <span class="ho-att-pill ho-att-pill--bad">{{
                    data.item.staffAb
                  }}</span>
                  <span class="ho-att-pill ho-att-pill--info">{{
                    data.item.staffLv
                  }}</span>
                  <span class="ho-att-pill ho-att-pill--warn">{{
                    data.item.staffLt
                  }}</span>
                </div>
              </template>
              <template #cell(actions)="data">
                <button
                  type="button"
                  class="ho-att-eye"
                  :title="$t('View campus')"
                  @click="openAttendance(data.item)"
                >
                  <feather-icon icon="EyeIcon" size="15" />
                </button>
              </template>

              <template #empty>
                <div class="ho-att-empty">
                  <feather-icon icon="InboxIcon" size="30" class="mb-50" />
                  <div>{{ $t("No records found") }}</div>
                </div>
              </template>
            </b-table>
          </div>
        </b-card>
      </template>

      <template v-else>
        <AttendanceCard :campusID="selectedCampusId" :initial-date="attDate" />
      </template>

      <!-- Homework -->
      <template v-if="sectionVisible('homework')">
        <b-card no-body v-if="isHeadOffice" class="dash-panel ho-hw-panel mt-2">
          <b-card-header class="dash-panel__header ho-hw-panel__header border-0">
            <div class="ho-hw-panel__top">
              <div class="dash-panel__heading">
                <span class="dash-panel__icon ho-hw-panel__icon">
                  <feather-icon icon="BookOpenIcon" size="18" />
                </span>
                <div>
                  <b-card-title class="dash-panel__title mb-0">{{
                    $t("Homework")
                  }}</b-card-title>
                  <small class="dash-panel__subtitle">{{
                    $t("Campus-wise assignment progress")
                  }}</small>
                </div>
              </div>

              <div class="ho-hw-panel__actions">
                <label class="ho-hw-date">
                  <feather-icon icon="CalendarIcon" size="15" />
                  <flat-pickr
                    v-model="hwDate"
                    :config="config2"
                    class="form-control flat-picker ho-hw-date__input"
                    :placeholder="$t('Select Date')"
                    @on-change="LoadHomework()"
                  />
                </label>
                <b-button
                  variant="primary"
                  class="ho-hw-show"
                  :disabled="hwLoading"
                  @click="LoadHomework()"
                >
                  <b-spinner v-if="hwLoading" small class="mr-50" />
                  <feather-icon v-else icon="RefreshCwIcon" size="15" class="mr-50" />
                  {{ $t("Show") }}
                </b-button>
              </div>
            </div>
          </b-card-header>

          <!-- Campus completion -->
          <div class="ho-hw-progress-wrap">
            <div class="ho-hw-progress__head">
              <span class="ho-hw-progress__title">{{
                $t("Campuses assigned homework")
              }}</span>
              <span class="ho-hw-progress__value">
                <strong>{{ hwSummary.markedCampuses || 0 }}</strong>
                / {{ hwSummary.totalCampuses || 0 }}
              </span>
            </div>
            <div
              class="ho-hw-progress"
              :title="`${hwCampusMarkedPercent}% ${$t('assigned')}`"
            >
              <div
                class="ho-hw-progress__fill"
                :style="{ width: `${hwCampusMarkedPercent}%` }"
              />
            </div>
          </div>

          <!-- Stat tiles -->
          <div class="ho-hw-stats">
            <div class="ho-hw-stat ho-hw-stat--total">
              <div class="ho-hw-stat__top">
                <span class="ho-hw-stat__icon">
                  <feather-icon icon="LayersIcon" size="13" />
                </span>
                <span class="ho-hw-stat__label">{{ $t("Total") }}</span>
              </div>
              <span class="ho-hw-stat__value">{{
                (hwSummary.totalHomework || 0).toLocaleString()
              }}</span>
              <div class="ho-hw-stat__foot">
                <span class="ho-hw-stat__pct">100%</span>
                <span class="ho-hw-stat__hint">{{ $t("all subjects") }}</span>
              </div>
            </div>
            <div class="ho-hw-stat ho-hw-stat--ok">
              <div class="ho-hw-stat__top">
                <span class="ho-hw-stat__icon">
                  <feather-icon icon="CheckCircleIcon" size="13" />
                </span>
                <span class="ho-hw-stat__label">{{ $t("Assigned") }}</span>
              </div>
              <span class="ho-hw-stat__value">{{
                (hwSummary.totalAssigned || 0).toLocaleString()
              }}</span>
              <div class="ho-hw-stat__foot">
                <span class="ho-hw-stat__meter">
                  <span
                    class="ho-hw-stat__meter-fill ho-hw-stat__meter-fill--ok"
                    :style="{ width: hwStatShare(hwSummary.totalAssigned) + '%' }"
                  />
                </span>
                <span class="ho-hw-stat__pct"
                  >{{ hwStatShare(hwSummary.totalAssigned) }}%</span
                >
              </div>
            </div>
            <div class="ho-hw-stat ho-hw-stat--published">
              <div class="ho-hw-stat__top">
                <span class="ho-hw-stat__icon">
                  <feather-icon icon="SendIcon" size="13" />
                </span>
                <span class="ho-hw-stat__label">{{ $t("Published") }}</span>
              </div>
              <span class="ho-hw-stat__value">{{
                (hwSummary.totalPublished || 0).toLocaleString()
              }}</span>
              <div class="ho-hw-stat__foot">
                <span class="ho-hw-stat__meter">
                  <span
                    class="ho-hw-stat__meter-fill ho-hw-stat__meter-fill--pub"
                    :style="{ width: hwStatShare(hwSummary.totalPublished) + '%' }"
                  />
                </span>
                <span class="ho-hw-stat__pct"
                  >{{ hwStatShare(hwSummary.totalPublished) }}%</span
                >
              </div>
            </div>
            <div class="ho-hw-stat ho-hw-stat--pending">
              <div class="ho-hw-stat__top">
                <span class="ho-hw-stat__icon">
                  <feather-icon icon="AlertCircleIcon" size="13" />
                </span>
                <span class="ho-hw-stat__label">{{ $t("Unassigned") }}</span>
              </div>
              <span class="ho-hw-stat__value">{{
                (hwSummary.totalUnAssigned || 0).toLocaleString()
              }}</span>
              <div class="ho-hw-stat__foot">
                <span class="ho-hw-stat__meter">
                  <span
                    class="ho-hw-stat__meter-fill ho-hw-stat__meter-fill--pending"
                    :style="{ width: hwStatShare(hwSummary.totalUnAssigned) + '%' }"
                  />
                </span>
                <span class="ho-hw-stat__pct"
                  >{{ hwStatShare(hwSummary.totalUnAssigned) }}%</span
                >
              </div>
            </div>
            <div class="ho-hw-stat ho-hw-stat--muted">
              <div class="ho-hw-stat__top">
                <span class="ho-hw-stat__icon">
                  <feather-icon icon="ClockIcon" size="13" />
                </span>
                <span class="ho-hw-stat__label">{{ $t("Unpublished") }}</span>
              </div>
              <span class="ho-hw-stat__value">{{
                (hwSummary.totalUnPublished || 0).toLocaleString()
              }}</span>
              <div class="ho-hw-stat__foot">
                <span class="ho-hw-stat__meter">
                  <span
                    class="ho-hw-stat__meter-fill ho-hw-stat__meter-fill--muted"
                    :style="{ width: hwStatShare(hwSummary.totalUnPublished) + '%' }"
                  />
                </span>
                <span class="ho-hw-stat__pct"
                  >{{ hwStatShare(hwSummary.totalUnPublished) }}%</span
                >
              </div>
            </div>
          </div>

          <!-- Search -->
          <div class="ho-hw-search">
            <span class="ho-hw-search__icon">
              <feather-icon icon="SearchIcon" size="15" />
            </span>
            <input
              v-model.trim="hwSearchQuery"
              type="text"
              class="ho-hw-search__input"
              :placeholder="$t('Search by campus')"
            />
            <span v-if="hwSearchQuery" class="ho-hw-search__count">
              {{ filteredHwItems.length }} / {{ hwItems.length }}
            </span>
          </div>

          <div class="ho-hw-table-wrap">
            <b-table
              class="ho-hw-table mb-0"
              sticky-header="400px"
              show-empty
              :items="filteredHwItems"
              :fields="hwFields"
              hover
              :busy="hwLoading"
            >
              <template #table-busy>
                <div class="ho-hw-skeleton">
                  <div
                    v-for="n in 6"
                    :key="`ho-hw-sk-${n}`"
                    class="ho-hw-skeleton__row"
                  >
                    <span class="ho-hw-skeleton__campus" />
                    <span class="ho-hw-skeleton__bar" />
                    <span class="ho-hw-skeleton__bar" />
                  </div>
                </div>
              </template>

              <template #cell(campus)="data">
                <div class="ho-hw-campus">
                  <span class="ho-hw-campus__dot" aria-hidden="true" />
                  <span class="ho-hw-campus__name">{{ data.item.campusName }}</span>
                </div>
              </template>

              <template #cell(assignedSummary)="data">
                <div class="ho-hw-metric">
                  <div class="ho-hw-metric__head">
                    <span class="ho-hw-metric__value">{{
                      parseHwRatio(data.item.assignedSummary).done
                    }}</span>
                    <span class="ho-hw-metric__of"
                      >/ {{ parseHwRatio(data.item.assignedSummary).total }}</span
                    >
                    <span class="ho-hw-metric__pct"
                      >{{ parseHwRatio(data.item.assignedSummary).pct }}%</span
                    >
                  </div>
                  <span class="ho-hw-meter">
                    <span
                      class="ho-hw-meter__fill ho-hw-meter__fill--assign"
                      :style="{
                        width: parseHwRatio(data.item.assignedSummary).pct + '%',
                      }"
                    />
                  </span>
                </div>
              </template>

              <template #cell(publishedSummary)="data">
                <div class="ho-hw-metric">
                  <div class="ho-hw-metric__head">
                    <span class="ho-hw-metric__value">{{
                      parseHwRatio(data.item.publishedSummary).done
                    }}</span>
                    <span class="ho-hw-metric__of"
                      >/ {{ parseHwRatio(data.item.publishedSummary).total }}</span
                    >
                    <span class="ho-hw-metric__pct ho-hw-metric__pct--pub"
                      >{{ parseHwRatio(data.item.publishedSummary).pct }}%</span
                    >
                  </div>
                  <span class="ho-hw-meter">
                    <span
                      class="ho-hw-meter__fill ho-hw-meter__fill--publish"
                      :style="{
                        width: parseHwRatio(data.item.publishedSummary).pct + '%',
                      }"
                    />
                  </span>
                </div>
              </template>

              <template #cell(actions)="data">
                <button
                  type="button"
                  class="ho-hw-eye"
                  :title="$t('View campus')"
                  @click="openHomework(data.item)"
                >
                  <feather-icon icon="EyeIcon" size="15" />
                </button>
              </template>

              <template #empty>
                <div class="ho-hw-empty">
                  <feather-icon icon="InboxIcon" size="30" class="mb-50" />
                  <div>{{ $t("No records found") }}</div>
                </div>
              </template>
            </b-table>
          </div>
        </b-card>
        <div v-else class="mt-2">
          <HomeworkCard :campusID="selectedCampusId" :initial-date="hwDate" />
        </div>
      </template>

      <b-row
        class="dash-charts-row dash-charts-row--yearly"
        v-if="showYearlyCollectionChart"
      >
        <b-col lg="12" md="12" cols="12">
          <chartjs-line-area-chart :yearlyData="yearlyData" />
        </b-col>
      </b-row>

      <!-- Scholarship Modal -->
      <!-- <b-modal
        id="modal-scholarship"
        :title="modalTitle"
        size="lg"
        centered
        hide-footer
        body-class="p-0"
        no-close-on-backdrop
      >
        <ScholarshipCard
          :campusID="selectedCampusId"
          :isHeadOffice="isHeadOffice"
          :region="selectedRegion"
          :category="selectedCategory"
        />
      </b-modal> -->

      <!-- Attendance Modal -->
      <b-modal
        id="modal-attendance"
        :title="modalTitle"
        size="lg"
        centered
        hide-footer
        body-class="p-0"
      >
        <AttendanceCard :campusID="selectedCampusId" :initial-date="attDate" />
      </b-modal>

      <!-- Homework Modal -->
      <b-modal
        id="modal-homework"
        :title="modalTitle"
        size="xl"
        centered
        hide-footer
        body-class="p-0"
      >
        <HomeworkCard :campusID="selectedCampusId" :initial-date="hwDate" />
      </b-modal>
    </div>

    <!-- User List Modal -->
    <b-modal
      id="modal-user-list"
      hide-header
      size="xl"
      hide-footer
      centered
      scrollable
      modal-class="professional-insights-modal app-download-insights-modal"
      content-class="app-download-insights-content"
      body-class="app-download-insights-body"
    >
      <!-- Gradient Line at Top -->
      <div class="gradient-line-wrapper"></div>

      <div class="app-insights-body px-2 pt-1 pb-1">
        <!-- Ultra-Clean Header -->
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div>
            <h5 class="mb-0 font-weight-bolder app-insights-title">
              App Download Insights
            </h5>
            <small class="app-insights-subtitle font-weight-bold"
              >Summary Status Statistics</small
            >
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            @click="$bvModal.hide('modal-user-list')"
            class="btn-icon rounded-circle shadow-none app-insights-close"
          >
            <feather-icon icon="XIcon" size="18" />
          </b-button>
        </div>

        <!-- Minimalist Action Hub -->
        <div
          class="d-flex align-items-center justify-content-between mb-1 p-50 border rounded app-insights-toolbar flex-wrap"
          style="gap: 12px"
        >
          <div class="flex-grow-1" style="min-width: 180px; max-width: 260px">
            <b-form-input
              v-model="userListSearch"
              placeholder="Quickly find records..."
              class="border-0 shadow-none font-small-3 px-1 h-auto py-50 app-insights-search"
              @input="userListCurrentPage = 1"
            />
          </div>

          <div v-if="userListModal.activeTab < 2" class="app-insights-filter">
            <small class="app-insights-filter-label d-block mb-25">Class</small>
            <v-select
              v-model="appStatsClassIDs"
              :options="appStatsClassOptions"
              :reduce="(opt) => opt.id"
              label="name"
              multiple
              :close-on-select="false"
              :clearable="false"
              :searchable="true"
              placeholder="All Classes"
              class="app-insights-filter-select"
              @input="onAppStatsClassFilterChange"
            />
          </div>

          <div v-else class="app-insights-filter">
            <small class="app-insights-filter-label d-block mb-25"
              >Department</small
            >
            <v-select
              v-model="appStatsDepartmentIDs"
              :options="appStatsDepartmentOptions"
              :reduce="(opt) => opt.id"
              label="name"
              multiple
              :close-on-select="false"
              :clearable="false"
              :searchable="true"
              placeholder="All Departments"
              class="app-insights-filter-select"
              @input="onAppStatsDepartmentFilterChange"
            />
          </div>

          <div class="d-flex align-items-center flex-wrap" style="gap: 6px">
            <b-button
              v-for="(label, index) in [
                'Active Stud.',
                'Inact. Stud.',
                'Active Staff',
                'Inact. Staff',
              ]"
              :key="index"
              @click="switchCategory(index)"
              :variant="
                userListModal.activeTab === index
                  ? 'primary'
                  : 'outline-primary'
              "
              class="font-small-1 font-weight-bolder px-75 py-50 border-radius-8 app-insights-tab"
            >
              {{ label }}
            </b-button>
          </div>
        </div>

        <!-- Precise Status Line -->
        <div
          class="d-flex align-items-center justify-content-between px-50 mb-75"
        >
          <span class="app-insights-meta font-small-2">
            Viewing
            <strong class="text-primary">{{ activeListLength }}</strong> records
            <span
              v-if="isPendingUserListTab && appPendingSelectedCount > 0"
              class="ml-50"
            >
              ·
              <strong class="text-primary">{{
                appPendingSelectedCount
              }}</strong>
              {{ $t("selected") }}
            </span>
          </span>
          <div class="d-flex align-items-center" style="gap: 8px">
            <b-button
              v-if="isPendingUserListTab && appPendingSelectedCount > 0"
              variant="primary"
              size="sm"
              class="font-small-1 font-weight-bolder px-75 py-50"
              :disabled="userListModal.loading || appPendingMsg.sending"
              @click="openAppPendingMsgForSelected"
            >
              <feather-icon icon="MailIcon" size="14" class="mr-50" />
              {{ $t("Send selected") }} ({{ appPendingSelectedCount }})
            </b-button>
            <b-button
              v-if="isPendingUserListTab && appPendingSelectedCount > 0"
              variant="outline-secondary"
              size="sm"
              class="btn-icon font-small-1 px-50 py-50"
              :title="$t('Clear selection')"
              @click="clearAppPendingSelection"
            >
              <feather-icon icon="XIcon" size="14" />
            </b-button>
            <b-button
              variant="outline-success"
              size="sm"
              class="font-small-1 font-weight-bolder px-75 py-50"
              :disabled="
                userListModal.loading ||
                userListExporting ||
                filteredUserListItems.length === 0
              "
              @click="exportAppDownloadUserList()"
            >
              <b-spinner v-if="userListExporting" small class="mr-50" />
              <feather-icon
                v-else
                icon="DownloadIcon"
                size="14"
                class="mr-50"
              />
              Download
            </b-button>
            <div
              class="status-dot-sm"
              :class="
                userListModal.activeTab % 2 === 0 ? 'bg-success' : 'bg-danger'
              "
            ></div>
            <small
              class="font-weight-bolder text-uppercase font-small-1"
              :class="
                userListModal.activeTab % 2 === 0
                  ? 'text-success'
                  : 'text-danger'
              "
            >
              {{ userListModal.activeTab % 2 === 0 ? "INSTALLED" : "PENDING" }}
            </small>
          </div>
        </div>

        <!-- Compact Scrollable Table -->
        <div class="user-list-scroll-view position-relative">
          <b-table
            :items="pagedUserListItems"
            :fields="currentTableFields"
            responsive
            hover
            show-empty
            class="professional-table-compact mb-0"
            :class="{ 'opacity-50': userListModal.loading }"
          >
            <template #head()="data">
              <span
                class="text-uppercase font-small-1 text-muted font-weight-bolder letter-spacing-1"
                >{{ data.label }}</span
              >
            </template>

            <template #head(selected)>
              <b-form-checkbox
                v-if="isPendingUserListTab"
                class="mb-0 app-pending-row-checkbox"
                :checked="appPendingAllFilteredSelected"
                :indeterminate="appPendingSomeFilteredSelected"
                :disabled="filteredUserListItems.length === 0"
                @change="toggleSelectAllAppPending"
              />
            </template>

            <template #cell(selected)="data">
              <b-form-checkbox
                v-if="isPendingUserListTab"
                class="mb-0 app-pending-row-checkbox"
                :checked="isAppPendingSelected(data.item, data.index)"
                @change="setAppPendingSelected(data.item, data.index, $event)"
              />
            </template>

            <template #cell(sno)="data">
              <span class="text-muted font-small-1 font-weight-bolder">{{
                data.index + 1
              }}</span>
            </template>

            <template #cell(grNo)="data">
              <span class="font-weight-bolder text-primary font-small-2">{{
                data.value
              }}</span>
            </template>

            <template #cell(name)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  size="24"
                  :variant="
                    userListModal.activeTab % 2 === 0 ? 'primary' : 'secondary'
                  "
                  :text="(data.value || '?').charAt(0)"
                  class="mr-75 font-weight-bolder font-small-1 app-insights-avatar"
                />
                <span class="font-weight-bold app-insights-name font-small-2">{{
                  data.value
                }}</span>
              </div>
            </template>

            <template #cell(className)="data">
              <span class="text-muted font-small-1">{{ data.value }}</span>
            </template>

            <template #cell(designation)="data">
              <b-badge
                variant="light-warning"
                class="font-small-1 border-0"
                style="font-size: 10px !important"
                >{{ data.value }}</b-badge
              >
            </template>

            <template #cell(contacts)="data">
              <div class="app-contact-grid">
                <div
                  v-for="contact in getStudentContacts(data.item)"
                  :key="contact.key"
                  class="app-contact-card"
                  :class="{
                    'app-contact-card--installed': contact.installed,
                    'app-contact-card--missing': !contact.installed,
                  }"
                >
                  <div class="app-contact-card__top">
                    <span
                      class="app-contact-card__dot"
                      :class="
                        contact.installed
                          ? 'app-contact-card__dot--installed'
                          : 'app-contact-card__dot--missing'
                      "
                    ></span>
                    <span class="app-contact-card__role">{{
                      contact.label
                    }}</span>
                    <b-button
                      v-if="contact.phone"
                      variant="flat-primary"
                      size="sm"
                      class="btn-icon app-contact-card__send"
                      v-b-tooltip.hover.top
                      :title="$t('Send message')"
                      @click.stop="
                        openAppPendingMsgForContact(data.item, contact.key)
                      "
                    >
                      <feather-icon icon="MailIcon" size="12" />
                    </b-button>
                  </div>
                  <div
                    class="app-contact-card__phone"
                    :class="
                      contact.installed
                        ? 'app-contact-card__phone--installed'
                        : 'app-contact-card__phone--missing'
                    "
                  >
                    {{ contact.phone || "-" }}
                  </div>
                </div>
              </div>
            </template>

            <template #cell(phoneNumber)="data">
              <span
                v-if="
                  getAppDownloadRecipientPhone(
                    data.item,
                    userListModal.activeTab >= 2
                  )
                "
                class="font-small-2 font-weight-bold text-dark"
                style="letter-spacing: 0.3px; white-space: nowrap"
              >
                {{
                  getAppDownloadRecipientPhone(
                    data.item,
                    userListModal.activeTab >= 2
                  )
                }}
              </span>
              <span v-else class="text-muted font-small-1">—</span>
            </template>

            <template #cell(actions)="data">
              <b-button
                v-if="isPendingUserListTab"
                variant="outline-primary"
                size="sm"
                class="btn-icon rounded-circle app-pending-mail-btn"
                :disabled="!canMessageAppPendingItem(data.item)"
                v-b-tooltip.hover.top
                :title="
                  canMessageAppPendingItem(data.item)
                    ? $t('Send message')
                    : $t('No phone or linked ID for messaging')
                "
                @click="openAppPendingMsgForItem(data.item)"
              >
                <feather-icon icon="MailIcon" size="14" />
              </b-button>
            </template>

            <template #empty>
              <div class="text-center py-4">
                <feather-icon
                  icon="InboxIcon"
                  size="30"
                  class="text-light mb-1"
                />
                <h6 class="text-muted font-small-3">
                  No matching records found
                </h6>
              </div>
            </template>
          </b-table>
        </div>

        <div
          v-if="filteredUserListItems.length > userListPerPage"
          class="d-flex align-items-center justify-content-between mt-1 px-50 app-insights-pagination"
        >
          <small class="text-muted font-small-2">
            {{ (userListCurrentPage - 1) * userListPerPage + 1 }} -
            {{
              Math.min(
                userListCurrentPage * userListPerPage,
                filteredUserListItems.length
              )
            }}
            of {{ filteredUserListItems.length }}
          </small>
          <b-pagination
            v-model="userListCurrentPage"
            :total-rows="filteredUserListItems.length"
            :per-page="userListPerPage"
            pills
            size="sm"
            class="mb-0"
          />
        </div>

        <div
          v-if="userListModal.loading"
          class="app-insights-loading-layer"
        >
          <div class="app-insights-loading-card">
            <b-spinner variant="primary" />
            <span>{{ $t("Loading records...") }}</span>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Pending app-download message composer (individual / multi-select) -->
    <b-sidebar
      id="app-pending-msg-sidebar"
      v-model="appPendingMsg.visible"
      right
      shadow
      backdrop
      width="420px"
      bg-variant="white"
      sidebar-class="app-pending-msg-sidebar"
      no-header
      @hidden="onAppPendingMsgSidebarHidden"
    >
      <div
        class="d-flex align-items-start justify-content-between content-sidebar-header px-2 py-1"
      >
        <div class="min-width-0 flex-grow-1 pr-1">
          <h5 class="mb-50 font-weight-bolder">{{ $t("Send Message") }}</h5>
          <div
            v-if="appPendingMsg.bulk && appPendingMsg.targets.length"
            class="app-pending-recipient-card app-pending-recipient-card--bulk"
          >
            <div class="app-pending-recipient-avatar">
              <feather-icon icon="UsersIcon" size="18" class="text-white" />
            </div>
            <div class="min-width-0">
              <div class="font-weight-bolder font-small-2">
                {{ appPendingMsg.targets.length }} {{ $t("selected") }}
              </div>
              <div class="app-pending-recipient-chips">
                <span
                  v-for="(rec, ri) in appPendingMsg.targets"
                  :key="'apr-' + ri"
                  class="app-pending-recipient-chip text-truncate"
                  :title="appPendingRecipientLabel(rec)"
                >
                  {{ rec.name || "—" }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-else-if="appPendingMsg.targets[0]"
            class="app-pending-recipient-card"
          >
            <div class="app-pending-recipient-avatar">
              <feather-icon icon="UserIcon" size="18" class="text-white" />
            </div>
            <div class="min-width-0">
              <div class="font-weight-bolder font-small-2 text-truncate">
                {{ appPendingMsg.targets[0].name || "—" }}
              </div>
              <small class="text-muted text-truncate d-block">
                {{ appPendingRecipientLabel(appPendingMsg.targets[0]) }}
              </small>
            </div>
          </div>
        </div>
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon rounded-circle flex-shrink-0"
          :disabled="appPendingMsg.sending"
          @click="appPendingMsg.visible = false"
        >
          <feather-icon icon="XIcon" size="18" />
        </b-button>
      </div>

      <div class="px-2 pb-2">
        <b-form-group
          :label="$t('Select template')"
          label-class="font-weight-bolder font-small-2"
          class="mb-2"
        >
          <div v-if="appPendingMsg.templatesLoading" class="text-center py-2">
            <b-spinner small type="grow" variant="primary" />
          </div>
          <v-select
            v-else
            v-model="appPendingMsg.selectedTemplateId"
            :options="appPendingMsg.templates"
            label="title"
            :reduce="(t) => t.id"
            :clearable="true"
            :searchable="true"
            append-to-body
            :calculate-position="appPendingTemplateDropdownPosition"
            :placeholder="$t('Select template')"
            @input="onAppPendingTemplateSelected"
          />
        </b-form-group>

        <b-form-group
          :label="$t('Message')"
          label-class="font-weight-bolder font-small-2"
          class="mb-2"
        >
          <b-form-textarea
            v-model.trim="appPendingMsg.message"
            rows="8"
            :placeholder="$t('Enter your message')"
          />
        </b-form-group>

        <small class="d-block text-muted mb-2">
          {{ $t("Sending to") }}
          <strong>{{ appPendingRecipientMeta.ids.length }}</strong>
          {{
            appPendingRecipientMeta.isStaff
              ? $t("staff ID(s)")
              : $t("student ID(s)")
          }}
          <span v-if="appPendingRecipientMeta.phones.length">
            +
            <strong>{{ appPendingRecipientMeta.phones.length }}</strong>
            {{ $t("phone(s)") }}
          </span>
        </small>

        <b-button
          variant="primary"
          block
          class="mb-1"
          :disabled="appPendingMsg.sending || !canSendAppPendingMessage"
          @click="sendAppPendingMessages('message')"
        >
          <b-spinner v-if="appPendingMsg.sending" small class="mr-50" />
          <feather-icon v-else icon="SendIcon" size="16" class="mr-50" />
          {{ $t("Send message") }}
        </b-button>

        <b-button
          variant="outline-warning"
          block
          :disabled="appPendingMsg.sending || !canSendAppPendingNotification"
          :title="
            !canSendAppPendingNotification
              ? $t('Notifications require linked student/staff records.')
              : undefined
          "
          @click="sendAppPendingMessages('notification')"
        >
          <feather-icon icon="BellIcon" size="16" class="mr-50" />
          {{ $t("Send notification") }}
        </b-button>
      </div>
    </b-sidebar>

    <!-- Scholarship Filter Modal -->
    <!-- <b-modal
      id="modal-scholarship-filter"
      :title="$t('Scholarship Filters')"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <b-form-group :label="$t('Scholarship Category')">
        <v-select
          multiple
          :closeOnSelect="false"
          v-model="selectedCategory"
          :options="scholarshipCategoryOptions"
          :clearable="false"
          class="form-select-custom"
          :disabled="
            scholarshipLoading || scholarshipPrinting || scholarshipExporting
          "
          :placeholder="$t('Select scholarship category')"
        />
      </b-form-group>
      <b-form-group :label="$t('Region')">
        <v-select
          multiple
          :closeOnSelect="false"
          v-model="selectedRegion"
          :options="regionOptions"
          :clearable="false"
          class="form-select-custom"
          :disabled="
            scholarshipLoading || scholarshipPrinting || scholarshipExporting
          "
          :placeholder="$t('Select region')"
        />
      </b-form-group>
      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="$bvModal.hide('modal-scholarship-filter')"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="
            scholarshipLoading || scholarshipPrinting || scholarshipExporting
          "
          @click="
            LoadScholarshipStatistics();
            LoadOtherStats();
            $bvModal.hide('modal-scholarship-filter');
          "
        >
          {{ $t("Apply") }}
        </b-button>
      </div>
    </b-modal> -->
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
  BTable,
  BBadge,
  BAvatar,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BFormGroup,
  VBTooltip,
  BImg,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BDropdown,
  BDropdownItem,
  BProgress,
  BTabs,
  BTab,
  BSidebar,
  BPagination,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";

import { getUserData } from "@/auth/utils";
import EcommerceMedal from "./EcommerceMedal.vue";
import EcommerceStatistics from "./EcommerceStatistics.vue";
import EcommerceRevenueReport from "./EcommerceRevenueReport.vue";
import EcommerceOrderChart from "./EcommerceOrderChart.vue";
import EcommerceProfitChart from "./EcommerceProfitChart.vue";
import EcommerceEarningsChart from "./EcommerceEarningsChart.vue";
import EcommerceCompanyTable from "./EcommerceCompanyTable.vue";
import EcommerceMeetup from "./EcommerceMeetup.vue";
import EcommerceBrowserStates from "./EcommerceBrowserStates.vue";
import EcommerceGoalOverview from "./EcommerceGoalOverview.vue";
import EcommerceTransactions from "./EcommerceTransactions.vue";
import ChartjsLineChart from "@/views/charts-and-maps/charts/chartjs/ChartjsLineChart.vue";
import ChartjsLineAreaChart from "@/views/charts-and-maps/charts/chartjs/ChartjsLineAreaChart.vue";
import FeeBarChart from "./FeeBarChart.vue";
import AttendanceCard from "./AttendanceCard.vue";
import HomeworkCard from "./HomeworkCard.vue";
import ScholarshipCard from "./ScholarshipCard.vue";
import ScholarshipOverview from "./ScholarshipOverview.vue";
import Statistics from "./statistics.vue";
import DashboardWelcomeHero from "./DashboardWelcomeHero.vue";
import CollectionInsightsPanel from "./CollectionInsightsPanel.vue";
import DashboardAcademicsHub from "./DashboardAcademicsHub.vue";
import DashboardCommsTrio from "./DashboardCommsTrio.vue";
import DashboardEventsCalendar from "./DashboardEventsCalendar.vue";
import {
  loadClassStrengthSummary,
  loadPendingLeaveRequests,
  loadPendingStudentRequests,
} from "./utils/dashboardAcademics.js";
import Birthdays from "./Birthdays.vue";
import PipelineInsights from "./PipelineInsights.vue";
import HoldFeesStatsPanel from "./HoldFeesStatsPanel.vue";
import PosStats from "./PosStats.vue";
import StaffStatsCard from "./StaffStatsCard.vue";
import AccountBalanceCard from "./AccountBalanceCard.vue";
import ResultComparisonInsights from "./ResultComparisonInsights.vue";
import { canShowAccountBalanceDashboard } from "./accountBalanceDashboard.js";
// Calendar is heavy and below the first screen — load it as its own chunk.
const EventCalendar = () =>
  import(
    /* webpackChunkName: "dashboard-calendar" */ "@/views/apps/event-calendar/Calendar.vue"
  );
import VueApexCharts from "vue-apexcharts";

import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import {
  getUploadedPaths,
  uploadToMySkool,
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";
import {
  extractChatSendStats,
  formatChatBulkSendMessage,
  isChatSendOk,
  normalizeChatSendApiResponse,
} from "@/utils/chatBulkSend";

export default {
  components: {
    flatPickr,
    vSelect,
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BAvatar,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BFormGroup,
    BImg,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    BProgress,
    BTabs,
    BTab,
    BSidebar,
    BPagination,
    ChartjsLineChart,
    ChartjsLineAreaChart,
    EcommerceMedal,
    EcommerceStatistics,
    EcommerceRevenueReport,
    EcommerceOrderChart,
    EcommerceProfitChart,
    EcommerceEarningsChart,
    EcommerceCompanyTable,
    EcommerceMeetup,
    EcommerceBrowserStates,
    EcommerceGoalOverview,
    EcommerceTransactions,
    FeeBarChart,
    AttendanceCard,
    HomeworkCard,
    ScholarshipCard,
    ScholarshipOverview,
    Statistics,
    DashboardWelcomeHero,
    CollectionInsightsPanel,
    DashboardAcademicsHub,
    DashboardCommsTrio,
    DashboardEventsCalendar,
    Birthdays,
    PipelineInsights,
    HoldFeesStatsPanel,
    AccountBalanceCard,
    ResultComparisonInsights,
    PosStats,
    StaffStatsCard,
    EventCalendar,
    VueApexCharts,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      data: {},
      dash: {},
      otherData: {},
      monthlyData: {},
      monthlySeries: [],
      weeklyData: {},
      yearlyData: {},
      loading: false,
      statsLoading: true,
      showDues: false,
      showCollection: false,
      financeLiveLoading: false,
      academicsHubLoading: false,
      academicsData: {
        strengthRows: [],
        strengthTotals: { male: 0, female: 0, other: 0, total: 0 },
        leaveRows: [],
        studentReqRows: [],
      },
      currentYear: "",
      rights: {},
      stLoading: false,
      items: [],
      fields: [
        { label: "campus", key: "campus" },
        { label: "inquiries", key: "inquiries" },
        { label: "registrations", key: "registrations" },
        { label: "admissions", key: "admissions" },
        // { label: "amount", key: "amount" },
        // { label: "actions", key: "actions" },
      ],
      attLoading: false,
      attItems: [],
      attFields: [
        {
          label: "campus",
          key: "campus",
          thClass: "ho-att-th-campus",
          tdClass: "ho-att-td-campus align-middle",
        },
        {
          label: "students",
          key: "totalStudents",
          thClass: "text-center",
          tdClass: "text-center align-middle",
        },
        {
          label: "details",
          key: "details",
          thClass: "text-center",
          tdClass: "text-center align-middle",
        },
        {
          label: "staff",
          key: "st_total",
          thClass: "text-center",
          tdClass: "text-center align-middle",
        },
        {
          label: "st_details",
          key: "st_details",
          thClass: "text-center",
          tdClass: "text-center align-middle",
        },
        {
          label: "actions",
          key: "actions",
          thClass: "text-center",
          tdClass: "text-center align-middle",
        },
      ],
      feeLoading: false,
      feeItems: [],
      feeFields: [
        { label: "Feetype", key: "feeType" },
        { label: "Students", key: "students" },
        { label: "Amount", key: "amount" },
      ],
      hwLoading: false,
      hwItems: [],
      hwSummary: {
        totalHomework: 0,
        totalAssigned: 0,
        totalPublished: 0,
        totalUnAssigned: 0,
        totalUnPublished: 0,
        totalCampuses: 0,
        markedCampuses: 0,
      },
      hwFields: [
        {
          label: "campus",
          key: "campus",
          thClass: "ho-hw-th-campus",
          tdClass: "ho-hw-td-campus align-middle",
        },
        {
          label: "assigned",
          key: "assignedSummary",
          thClass: "text-center",
          tdClass: "align-middle",
        },
        {
          label: "published",
          key: "publishedSummary",
          thClass: "text-center",
          tdClass: "align-middle",
        },
        {
          label: "actions",
          key: "actions",
          thClass: "text-center ho-hw-th-actions",
          tdClass: "text-center align-middle ho-hw-td-actions",
        },
      ],
      config: {
        mode: "range",
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      config2: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      dateObj: {
        dtFrom: null,
        dtTo: null,
      },
      rangeDate: "",
      attDate: new Date().toJSON().split("T")[0],
      hwDate: new Date().toJSON().split("T")[0],
      hwSearchQuery: "",
      summaryObj: {
        inqTotal: 0,
        inqCompTotal: 0,
        amountTotal: 0,
        depositAmountTotal: 0,
        admPendingTotal: 0,
        admTotal: 0,
        regTotal: 0,
        regPendingTotal: 0,
      },
      lastLoad: {
        from: null,
        to: null,
      },
      slipData: [],
      currentSlip: null,
      slipDeleting: false,
      slipSaving: false,
      slipForm: false,
      slipObj: null,
      slipImages: [],
      docLoading: false,
      campuses: [],
      campIDs: [0],
      attCampIDs: [0],
      attSummary: {
        stdTotal: 0,
        stdPTotal: 0,
        stdATotal: 0,
        stdLvTotal: 0,
        stdLtTotal: 0,
        stfTotal: 0,
        stfPTotal: 0,
        stfATotal: 0,
        stfLvTotal: 0,
        stfLtTotal: 0,
        stdUnmarkedTotal: 0,
        totalCampus: 0,
        markedCampus: 0,
      },
      quickStats: {
        collection: 125000,
        receipts: 45,
        students: 2450,
        staff: 120,
        inquiries: 15,
        dues: 75000,
      },
      selectedCampusId: this.$store.state.userData.cId,
      modalTitle: "",
      attVisible: ["myskoolstjacob"],
      amountVisible: ["myskoolhpgs"],
      isHeadOffice: null,
      settingLoading: true,
      subVisible: ["myskoolskh"],
      subLoading: false,
      subData: [],
      subFields: [
        { label: "staff", key: "staff", tdClass: "px-1" },
        { label: "subject", key: "subject", tdClass: "px-1" },
        { label: "students", key: "totalStudents", tdClass: "px-1" },
      ],
      showVisitor: false,
      visitorObj: {},
      dashRights: [],
      downloadLoading: false,
      downloadStats: null,
      appStatsOpeningKey: "",
      colorsDemographicsLoading: false,
      colorsChartBooting: false,
      colorsChartEpoch: 0,
      colorsDemographics: null,
      colorsDemographicsObj: null,
      enrolmentOverviewSetting: null,
      selectedColorsHouse: null,
      colorsStudentFields: [
        {
          key: "className",
          label: "Class",
          thClass: "hcd-th",
          tdClass: "hcd-td",
        },
        { key: "name", label: "Student", thClass: "hcd-th", tdClass: "hcd-td" },
        { key: "grNo", label: "GR No", thClass: "hcd-th", tdClass: "hcd-td" },
        {
          key: "sectionName",
          label: "Section",
          thClass: "hcd-th",
          tdClass: "hcd-td",
        },
        {
          key: "gender",
          label: "Gender",
          thClass: "hcd-th text-center",
          tdClass: "hcd-td text-center",
        },
      ],
      downloadItems: [],
      campusSearchQuery: "",
      lastSortField: "",
      sortAscending: false,
      campusFields: [
        { label: "Campus", key: "branch", tdClass: "align-middle" },
        {
          label: "Total Downloads",
          key: "totalDownloads",
          tdClass: "align-middle",
        },
        {
          label: "Student Downloads",
          key: "studentDownloads",
          tdClass: "align-middle",
        },
        {
          label: "Staff Downloads",
          key: "staffDownloads",
          tdClass: "align-middle",
        },
        { label: "Active Users", key: "activeUsers", tdClass: "align-middle" },
        {
          label: "Total Strength",
          key: "totalStrength",
          tdClass: "align-middle",
        },
      ],
      userListModal: {
        show: false,
        title: "",
        items: [],
        fields: [],
        activeTab: 0,
        loading: false,
      },
      userListSearch: "",
      userListExporting: false,
      userListCurrentPage: 1,
      userListPerPage: 10,
      appPendingSelection: {},
      appPendingMsg: {
        visible: false,
        templates: [],
        templatesLoading: false,
        selectedTemplateId: null,
        media: "",
        message: "",
        sending: false,
        targets: [],
        isStaff: false,
        bulk: false,
      },
      scholarshipTotals: {
        assigned: 0,
        accepted: 0,
        acceptedAppLogin: 0,
        acceptedAdminPortal: 0,
        acceptedPerc: 0,
        notAccepted: 0,
        notAcceptedLeft: 0,
        active: 0,
        onProbation: 0,
        inactive: 0,
      },
      scholarshipItems: [],
      assignedStrength: [],
      acceptedStrength: [],
      notAcceptedStrength: [],
      scholarshipFields: [
        { label: "campus", key: "campus" },
        { label: "assigned", key: "assigned", tdClass: "text-center" },
        { label: "accepted", key: "accepted" },
        { label: "not accepted", key: "notAccepted", tdClass: "text-center" },
        { label: "status", key: "status" },
        { label: "actions", key: "actions" },
      ],
      scholarshipLoading: false,
      scholarshipStudents: [],
      scholarshipPrinting: false,
      scholarshipExporting: false,
      hiddenSections: [
        "dues",
        "collection",
        "app-downloads",
        "homework",
        "hold-fees-stats",
      ],
      sessionID: null,
      appStatsClassIDs: [0],
      appStatsDepartmentIDs: [0],
      appStatsClasses: [],
      appStatsDepartments: [],
      sessions: [],
      statsBundleSetting: null,
      selectedAppBundleID: null,
      otherLoading: false,
      classStats: [],
      genderStats: {
        assignedTotal: 0,
        assignedMale: 0,
        assignedFemale: 0,
        assignedMalePerc: 0,
        assignedFemalePerc: 0,
        acceptedTotal: 0,
        acceptedMale: 0,
        acceptedFemale: 0,
        acceptedMalePerc: 0,
        acceptedFemalePerc: 0,
        notAcceptedTotal: 0,
        notAcceptedMale: 0,
        notAcceptedFemale: 0,
        notAcceptedMalePerc: 0,
        notAcceptedFemalePerc: 0,
      },
      posVisible: ["myskoolstjacob"],
      selectedRegion: [],
      selectedCategory: [],
      regionOptions: [],
      scholarshipCategoryOptions: [
        "Offered Coaching",
        "Offered Pvt College",
        "Offered TCF College",
      ],
      isPcpLoaded: true,
      pcpOnly: false,
      isUtpLoaded: true,
      utpCampusLoaded: false,
      utpCampusIDs: [],
    };
  },
  created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          reportTypes: ch.reportTypes || [],
        };
      });
    });

    // console.log(right);
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      // console.log(this.$store.state.rights[this.$route.name]);
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];

      if (this.rights.reportTypes && this.rights.reportTypes.length > 0) {
        this.dashRights = this.rights.reportTypes;
      }
      // console.log(this.rights);
      this.currentYear = new Date().getFullYear();
      this.LoadData();
      this.checkProbationCompletion();

      if (this.subVisible.includes(this.reportDomain)) {
        this.LoadSubEnrolled();
      } else if (this.showAcademicsHub) {
        this.LoadSubEnrolled();
      }

      const dt = new Date().toJSON().split("T")[0];
      this.dateObj.dtFrom = dt;
      this.dateObj.dtTo = dt;
      this.lastLoad.from = dt;
      this.lastLoad.to = dt;
      this.rangeDate = `${this.dateObj.dtFrom} to ${this.dateObj.dtTo}`;

      this.LoadSettings();
      // if (["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain)) {
      //   this.LoadRegions();
      // }
      this.LoadCollection();
      this.LoadDues();
      this.loadAcademicsHubData();
      this.loadSessions();
      this.$http.get("/ecommerce/data").then((response) => {
        this.data = response.data;
        // console.log(this.data);

        // ? Your API will return name of logged in user or you might just directly get name of logged in user
        // ? This is just for demo purpose
        const userData = getUserData();
        this.data.congratulations.name =
          userData.fullName.split(" ")[0] || userData.username;
      });
    }
  },
  mounted() {
    // Capture-phase scroll on every scroll event (incl. inner scrollers) is
    // expensive. Only listen while the dropdown can be open, coalesce to one
    // work per frame, and keep the listener passive.
    this._appBundleScrollRaf = 0;
    this._closeAppBundleOnScroll = () => {
      if (!this.showAppBundleSelector) return;
      const select = this.$refs.appBundleSelect;
      if (!select || !select.open) return;
      if (this._appBundleScrollRaf) return;
      this._appBundleScrollRaf = requestAnimationFrame(() => {
        this._appBundleScrollRaf = 0;
        this.closeAppBundleDropdownOnScroll();
      });
    };
    window.addEventListener("scroll", this._closeAppBundleOnScroll, {
      capture: true,
      passive: true,
    });
    this.startFinanceLivePolling();
  },
  beforeDestroy() {
    if (this._closeAppBundleOnScroll) {
      window.removeEventListener("scroll", this._closeAppBundleOnScroll, true);
    }
    if (this._appBundleScrollRaf) {
      cancelAnimationFrame(this._appBundleScrollRaf);
      this._appBundleScrollRaf = 0;
    }
    if (this._hcdChartBootTimer) {
      clearTimeout(this._hcdChartBootTimer);
    }
    this.stopFinanceLivePolling();
  },
  computed: {
    dashboardUserName() {
      const logged = this.$store.state.loggedUser || {};
      const user = this.$store.state.userData || {};
      return (
        logged.fullName ||
        user.fullName ||
        user.username ||
        "User"
      );
    },
    dashboardCampusName() {
      const branch = this.$store.state.currentBranch;
      return (
        this.$store.state.branchName ||
        (branch && (branch.branch || branch.name)) ||
        "Campus"
      );
    },
    dashboardProfileImage() {
      const logged = this.$store.state.loggedUser || {};
      return logged.avatar || "";
    },
    /**
     * Client/report slug with underscores stripped, exactly as the rest of this
     * component has always used it. Derived, not frozen in `data()`: the navbar
     * commits the real client ID to the store after login (`setReport`), so a
     * snapshot taken in created() could stay "reports". That made the TCF
     * template gate and `isTcfDomain` disagree — the fee blocks hid themselves
     * while the scholarship block also hid, leaving only the App Download card.
     */
    reportDomain() {
      return String(this.$store.state.report || "").replace(/_/g, "");
    },
    dashHasCoreData() {
      const d = this.dash;
      if (!d || typeof d !== "object") return false;
      return (
        d.students != null ||
        d.staff != null ||
        d.collection != null ||
        d.dues != null
      );
    },

    /** Fee/collection dashboard blocks (hidden on TCF like legacy statistics/dues). */
    showCollectionInsights() {
      if (this.isTcfDomain) return false;
      return this.showCollection && this.sectionVisible("collection");
    },
    /** Dashboard read-only calendar (Happiness uses the full app calendar at top; hidden on TCF). */
    showEventsCalendar() {
      if (this.isTcfDomain) return false;
      return !this.isHappinessFoundationDomain;
    },
    showHoldFeesPanel() {
      if (this.isHappinessFoundationDomain) return false;
      return this.sectionVisible("hold-fees-stats");
    },
    /** Head-office attendance: share of campuses already marked. */
    campusMarkedPercent() {
      const total = Number(this.attSummary && this.attSummary.totalCampus) || 0;
      if (total <= 0) return 0;
      const marked = Number(this.attSummary.markedCampus) || 0;
      return Math.min(100, Math.round((marked / total) * 100));
    },
    /** Head-office homework: share of campuses that assigned homework. */
    hwCampusMarkedPercent() {
      const total = Number(this.hwSummary && this.hwSummary.totalCampuses) || 0;
      if (total <= 0) return 0;
      const marked = Number(this.hwSummary.markedCampuses) || 0;
      return Math.min(100, Math.round((marked / total) * 100));
    },
    showCalendarDemographicsRow() {
      return this.showEventsCalendar || this.showColorsDemographics;
    },
    colorsDemographicsPaired() {
      return this.showEventsCalendar && this.showColorsDemographics;
    },
    /**
     * Yearly paid-vs-unpaid chart.
     * Hidden for Happiness Foundation only (its dashboard has no fee blocks).
     * Do NOT gate this on `sectionVisible("collection")`: that flag is an
     * opt-in role right, so requiring it hid the chart on every other domain.
     * Data availability (`showCollection` / `showDues`) already tells us
     * whether there is anything to draw.
     */
    showYearlyCollectionChart() {
      if (this.isHappinessFoundationDomain) return false;
      return this.showCollection && this.showDues;
    },
    showAcademicsHub() {
      if (this.isHappinessFoundationDomain) return false;
      if (!this.dashHasCoreData) return false;
      if (this.isTcfDomain) return false;
      return (
        this.sectionVisible("statistics") ||
        this.sectionVisible("student-statistics") ||
        this.sectionVisible("attendance") ||
        this.sectionVisible("homework")
      );
    },
    showBirthdaysAboveStudentStrength() {
      if (this.isTcfDomain) return false;
      if (this.isHappinessFoundationDomain) return false;
      return this.showAcademicsHub;
    },
    showBirthdaysAboveAppDownloadStrength() {
      if (this.isTcfDomain) return false;
      if (this.isHappinessFoundationDomain) return false;
      if (this.showAcademicsHub) return false;
      return this.sectionVisible("app-downloads");
    },
    showBirthdaysAfterStatisticsFallback() {
      if (this.isTcfDomain) return false;
      if (this.isHappinessFoundationDomain) return false;
      if (this.showAcademicsHub) return false;
      if (this.sectionVisible("app-downloads")) return false;
      return true;
    },
    remainingNotAccepted() {
      return (
        this.scholarshipTotals.notAccepted -
        this.scholarshipTotals.notAcceptedLeft
      );
    },
    facultyStatsDistinctClasses() {
      const set = new Set();
      const rows = this.classStats || [];
      for (const row of rows) {
        const arr = row.classes;
        if (!Array.isArray(arr)) continue;
        for (const b of arr) {
          if (b != null && b.className != null) set.add(b.className);
        }
      }
      return [...set].sort((a, b) => a - b);
    },
    facultyStatsFields() {
      // const tdClass = "text-white font-weight-bold";
      const thClass = "bg-success text-white";
      const fields = [{ key: "section", label: "faculty", thClass }];

      for (const mid of this.facultyStatsDistinctClasses) {
        fields.push({ key: `mdb_${mid}`, label: mid, thClass });
      }
      return fields;
    },
    showPipelineInsights() {
      const s = this.enrolmentOverviewSetting;
      if (!s) return false;
      if (s.valueBool === true) return true;
      const v = s.value;
      return (
        v === true ||
        v === 1 ||
        String(v).toLowerCase() === "true" ||
        String(v) === "1"
      );
    },
    showAccountBalance() {
      return canShowAccountBalanceDashboard(
        this.reportDomain,
        this.$store.state.rights
      );
    },
    showColorsDemographics() {
      return !!(
        this.colorsDemographicsObj && this.colorsDemographicsObj.valueBool
      );
    },
    // myskoolhpgs: explicit role right (like result-comparison). Others: keep Statistics.
    staffDesignationStatsVisible() {
      if (this.isHappinessFoundationDomain) return false;
      if (this.reportDomain === "myskoolhpgs") {
        return this.sectionVisible("staff-designation-stats");
      }
      return this.sectionVisible("statistics");
    },
    isTcfDomain() {
      const hostname =
        typeof window !== "undefined"
          ? window.location.hostname.toLowerCase()
          : "";
      const slug = String(this.reportDomain || "").toLowerCase();
      return (
        ["myskooltcfdemo", "myskooltcf"].includes(slug) ||
        hostname === "myskooltcf.myskool.app"
      );
    },
    feeDuesMaxAmount() {
      const nums = (this.feeItems || []).map((r) => Number(r.amount) || 0);
      return Math.max(...nums, 1);
    },
    /** Keep left mosaic column as tall as the bar chart (FeeBarChart.applyChartHeight). */
    duesChartBodyHeight() {
      const rows = (this.feeItems && this.feeItems.length) || 0;
      if (!rows) return 280;
      return Math.min(720, Math.max(280, rows * 44 + 48));
    },
    feeDuesTotalFormatted() {
      const sum = (this.feeItems || []).reduce(
        (acc, r) => acc + (Number(r.amount) || 0),
        0
      );
      return sum.toLocaleString();
    },
    /** Report domain slug (normalized, same base as TCF checks). */
    isHappinessFoundationDomain() {
      const fromReport = String(this.reportDomain || "")
        .replace(/_/g, "")
        .toLowerCase();
      const fromStore = String(this.$store.state.report || "")
        .replace(/_/g, "")
        .toLowerCase();
      return (
        fromReport === "myskoolhappinessfoundation" ||
        fromStore === "myskoolhappinessfoundation"
      );
    },
    activeColorsHouse() {
      if (!this.colorsDemographics || !this.colorsDemographics.houses) {
        return null;
      }
      const houses = this.colorsDemographics.houses;
      if (!houses.length) return null;
      if (this.selectedColorsHouse) {
        return (
          houses.find((h) => h.house === this.selectedColorsHouse) || houses[0]
        );
      }
      return houses[0];
    },
    activeColorsStudentRows() {
      if (!this.activeColorsHouse || !this.activeColorsHouse.classes) return [];
      const rows = [];
      this.activeColorsHouse.classes.forEach((cls) => {
        (cls.students || []).forEach((student) => {
          rows.push({
            className: cls.className,
            name: student.name,
            grNo: student.grNo || "—",
            sectionName: student.sectionName || "—",
            gender: student.gender || "—",
          });
        });
      });
      return rows;
    },
    colorsHouseClassBarSeries() {
      if (!this.activeColorsHouse || !this.activeColorsHouse.classes) return [];
      return [
        {
          name: "Students",
          data: this.activeColorsHouse.classes.map((c) => c.count),
        },
      ];
    },
    colorsHouseClassBarOptions() {
      const house = this.activeColorsHouse ? this.activeColorsHouse.house : "";
      const classes =
        (this.activeColorsHouse && this.activeColorsHouse.classes) || [];
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "inherit",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 900,
            animateGradually: {
              enabled: true,
              delay: 110,
            },
            dynamicAnimation: {
              enabled: true,
              speed: 750,
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 8,
            barHeight: this.colorsDemographicsPaired ? "72%" : "45%",
            distributed: true,
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "horizontal",
            shadeIntensity: 0.25,
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 0.8,
            stops: [0, 100],
          },
        },
        colors: classes.map(() => this.houseHexColor(house)),
        dataLabels: {
          enabled: true,
          style: { fontSize: "11px", fontWeight: 600 },
        },
        xaxis: {
          categories: classes.map((c) => c.className),
          labels: { style: { fontSize: "12px", colors: "#6e6b7b" } },
        },
        yaxis: {
          labels: {
            style: {
              fontSize: this.colorsDemographicsPaired ? "11px" : "12px",
              colors: "#6e6b7b",
            },
            offsetX: this.colorsDemographicsPaired ? 0 : 0,
            maxWidth: this.colorsDemographicsPaired ? 88 : 120,
          },
        },
        grid: {
          borderColor: "#ebe9f1",
          strokeDashArray: 4,
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: false } },
          padding: {
            left: this.colorsDemographicsPaired ? 4 : 12,
            right: 8,
          },
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter(val) {
              return val + " students";
            },
          },
        },
      };
    },
    colorsHouseClassBarHeight() {
      const count =
        this.activeColorsHouse && this.activeColorsHouse.classes
          ? this.activeColorsHouse.classes.length
          : 0;
      if (this.colorsDemographicsPaired) {
        return Math.min(340, Math.max(220, 40 + count * 24));
      }
      return Math.min(450, Math.max(250, 40 + count * 35));
    },
    filteredHwItems() {
      let items = this.hwItems;
      if (!this.isHeadOffice) {
        items = items.filter(
          (item) => item.campusID == this.$store.state.userData.cId
        );
      }
      if (!this.hwSearchQuery) return items;
      const query = this.hwSearchQuery.toLowerCase();
      return items.filter((item) =>
        item.campusName.toLowerCase().includes(query)
      );
    },
    filteredCampusData() {
      if (!this.campusSearchQuery || !this.downloadItems)
        return this.downloadItems;
      const query = this.campusSearchQuery.toLowerCase();
      return this.downloadItems.filter(
        (item) =>
          item.branch.toLowerCase().includes(query) ||
          item.campusName.toLowerCase().includes(query)
      );
    },
    downloadPercentage() {
      if (!this.downloadStats) return { students: 0, staff: 0 };
      const total =
        this.downloadStats.activeStudents + this.downloadStats.activeStaff;
      return {
        students:
          total > 0 ? (this.downloadStats.activeStudents / total) * 100 : 0,
        staff: total > 0 ? (this.downloadStats.activeStaff / total) * 100 : 0,
      };
    },
    activationRate() {
      if (!this.downloadStats || this.downloadStats.totalDownloads === 0)
        return "0.0";
      return (
        (this.downloadStats.totalActiveUsers /
          this.downloadStats.totalDownloads) *
        100
      ).toFixed(1);
    },
    activeListLength() {
      return this.filteredUserListItems.length;
    },
    filteredUserListItems() {
      const items = this.currentTableItems;
      const q = (this.userListSearch || "").trim().toLowerCase();
      if (!q) return items;
      return items.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(q)
      );
    },
    pagedUserListItems() {
      const start = (this.userListCurrentPage - 1) * this.userListPerPage;
      return this.filteredUserListItems.slice(start, start + this.userListPerPage);
    },
    currentTableItems() {
      if (!this.downloadStats) return [];
      switch (this.userListModal.activeTab) {
        case 0:
          return this.downloadStats.activeStudentsList;
        case 1:
          return this.downloadStats.inactiveStudentsList;
        case 2:
          return this.downloadStats.activeStaffList;
        case 3:
          return this.downloadStats.inactiveStaffList;
        default:
          return [];
      }
    },
    currentTableFields() {
      const isPending = this.userListModal.activeTab % 2 === 1;
      let fields;
      if (this.userListModal.activeTab >= 2) {
        fields = [
          { key: "sno", label: "S.No", thStyle: { width: "50px" } },
          { key: "name", label: "Staff Name", sortable: true },
          { key: "designation", label: "Designation", sortable: true },
          { key: "phoneNumber", label: "Phone", sortable: true },
        ];
      } else {
        fields = [
          { key: "sno", label: "S.No", thStyle: { width: "50px" } },
          {
            key: "grNo",
            label: "GR No",
            sortable: true,
            thStyle: { width: "100px" },
          },
          { key: "name", label: "Student Name", sortable: true },
          { key: "father_name", label: "Father Name", sortable: true },
          {
            key: "className",
            label: "Class",
            formatter: (val, key, item) => `${val} - ${item.sectionName}`,
            sortable: true,
          },
          {
            key: "contacts",
            label: "App / Contacts",
            thStyle: { minWidth: "400px" },
          },
        ];
      }
      if (!isPending) return fields;
      return [
        {
          key: "selected",
          label: "",
          thStyle: { width: "40px" },
          tdClass: "text-center align-middle",
        },
        ...fields,
        {
          key: "actions",
          label: "",
          thStyle: { width: "48px" },
          tdClass: "text-center align-middle",
        },
      ];
    },
    studentDownloadRate() {
      if (!this.downloadStats || this.downloadStats.studentStrength === 0)
        return "0.0";
      return (
        (this.downloadStats.activeStudents /
          this.downloadStats.studentStrength) *
        100
      ).toFixed(1);
    },
    staffDownloadRate() {
      if (!this.downloadStats || this.downloadStats.staffStrength === 0)
        return "0.0";
      return (
        (this.downloadStats.activeStaff / this.downloadStats.staffStrength) *
        100
      ).toFixed(1);
    },
    appBundleOptions() {
      const setting = this.statsBundleSetting;
      if (!setting || !setting.valueBool || !setting.value) return [];

      const customBundleId = String(setting.value).trim();
      if (!customBundleId) return [];

      return [
        {
          label: this.formatBundleLabel(customBundleId),
          value: customBundleId,
        },
        { label: "Myskool App", value: "com.myskool.ae" },
      ];
    },
    showAppBundleSelector() {
      return this.appBundleOptions.length > 0;
    },
    appStatsClassOptions() {
      const list = (this.appStatsClasses || []).map((cls) => ({
        id: Number(cls.id) || 0,
        name: cls.name || cls.className || `Class ${cls.id}`,
      }));
      return [{ id: 0, name: "All Classes" }, ...list];
    },
    appStatsDepartmentOptions() {
      const list = (this.appStatsDepartments || []).map((dep) => ({
        id: Number(dep.id) || 0,
        name: dep.name || dep.department || `Department ${dep.id}`,
      }));
      return [{ id: 0, name: "All Departments" }, ...list];
    },
    isPendingUserListTab() {
      return this.userListModal.activeTab % 2 === 1;
    },
    appPendingSelectedCount() {
      return Object.keys(this.appPendingSelection || {}).length;
    },
    appPendingSelectedItems() {
      return Object.values(this.appPendingSelection || {});
    },
    appPendingAllFilteredSelected() {
      const items = this.filteredUserListItems || [];
      if (!items.length || !this.isPendingUserListTab) return false;
      return items.every((item, index) =>
        this.isAppPendingSelected(item, index)
      );
    },
    appPendingSomeFilteredSelected() {
      if (!this.isPendingUserListTab || this.appPendingSelectedCount === 0)
        return false;
      return !this.appPendingAllFilteredSelected;
    },
    appPendingRecipients() {
      return this.appPendingMsg.targets || [];
    },
    appPendingRecipientMeta() {
      const isStaff = !!this.appPendingMsg.isStaff;
      const items = this.appPendingMsg.targets || [];
      const ids = [];
      const phones = [];
      items.forEach((item) => {
        if (!isStaff && item.__contactType) {
          const contactPhone = this.getAppDownloadRecipientPhone(item, false);
          if (contactPhone) phones.push(contactPhone);
          return;
        }
        if (isStaff) {
          const id = this.getAppDownloadRecipientId(item, true);
          if (id > 0) ids.push(id);
          return;
        }
        if (this.hasStudentAppInstalled(item)) {
          const id = this.getAppDownloadRecipientId(item, false);
          if (id > 0) ids.push(id);
          return;
        }
        const phone = this.getAppDownloadRecipientPhone(item, false);
        if (phone) phones.push(phone);
      });
      return {
        isStaff,
        ids: [...new Set(ids)],
        phones: [...new Set(phones)],
        total: items.length,
      };
    },
    canSendAppPendingMessage() {
      if (!String(this.appPendingMsg.message || "").trim()) return false;
      const meta = this.appPendingRecipientMeta;
      return meta.ids.length > 0 || meta.phones.length > 0;
    },
    canSendAppPendingNotification() {
      if (!String(this.appPendingMsg.message || "").trim()) return false;
      const meta = this.appPendingRecipientMeta;
      return meta.ids.length > 0;
    },
  },
  methods: {
    ...mapActions({
      get: "get",
      post: "post",
      put: "put",
      delete: "delete",
      postObj: "postObj",
    }),

    formatFeeAmount(val) {
      return (Number(val) || 0).toLocaleString();
    },
    feeDuesBarPercent(amount) {
      const n = Number(amount) || 0;
      const pct = Math.round((n / this.feeDuesMaxAmount) * 100);
      return `${Math.min(100, Math.max(4, pct))}%`;
    },
    feeDuesSharePercent(amount) {
      const n = Number(amount) || 0;
      if (!this.feeDuesMaxAmount) return 0;
      return Math.min(
        100,
        Math.max(0, Math.round((n / this.feeDuesMaxAmount) * 100))
      );
    },
    feeDuesPalette() {
      return [
        "#6366f1",
        "#8b5cf6",
        "#0ea5e9",
        "#14b8a6",
        "#f59e0b",
        "#ef4444",
        "#64748b",
        "#ec4899",
      ];
    },
    feeDuesAccentColor(index) {
      return this.feeDuesPalette()[index % this.feeDuesPalette().length];
    },
    feeDuesBarGradient(index) {
      const c = this.feeDuesAccentColor(index);
      return `linear-gradient(90deg, ${c}, ${c}cc)`;
    },

    /** Convert a homework summary figure into a share of the total. */
    hwStatShare(value) {
      const total = Number(this.hwSummary && this.hwSummary.totalHomework) || 0;
      if (total <= 0) return 0;
      return Math.min(100, Math.round((Number(value) || 0) / total) * 100);
    },

    // Dashboard-load sweep: creates/removes "Probation Period Completion"
    // notifications for staff whose probation has ended. Side-effect only.
    // cID 1 = all campuses (head office), handled by the API.
    async checkProbationCompletion() {
      const cID = this.$store.state.userData.cId;
      if (!cID) return;

      try {
        await this.get({
          url:
            this.$store.state.domain +
            "Staff/CheckProbationCompletion?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            cID,
          token: this.$store.state.userData.token,
        });
      } catch (error) {
        console.error("Failed to check probation completion", error);
      }
    },

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    onPicturePreviewError(event, url) {
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },

    switchCategory(index) {
      if (this.userListModal.activeTab === index) return;
      this.userListModal.loading = true;
      this.userListCurrentPage = 1;
      this.clearAppPendingSelection();
      setTimeout(() => {
        this.userListModal.activeTab = index;
        this.userListModal.loading = false;
      }, 300);
    },
    getAppDownloadUserListTabMeta() {
      const tabs = [
        { label: "Active Stud.", slug: "active-students", isStaff: false },
        { label: "Inact. Stud.", slug: "inactive-students", isStaff: false },
        { label: "Active Staff", slug: "active-staff", isStaff: true },
        { label: "Inact. Staff", slug: "inactive-staff", isStaff: true },
      ];
      return tabs[this.userListModal.activeTab] || tabs[0];
    },
    async exportAppDownloadUserList() {
      const items = this.filteredUserListItems;
      if (!items.length) {
        this.$toast.warning("No records to export.", {
          position: "top-right",
          duration: 2000,
        });
        return;
      }

      this.userListExporting = true;
      try {
        const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");
        const tab = this.getAppDownloadUserListTabMeta();
        const statusLabel =
          this.userListModal.activeTab % 2 === 0 ? "INSTALLED" : "PENDING";
        const stamp = new Date().toISOString().slice(0, 10);
        const fname = `app-download-${tab.slug}-${stamp}.xlsx`;

        let expData;
        if (tab.isStaff) {
          expData = items.map((item, index) => ({
            "S.No": index + 1,
            "Staff Name": item.name || "",
            Designation: item.designation || "",
            "Phone Number": item.phoneNumber || "",
            Status: statusLabel,
          }));
        } else {
          expData = items.map((item, index) => {
            const classLabel = [item.className, item.sectionName]
              .filter(Boolean)
              .join(" - ");
            const contacts = this.getStudentContacts(item);
            const contactCols = {};
            contacts.forEach((contact) => {
              const label = String(contact.label || "").trim();
              contactCols[`${label} Phone`] = contact.phone || "";
              contactCols[`${label} App`] = contact.installed ? "Yes" : "No";
            });
            return {
              "S.No": index + 1,
              "GR No": item.grNo != null ? String(item.grNo) : "",
              "Student Name": item.name || "",
              "Father Name": item.father_name || "",
              Class: classLabel,
              ...contactCols,
              Status: statusLabel,
            };
          });
        }

        const worksheet = XLSX.utils.json_to_sheet(expData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          tab.label.slice(0, 31)
        );
        XLSX.writeFile(workbook, fname);

        this.$toast.success(`Exported ${items.length} record(s).`, {
          position: "top-right",
          duration: 2000,
        });
      } finally {
        this.userListExporting = false;
      }
    },
    /** Parse "0/15 (0%)" style summaries into parts for the meter. */
    parseHwRatio(value) {
      const str = String(value == null ? "" : value);
      const match = str.match(/(\d+)\s*\/\s*(\d+)/);
      const done = match ? Number(match[1]) : 0;
      const total = match ? Number(match[2]) : 0;
      const pctMatch = str.match(/\(([\d.]+)\s*%\)/);
      const pct = pctMatch
        ? Math.min(100, Math.round(Number(pctMatch[1])))
        : total > 0
        ? Math.min(100, Math.round((done / total) * 100))
        : 0;
      return { done, total, pct };
    },
    sectionVisible(type) {
      const tcfFeeSections = [
        "statistics",
        "dues",
        "collection",
        "hold-fees-stats",
      ];
      if (this.isTcfDomain && tcfFeeSections.includes(type)) {
        return false;
      }

      // Happiness: keep fee/collection blocks off even when role grants them
      if (this.isHappinessFoundationDomain) {
        if (type === "hold-fees-stats" || type === "collection") {
          return false;
        }
      }

      // Opt-in sections (same rights model on every domain, including Happiness)
      if (type === "result-comparison") {
        return this.dashRights.includes("result-comparison");
      }
      if (type === "hold-fees-stats") {
        return this.dashRights.includes("hold-fees-stats");
      }
      if (type === "collection") {
        // Opt-out, not opt-in. Roles that never configured dashboard rights
        // must keep the collection blocks (same as before this right existed);
        // only hide when the role has an explicit rights list without it.
        const rights = this.dashRights || [];
        if (rights.includes("collection")) return true;
        const otherRights = rights.filter((r) => r !== "collection");
        return otherRights.length === 0;
      }
      if (type === "staff-designation-stats") {
        return this.dashRights.includes("staff-designation-stats");
      }

      if (this.isHappinessFoundationDomain) {
        return !this.hiddenSections.includes(type);
      }
      // Other sections: empty rights = all visible. Ignore opt-in rights
      // so granting only those does not hide the rest of the dashboard.
      const sectionRights = (this.dashRights || []).filter(
        (r) =>
          r !== "result-comparison" &&
          r !== "hold-fees-stats" &&
          r !== "collection" &&
          r !== "staff-designation-stats"
      );
      if (sectionRights.length == 0) return true;
      return sectionRights.includes(type);
    },
    async LoadSettingsAll() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettingsAll?db=" +
          this.$store.state.userData.db,
        body: [
          "visitor_checkout",
          "house_colors_demographics",
          "stats_bundleID",
          "enrolment_overview",
        ],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      if (!Array.isArray(res)) return;
      let obj = res.find((el) => el.key == "visitor_checkout");
      this.showVisitor = obj ? obj.valueBool : false;
      this.colorsDemographicsObj = res.find(
        (el) => el.key == "house_colors_demographics"
      );
      this.statsBundleSetting =
        res.find((el) => el.key == "stats_bundleID") || null;
      this.enrolmentOverviewSetting =
        res.find((el) => el.key == "enrolment_overview") || null;
      this.initAppBundleSelection();
      if (this.showColorsDemographics) {
        this.loadColorsDemographics();
      }
      if (this.sectionVisible("app-downloads")) {
        this.LoadAppDownloadStats();
      }
    },
    async LoadSettings() {
      this.settingLoading = true;
      // `post` resolves to `false` on an API/transport error, so `res` is not
      // always an array. Guard it and always clear `settingLoading`, otherwise
      // the TCF scholarship block below stays hidden behind the skeleton.
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["head_office", "pcp_only"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.settingLoading = false;
      if (!Array.isArray(res)) {
        if (this.isHeadOffice === null) this.isHeadOffice = false;
        return;
      }

      let ho = res.find((el) => el.key == "head_office");
      this.isHeadOffice = ho ? ho.valueBool : false;

      let pcpObj = res.find((el) => el.key == "pcp_only");
      this.pcpOnly = pcpObj ? pcpObj.valueBool : false;

      const canLoadCampuses =
        this.isHeadOffice ||
        this.attVisible.includes(this.reportDomain) ||
        (this.sectionVisible("result-comparison") &&
          (this.isHeadOffice || Number(this.$store.state.userData.cId) === 1));

      if (canLoadCampuses) {
        this.LoadCampuses();
      }

      if (this.isHeadOffice || this.attVisible.includes(this.reportDomain)) {
        this.LoadAttendance();
        if (this.isHeadOffice) this.setHeadOffice();
        // if (
        //   this.isHeadOffice &&
        //   ["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain)
        // ) {
        //   this.LoadScholarshipStatistics();
        //   this.LoadOtherStats();
        // }
      }

      if (this.sectionVisible("homework")) {
        this.LoadHomework();
      }
      if (
        !this.isHeadOffice &&
        this.isTcfDomain
      ) {
        this.LoadUtpCampuses();
      }
    },
    formatBundleLabel(bundleId) {
      const lastPart =
        String(bundleId || "")
          .split(".")
          .pop() || "";
      if (!lastPart) return bundleId;

      const withSpaces = lastPart
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");

      if (!/[A-Z]/.test(lastPart)) {
        const suffixes = [
          "digital",
          "school",
          "academy",
          "college",
          "international",
        ];
        for (const suffix of suffixes) {
          if (lastPart.endsWith(suffix) && lastPart.length > suffix.length) {
            const prefix = lastPart.slice(0, -suffix.length);
            return [prefix, suffix]
              .filter(Boolean)
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              )
              .join(" ");
          }
        }
      }

      return withSpaces
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    },
    initAppBundleSelection() {
      if (!this.showAppBundleSelector) {
        this.selectedAppBundleID = null;
        return;
      }

      const hasSelection = this.appBundleOptions.some(
        (option) => option.value === this.selectedAppBundleID
      );
      if (!hasSelection && this.appBundleOptions.length) {
        this.selectedAppBundleID = this.appBundleOptions[0].value;
      }
    },
    appBundleDropdownPosition(dropdownList, component, { width, top, left }) {
      dropdownList.classList.add("app-bundle-dropdown-menu");
      dropdownList.style.top = top;
      dropdownList.style.left = left;
      dropdownList.style.width = width;
      dropdownList.style.minWidth = width;
    },
    closeAppBundleDropdownOnScroll() {
      const select = this.$refs.appBundleSelect;
      if (select && select.open) {
        select.open = false;
      }
    },
    setHeadOffice() {
      // this.LoadHomework();
      this.LoadSummary();
      // this.LoadAppDownloadStats();

      if (this.amountVisible.includes(this.reportDomain)) {
        this.fields.push(
          { label: "amount", key: "amount" },
          { label: "total", key: "total" },
          { label: "actions", key: "actions" }
        );
      }
    },
    setCamp() {
      if (this.campIDs.at(-1) == 0) {
        this.campIDs = [0];
      } else {
        this.campIDs = this.campIDs.filter((el) => el != 0);
      }
    },
    setAttCamp() {
      if (this.attCampIDs.at(-1) == 0) {
        this.attCampIDs = [0];
      } else {
        this.attCampIDs = this.attCampIDs.filter((el) => el != 0);
      }
    },
    printSummary(formatText) {
      let ids =
        this.campIDs.length == 0 || this.campIDs[0] == 0
          ? this.campuses.map((el) => el.id)
          : this.campIDs;

      var url =
        `https://${this.reportDomain}.myskool.app/Fee/Show?type=dcr&dtFrom=${this.lastLoad.from}&dtTo=${this.lastLoad.to}&dbb=` +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&month=&feeType=&format=" +
        formatText +
        "&campusIDs=" +
        ids;
      window.open(url, "_blank");
    },
    openCashlog(id, type) {
      // console.log(this.lastLoad);
      var url =
        `https://${this.reportDomain}.myskool.app/Fee/Show?type=dcr&dtFrom=${this.lastLoad.from}&dtTo=${this.lastLoad.to}&dbb=` +
        this.$store.state.userData.db +
        "&cID=" +
        id +
        "&month=&feeType=&format=" +
        type;
      window.open(url, "_blank");
    },
    openAttachment(url) {
      const preview = this.getPicturePreview(url) || url;
      if (preview) window.open(preview, "_blank");
    },

    slipAttachmentList(attachment) {
      if (!attachment) return [];
      return String(attachment)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    },

    openSlip(item) {
      let obj = this.slipData.find((el) => el.campusID == item.campusID);
      // console.log(obj);
      if (obj) {
        this.currentSlip = { ...obj, campusName: item.campusName };
        this.$bvModal.show("modal-slip");
      }
    },
    openEditSlip() {
      this.slipObj = { ...this.currentSlip };
      this.slipImages = this.slipAttachmentList(this.slipObj.attachment);
      this.slipForm = true;
    },
    resetSlipForm() {
      console.log(this.slipObj);
      this.slipForm = false;
      this.slipObj = null;
      this.slipImages = [];
    },
    async addSlipImages() {
      const input = this.$refs.slipDocs;
      if (!input || !input.files || !input.files.length) return;

      this.docLoading = true;
      try {
        const uploaded = await uploadToMySkool(input.files);
        const paths = getUploadedPaths(uploaded);
        this.slipImages = [...this.slipImages, ...paths];
        this.slipObj.attachment = this.slipImages.join(",");
      } catch (err) {
        console.log(err);
      } finally {
        this.docLoading = false;
        if (input) input.value = "";
      }
    },
    removeSlipImage(index) {
      this.slipImages.splice(index, 1);
      // Update the slipObj.attachment to reflect remaining images
      this.slipObj.attachment = this.slipImages.join(",");
    },
    async saveSlip() {
      let valid = true;

      // Validate amount
      let x = parseInt(this.slipObj.amount);
      if (isNaN(x) || x < 0) {
        this.$refs.slip_amount.state = false;
        valid = false;
      } else {
        this.$refs.slip_amount.state = true;
        this.slipObj.amount = x;
      }

      // Validate date
      // if (!this.slipObj.date) {
      //   this.$refs.slip_date.state = false;
      //   valid = false;
      // } else {
      //   this.$refs.slip_date.state = true;
      // }

      // Validate attachment
      if (!this.slipObj.attachment) {
        this.$refs.slip_attach.state = false;
        valid = false;
      } else {
        this.$refs.slip_attach.state = true;
      }

      // Validate type
      if (!this.slipObj.type) {
        this.$refs.slip_type.state = false;
        valid = false;
      } else {
        this.$refs.slip_type.state = true;
      }

      if (valid) {
        // console.log(this.slipObj);
        this.slipSaving = true;
        var status = await this.put({
          url:
            this.$store.state.domain +
            "depositSlip/" +
            this.slipObj.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Slip updated successfully.",
          context: this,
          body: this.slipObj,
          token: this.$store.state.userData.token,
        });
        this.slipSaving = false;
        // console.log(res);
        if (status) {
          // Update the slip in slipData array
          const index = this.slipData.findIndex(
            (slip) => slip.id === this.slipObj.id
          );
          if (index !== -1) {
            this.slipData[index] = { ...this.slipObj };
          }
          console.log(this.slipData);
          this.currentSlip = { ...this.slipObj };
          this.resetSlipForm();
        }
      }
    },
    async deleteSlip() {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        this.slipDeleting = true;
        const status = await this.delete({
          url:
            this.$store.state.domain +
            "depositSlip/" +
            this.currentSlip.id +
            "?db=" +
            this.$store.state.userData.db,
          token: this.$store.state.userData.token,
          message: "Slip deleted successfully!",
          context: this,
        });
        this.slipDeleting = false;
        if (status) {
          // Remove the deleted slip from slipData array
          this.slipData = this.slipData.filter(
            (slip) => slip.id !== this.currentSlip.id
          );
          this.LoadSummary();
          this.$bvModal.hide("modal-slip");
          this.$toast.success("Slip deleted successfully!");
        }
      }
    },

    dashSnapshotCacheKey() {
      const u = this.$store.state.userData || {};
      return `myskool_dash_v1_${u.db}_${u.cId}_${this.currentYear}`;
    },
    hydrateDashFromCache() {
      try {
        const raw = sessionStorage.getItem(this.dashSnapshotCacheKey());
        if (!raw) return false;
        const snap = JSON.parse(raw);
        if (!snap || !snap.dash) return false;
        this.dash = snap.dash;
        this.otherData = snap.otherData || {
          familyCount: 0,
          monthly: 0,
        };
        this.visitorObj = snap.visitorObj || null;
        if (Array.isArray(snap.feeItems)) {
          this.feeItems = snap.feeItems;
        }
        return true;
      } catch (e) {
        return false;
      }
    },
    saveDashSnapshot() {
      try {
        sessionStorage.setItem(
          this.dashSnapshotCacheKey(),
          JSON.stringify({
            dash: this.dash,
            otherData: this.otherData,
            visitorObj: this.visitorObj,
            feeItems: this.feeItems,
            savedAt: Date.now(),
          })
        );
      } catch (e) {
        /* quota / private mode */
      }
    },
    applyDashboardResponse(res, { loadSecondary = true } = {}) {
      this.dash = res.data;
      this.otherData = {
        familyCount: res.familyCount,
        monthly: res.monthly,
      };
      this.visitorObj = res.visitor;
      this.feeItems = res.outstandingData;
      if (!loadSecondary) return;
      this.$nextTick(() => {
        this.loadAcademicsHubData();
      });
    },
    async LoadData() {
      const hadCache = this.hydrateDashFromCache();
      this.statsLoading = !hadCache;

      this.LoadSettingsAll().catch(() => {});

      var obj = {
        url:
          this.$store.state.domain +
          "Users/Dashboard?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&year=" +
          this.currentYear,
        token: this.$store.state.userData.token,
        dashboard: true,
      };
      try {
        const res = await this.get(obj);
        // `get` resolves to [] on transport/API errors — keep the cached
        // snapshot instead of blanking the whole dashboard.
        if (res && res.data) {
          this.applyDashboardResponse(res);
          this.saveDashSnapshot();
        }
      } finally {
        this.statsLoading = false;
        this.loading = false;
      }
    },
    async LoadSubEnrolled() {
      this.subLoading = true;
      let res = await this.get({
        url:
          this.$store.state.domain +
          "Users/DashboardSubjectsEnrolled?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      });
      this.subData = res;
      this.subLoading = false;
    },

    async printScholarshipSummary(isExport) {
      this.scholarshipPrinting = !isExport;
      this.scholarshipExporting = isExport;
      this.scholarshipStudents = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/ScholarshipSummaryAll?db=" +
          this.$store.state.userData.db +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&category=" +
          (this.selectedCategory.join(",") || ""),
        token: this.$store.state.userData.token,
      });
      // console.log(this.scholarshipStudents);
      if (isExport) await this.exportScholarshipReport();
      else this.printScholarshipReport();

      this.scholarshipPrinting = false;
      this.scholarshipExporting = false;
    },
    escapeHtml(text) {
      if (text == null) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    statusVariant(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s === "active") return "success";
      if (s === "in-active" || s === "inactive") return "danger";
      if (s === "on-probation") return "warning";
      return "secondary";
    },
    scholarshipVariant(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s === "accepted") return "success";
      if (s === "not-accepted" || s === "not accepted") return "danger";
      if (s === "pending") return "warning";
      return "secondary";
    },
    async printScholarshipReport() {
      const campusMeta = this.$store.state.currentBranch || {};
      const displayName = campusMeta.name || "";
      const branch = campusMeta.branch || "";

      const schoolUpper = displayName.toUpperCase();
      const campusUpper = branch.toUpperCase();

      const now = new Date();
      const periodStr = now.toLocaleString("en-GB", {
        month: "long",
        year: "numeric",
      });
      const generatedStr = now.toLocaleString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const reportTitle = this.$t("Scholarship Status Report");
      const recordCount = (this.scholarshipStudents || []).length;

      const mainTitleHtml = campusUpper
        ? `<span class="school-name">${this.escapeHtml(
            schoolUpper
          )}</span><span class="title-dash"> — </span><span class="campus-name">${this.escapeHtml(
            campusUpper
          )}</span>`
        : `<span class="school-name">${this.escapeHtml(schoolUpper)}</span>`;

      const metaHtml = `${this.escapeHtml(
        reportTitle
      )} <span class="meta-sep">|</span> <strong>${this.$t(
        "Period"
      )}:</strong> ${this.escapeHtml(
        periodStr
      )} <span class="meta-sep">|</span> <strong>${this.$t(
        "Records"
      )}:</strong> ${recordCount} <span class="meta-sep">|</span> <strong>${this.$t(
        "Generated on"
      )}</strong> ${this.escapeHtml(generatedStr)}`;

      const thead = `<tr>
        <th>${this.$t("Federal Board Reg No")}</th>
        <th>${this.$t("Student Name")}</th>
        <th>${this.$t("Father Name")}</th>
        <th>${this.$t("Institute")}</th>
        <th>${this.$t("Class")}</th>
        <th>${this.$t("Scholarship Category")}</th>
        <th>${this.$t("Scholarship Status")}</th>
        <th>${this.$t("Student Status")}</th>
        <th>WhatsApp No</th>
        <th>Parent's Contact</th>
        <th>Student's Contact</th>
      </tr>`;

      const rows = (this.scholarshipStudents || [])
        .map((item) => {
          const camp = item.campus || {};
          const st = item.student || {};
          const det = item.studentDetails || {};
          const schV = this.scholarshipVariant(det.scholarshipStatus || "");
          const stV = this.statusVariant(det.studentStatus || "");
          const fed = det.federalRegNo ? String(det.federalRegNo) : "—";
          const wa =
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "—";
          const parentC =
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "—";
          const studentC =
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "—";
          return `<tr>
            <td>${this.escapeHtml(fed)}</td>
            <td>${this.escapeHtml(st.name || "—")}</td>
            <td>${this.escapeHtml(st.father_name || "—")}</td>
            <td>${this.escapeHtml(camp.name || "—")}</td>
            <td>${this.escapeHtml(st.meta_class || "—")}</td>
            <td>${this.escapeHtml(det.scholarshipCategory || "—")}</td>
            <td class="pv-hl-${schV}">${this.escapeHtml(
            det.scholarshipStatus || "—"
          )}</td>
            <td class="pv-hl-${stV}">${this.escapeHtml(
            det.studentStatus || "—"
          )}</td>
            <td>${this.escapeHtml(wa)}</td>
            <td>${this.escapeHtml(parentC)}</td>
            <td>${this.escapeHtml(studentC)}</td>
          </tr>`;
        })
        .join("");

      const emptyRow = `<tr><td colspan="9" class="text-center">${this.$t(
        "No records"
      )}</td></tr>`;

      const docTitle = this.$t("Scholarship Summary");

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${this.escapeHtml(
        docTitle
      )}</title>
<style>
  @page { size: A4 landscape; margin: 4mm; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; }
  .print-header { margin-bottom: 0; }
  .print-main-title {
    font-size: 18px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.25;
    margin: 0 0 10px;
  }
  .print-main-title .school-name { color: #000; }
  .print-main-title .title-dash { color: #000; font-weight: 700; }
  .print-main-title .campus-name { color: #4a4a4a; font-weight: 700; }
  .print-meta {
    font-size: 11px;
    color: #5c5c5c;
    font-weight: 500;
    line-height: 1.5;
    margin: 0 0 10px;
  }
  .print-meta .meta-sep {
    color: #9a9a9a;
    font-weight: 300;
    padding: 0 10px;
  }
  .print-meta strong { color: #444; font-weight: 600; }
  .print-header-rule {
    border: none;
    border-bottom: 3px solid #000;
    margin: 0 0 14px;
    padding: 0;
    height: 0;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 5px 6px; text-align: left; vertical-align: top; }
  th { background: #e8e8e8; font-weight: 600; }
  tr:nth-child(even) td:not([class*="pv-hl-"]) { background: #fafafa; }
  td[class*="pv-hl-"] {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-weight: 600;
  }
  td.pv-hl-success { background: #cfe9de !important; color: #0f5132 !important; }
  td.pv-hl-danger { background: #f8d7da !important; color: #842029 !important; }
  td.pv-hl-warning { background: #fff3cd !important; color: #664d03 !important; }
  td.pv-hl-secondary { background: #e9ecef !important; color: #495057 !important; }
  .text-center { text-align: center; }
</style></head><body>
  <header class="print-header">
    <div class="print-main-title">${mainTitleHtml}</div>
    <div class="print-meta">${metaHtml}</div>
    <hr class="print-header-rule" />
  </header>
  <table><thead>${thead}</thead><tbody>${rows || emptyRow}</tbody></table>
</body></html>`;

      const iframe = document.createElement("iframe");
      iframe.setAttribute(
        "style",
        "position:fixed;width:0;height:0;border:0;visibility:hidden;"
      );
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          setTimeout(() => iframe.remove(), 400);
        }
      }, 200);
    },
    async exportScholarshipReport() {
      const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");
      // console.log(this.scholarshipStudents);
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = `scholarship-summary-${stamp}.xlsx`;

      const kFederal = this.$t("Federal Board Reg No");
      const kStudent = this.$t("Student Name");
      const kFather = this.$t("Father Name");
      const kCampus = this.$t("Institute");
      const kClass = this.$t("Class");
      const kSchCat = this.$t("Scholarship Category");
      const kSch = this.$t("Scholarship Status");
      const kStat = this.$t("Student Status");
      const kWa = "WhatsApp No";
      const kParentContact = "Parent's Contact";
      const kStudentContact = "Student's Contact";
      const kStatus = "Current Status";

      const expData = (this.scholarshipStudents || []).map((item) => {
        const camp = item.campus || {};
        const st = item.student || {};
        const det = item.studentDetails || {};
        return {
          [kFederal]: det.federalRegNo ? String(det.federalRegNo) : "",
          [kStudent]: st.name || "",
          [kFather]: st.father_name || "",
          [kCampus]: camp.name || "",
          [kClass]: st.meta_class || "",
          [kSchCat]: det.scholarshipCategory || "",
          [kSch]: det.scholarshipStatus || "",
          [kStat]: det.studentStatus || "",
          [kWa]:
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "",
          [kParentContact]:
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "",
          [kStudentContact]:
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "",
          [kStatus]: st.status || "",
          "Accepted By": det.acceptedBy || "",
          "Verified By": det.verifiedBy || "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(expData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Scholarship");
      XLSX.writeFile(workbook, fname);
    },

    async LoadScholarshipStatistics() {
      this.scholarshipLoading = true;
      let res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/ScholarshipSummaryByBranch?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&category=" +
          (this.selectedCategory.join(",") || ""),
        token: this.$store.state.userData.token,
      });
      this.scholarshipLoading = false;
      // console.log(res);
      this.scholarshipItems = res?.branches ?? [];
      if (res?.totalCounts) this.scholarshipTotals = res?.totalCounts;
      this.assignedStrength = res?.classStrength ?? [];
      this.acceptedStrength = res?.classAcceptedStrength ?? [];
      this.notAcceptedStrength = res?.classNotAcceptedStrength ?? [];
    },
    async LoadOtherStats() {
      this.otherLoading = true;
      let res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/ScholarshipStats?db=" +
          this.$store.state.userData.db +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&category=" +
          (this.selectedCategory.join(",") || ""),
        token: this.$store.state.userData.token,
      });
      this.otherLoading = false;
      // console.log(res);
      if (res) {
        if (res.genderStats) this.genderStats = res.genderStats;
        this.classStats = res?.classStats ?? [];
        this.$nextTick(() => console.log(this.facultyStatsDistinctClasses));
      }
    },
    async LoadRegions() {
      let res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/GetRegions?db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
      });
      this.regionOptions = res;
    },
    async LoadUtpCampuses() {
      var obj = {
        url:
          this.$store.state.domain +
          "CampusDetails/LoadData?db=" +
          this.$store.state.userData.db +
          "&isUtp=true",
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      this.utpCampusIDs = Array.isArray(res) ? res.map((el) => el.id) : [];
      // console.log("utp campus", this.utpCampusIDs);
      this.utpCampusLoaded = false;
    },
    getFacultyStatsForClass(row, cls) {
      const arr = row && row.classes;
      if (!Array.isArray(arr)) return null;
      return arr.find((b) => b != null && b.className === cls) || null;
    },
    openScholarshipDetails(item) {
      this.selectedCampusId = item.campusID;
      this.modalTitle = item.campusName;
      this.$bvModal.show("modal-scholarship");
    },
    setDate(sd, dateStr) {
      // console.log(sd, dateStr);
      if (dateStr) {
        if (sd.length > 1) {
          let date = dateStr.split(" to ");
          if (date.length == 1) {
            this.dateObj.dtFrom = date[0];
            this.dateObj.dtTo = date[0];
          } else {
            this.dateObj.dtFrom = date[0];
            this.dateObj.dtTo = date[1];
          }
          this.LoadSummary();
        } else {
          this.dateObj.dtFrom = dateStr;
          this.dateObj.dtTo = null;
        }
      } else {
        this.dateObj.dtFrom = null;
        this.dateObj.dtTo = null;
      }
      // console.log(this.dateObj.dtFrom, this.dateObj.dtTo);
    },
    async LoadSummary() {
      if (this.dateObj.dtFrom && this.dateObj.dtTo) {
        this.stLoading = true;
        let ids =
          this.campIDs.length == 0 || this.campIDs[0] == 0 ? [] : this.campIDs;

        var obj = {
          url:
            this.$store.state.domain +
            "Users/DashboardInquiries?_db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dtFrom=" +
            this.dateObj.dtFrom +
            "&dtTo=" +
            this.dateObj.dtTo +
            "&campID=" +
            ids,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        let res = await this.get(obj);
        this.items = res.data;
        this.summaryObj = {
          amountTotal: res.amountTotal,
          depositAmountTotal: res.depositAmountTotal,
          regTotal: res.regTotal,
          regPendingTotal: res.regPendingTotal,
          inqTotal: res.inqTotal,
          inqCompTotal: res.inqCompTotal,
          admTotal: res.admTotal,
          admPendingTotal: res.admPendingTotal,
        };
        this.lastLoad = {
          from: this.dateObj.dtFrom,
          to: this.dateObj.dtTo,
        };
        this.slipData = res.depositData ? res.depositData : [];
        // console.log(this.items);
        this.stLoading = false;
      }
    },
    async LoadAttendance() {
      if (!this.attDate) return;
      this.attLoading = true;
      try {
        let ids =
          this.attCampIDs.length == 0 || this.attCampIDs[0] == 0
            ? []
            : this.attCampIDs;

        var obj = {
          url:
            this.$store.state.domain +
            "Users/DasboardAttendance?_db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dt=" +
            this.attDate +
            "&campID=" +
            ids,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        let res = await this.get(obj);
        this.attItems = Array.isArray(res && res.data) ? res.data : [];
        this.attSummary = {
          stdTotal: res.stdTotal,
          stdPTotal: res.stdPTotal,
          stdATotal: res.stdATotal,
          stdLvTotal: res.stdLvTotal,
          stdLtTotal: res.stdLtTotal,
          stfTotal: res.stfTotal,
          stfPTotal: res.stfPTotal,
          stfATotal: res.stfATotal,
          stfLvTotal: res.stfLvTotal,
          stfLtTotal: res.stfLtTotal,
          stdUnmarkedTotal: res.stdUnmarkedTotal,
          totalCampus: res.totalCampus,
          markedCampus: res.markedCampus,
        };
      } finally {
        this.attLoading = false;
      }
    },
    openAttendance(item) {
      this.selectedCampusId = item.campusID;
      this.modalTitle = item.campusName;
      this.$bvModal.show("modal-attendance");
    },
    async LoadHomework() {
      if (!this.hwDate) return;
      this.hwLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "homework/LoadCampusHomeworkSummary?db=" +
          this.$store.state.userData.db +
          "&dt=" +
          this.hwDate +
          "&type=homework";

        if (!this.isHeadOffice) {
          url += "&cID=" + this.$store.state.userData.cId;
        }

        var obj = {
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        let res = await this.get(obj);
        if (res && res.status == "success") {
          this.hwItems = res.data || [];
          this.hwSummary = {
            totalHomework: res.totalHomework,
            totalAssigned: res.totalAssigned,
            totalPublished: res.totalPublished,
            totalUnAssigned: res.totalUnAssigned,
            totalUnPublished: res.totalUnPublished,
            totalCampuses: res.totalCampuses,
            markedCampuses: res.markedCampuses,
          };
        } else {
          this.hwItems = [];
          this.hwSummary = {
            totalHomework: 0,
            totalAssigned: 0,
            totalPublished: 0,
            totalUnAssigned: 0,
            totalUnPublished: 0,
            totalCampuses: 0,
            markedCampuses: 0,
          };
        }
      } finally {
        this.hwLoading = false;
      }
    },
    openHomework(item) {
      this.selectedCampusId = item.campusID;
      this.modalTitle = item.campusName;
      this.$bvModal.show("modal-homework");
    },
    async LoadCampuses() {
      var obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      const response = await this.get(obj);
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      this.campuses = list
        .filter((c) => c && Number(c.id) > 0)
        .map((c) => ({
          ...c,
          branch: c.branch || c.name || `Campus ${c.id}`,
        }));
    },
    async LoadCollection() {
      if (this.isTcfDomain) return;
      var obj = {
        url:
          this.$store.state.domain +
          "Users/DashboardCollection?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&year=" +
          this.currentYear,
        token: this.$store.state.userData.token,
      };
      let collData;
      try {
        collData = await this.get(obj);
      } catch (e) {
        return;
      }
      // `get` resolves to [] on failure — keep the previous graph instead of
      // throwing on a missing payload.
      if (!collData || !collData.collectionGraph) return;

      this.weeklyData = {
        data: collData.collectionGraph,
        highestCollection: collData.highestCollection,
      };

      this.showCollection = true;
    },
    /** Refresh collection figures every 3 min while the tab stays visible. */
    startFinanceLivePolling() {
      this.stopFinanceLivePolling();
      this._financeLiveTimer = setInterval(() => {
        if (typeof document !== "undefined" && document.hidden) return;
        if (this.financeLiveLoading || !this.showCollectionInsights) return;
        this.refreshFinanceLive();
      }, 3 * 60 * 1000);
    },
    stopFinanceLivePolling() {
      if (this._financeLiveTimer) {
        clearInterval(this._financeLiveTimer);
        this._financeLiveTimer = null;
      }
    },
    async refreshFinanceLive() {
      if (this.financeLiveLoading) return;
      this.financeLiveLoading = true;
      try {
        const jobs = [];
        if (!this.isTcfDomain) {
          jobs.push(this.LoadCollection(), this.LoadDues());
        }
        if (this.sectionVisible("homework")) {
          jobs.push(this.LoadHomework());
        }
        await Promise.all(
          jobs.map((job) => Promise.resolve(job).catch(() => null))
        );
      } finally {
        this.financeLiveLoading = false;
      }
    },
    /** Lightweight re-fetch of Users/Dashboard (no settings sweep, no toasts). */
    async refreshCoreDashboard() {
      const u = this.$store.state.userData || {};
      if (!u.token || !u.db) return;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "Users/Dashboard?db=" +
            u.db +
            "&cID=" +
            u.cId +
            "&year=" +
            this.currentYear,
          token: u.token,
          dashboard: true,
        });
        if (res && res.data) {
          this.applyDashboardResponse(res, { loadSecondary: false });
          this.saveDashSnapshot();
        }
      } catch (e) {
        /* keep the last good snapshot */
      }
    },
    async loadAcademicsHubData() {
      if (!this.showAcademicsHub) return;
      const u = this.$store.state.userData || {};
      if (!u.token || !u.db) return;
      this.academicsHubLoading = true;
      const base = {
        get: this.get.bind(this),
        domain: this.$store.state.domain,
        token: u.token,
        db: u.db,
        campusID: u.cId,
      };
      try {
        const [strength, leaveRows, studentReqRows] = await Promise.all([
          loadClassStrengthSummary(base),
          loadPendingLeaveRequests({
            ...base,
            userID: u.userID || u.localID,
            reportDomain: this.reportDomain,
          }),
          loadPendingStudentRequests(base),
        ]);
        this.academicsData = {
          strengthRows: strength.rows || [],
          strengthTotals: strength.totals || {
            male: 0,
            female: 0,
            other: 0,
            total: 0,
          },
          leaveRows: leaveRows || [],
          studentReqRows: studentReqRows || [],
        };
        if (!this.subData.length && this.showAcademicsHub) {
          this.LoadSubEnrolled();
        }
      } catch (e) {
        console.warn("Academics hub:", e);
      } finally {
        this.academicsHubLoading = false;
      }
    },
    onAcademicsOpenMessage({ row, kind }) {
      if (!row) return;
      const isStaff = kind === "staff";
      const target = isStaff
        ? {
            name: row.name,
            tID: row.tID || row.localID,
            localID: row.localID || row.tID,
            phoneNumber: row.phoneNumber || row.contact,
          }
        : {
            name: row.name,
            sID: row.studentId || row.sID,
            localID: row.studentId || row.sID,
            grNo: row.grNo,
            className: row.className,
            contact: row.contact,
          };
      this.openDashboardMsgComposer({
        targets: [target],
        isStaff,
        bulk: false,
      });
    },
    async openDashboardMsgComposer({ targets, isStaff = false, bulk = false }) {
      const list = Array.isArray(targets) ? targets.filter(Boolean) : [];
      if (!list.length) {
        this.$bvToast.toast(this.$t("No recipients selected."), {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.appPendingMsg.targets = list;
      this.appPendingMsg.isStaff = !!isStaff;
      this.appPendingMsg.bulk = !!bulk;
      this.appPendingMsg.selectedTemplateId = null;
      this.appPendingMsg.media = "";
      this.appPendingMsg.message = "";
      this.appPendingMsg.visible = true;
      if (!this.appPendingMsg.templates.length) {
        await this.loadAppPendingMsgTemplates();
      }
    },
    async LoadDues() {
      if (this.isTcfDomain) return;
      var obj = {
        url:
          this.$store.state.domain +
          "Users/DashboardDues?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&year=" +
          this.currentYear,
        token: this.$store.state.userData.token,
      };
      let duesData;
      try {
        duesData = await this.get(obj);
      } catch (e) {
        return;
      }
      if (!duesData || !duesData.duesGraph) return;
      this.yearlyData = {
        data: duesData.duesGraph,
        highestDues: duesData.highestDues,
      };
      this.showDues = true;
    },
    houseHexColor(houseName) {
      const key = String(houseName || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

      const map = {
        // Elixir color houses
        blue: "#3B82F6",
        green: "#10B981",
        orange: "#F59E0B",
        red: "#EF4444",
        // SHS School named houses
        iqbal: "#059669",
        jinnah: "#2563EB",
        liaquat: "#DC2626",
        "sir syed": "#D97706",
        sirsyed: "#D97706",
        "sir-syed": "#D97706",
      };

      if (map[key]) return map[key];

      // Stable fallback so other house names still get distinct colors
      const palette = [
        "#7C3AED",
        "#0891B2",
        "#DB2777",
        "#65A30D",
        "#EA580C",
        "#4F46E5",
        "#0D9488",
        "#CA8A04",
      ];
      if (!key) return "#7367F0";
      let hash = 0;
      for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
      }
      return palette[hash % palette.length];
    },
    houseCountPercent(count) {
      const total = Number(this.colorsDemographics?.totalCount) || 0;
      if (!total) return 0;
      return Math.round((Number(count) / total) * 1000) / 10;
    },
    selectColorsHouse(houseName) {
      if (this.selectedColorsHouse === houseName) return;
      this.selectedColorsHouse = houseName;
      this.colorsChartBooting = true;
      clearTimeout(this._hcdChartBootTimer);
      this._hcdChartBootTimer = setTimeout(() => {
        this.colorsChartEpoch += 1;
        this.colorsChartBooting = false;
      }, 420);
    },
    async loadColorsDemographics() {
      this.colorsDemographicsLoading = true;
      try {
        const url =
          this.$store.state.domain +
          "Students/ColorsDemographics?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.$store.state.userData.cId;

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const payload =
          response && response.data && response.data.houses
            ? response.data
            : response && response.houses
            ? response
            : null;

        if (payload && Array.isArray(payload.houses)) {
          this.colorsDemographics = {
            campusID: payload.campusID,
            classID: payload.classID,
            totalCount: payload.totalCount || 0,
            houses: payload.houses,
          };
          if (
            !this.selectedColorsHouse ||
            !payload.houses.some((h) => h.house === this.selectedColorsHouse)
          ) {
            this.selectedColorsHouse = payload.houses[0]?.house || null;
          }
        } else {
          this.colorsDemographics = null;
        }
      } catch (error) {
        console.error("Error loading colors demographics:", error);
        this.colorsDemographics = null;
      } finally {
        this.colorsDemographicsLoading = false;
      }
    },
    async LoadAppDownloadStats() {
      this.downloadLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "Users/GetAppDownloadStatistics?db=" +
          this.$store.state.userData.db;

        const userData = getUserData();
        // If not Head Office, add campusID to filter stats
        if (!this.isHeadOffice) {
          url += "&campusID=" + this.$store.state.userData.cId;
        }

        if (this.sessionID) {
          url += "&sessionID=" + this.sessionID;
        }

        if (this.selectedAppBundleID) {
          url += "&bundleID=" + encodeURIComponent(this.selectedAppBundleID);
        }

        // Student class filter (multi): 0 = all classes
        const classIds = Array.isArray(this.appStatsClassIDs)
          ? this.appStatsClassIDs
          : [0];
        const hasSpecificClass = classIds.some((id) => Number(id) !== 0);
        const classIDs = hasSpecificClass
          ? classIds.filter((id) => Number(id) !== 0).join(",")
          : "0";
        url += "&classIDs=" + encodeURIComponent(classIDs);

        // Staff department filter (multi): 0 = all departments
        const deptIds = Array.isArray(this.appStatsDepartmentIDs)
          ? this.appStatsDepartmentIDs
          : [0];
        const hasSpecificDept = deptIds.some((id) => Number(id) !== 0);
        const departmentIDs = hasSpecificDept
          ? deptIds.filter((id) => Number(id) !== 0).join(",")
          : "0";
        url += "&departmentIDs=" + encodeURIComponent(departmentIDs);

        var obj = {
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        let response = await this.get(obj);
        console.log("Download Stats Response:", response);

        // Handle both direct response and wrapped response
        let res = response.data || response;

        // Store the summary statistics with Vue.set for reactivity
        const stats = {
          totalDownloads: res.totalDownloads || 0,
          activeStudents: res.activeStudents || 0,
          activeStaff: res.activeStaff || 0,
          totalActiveUsers: res.totalActiveUsers || 0,
          studentStrength: res.studentStrength || 0,
          staffStrength: res.staffStrength || 0,
          totalStrength: res.totalStrength || 0,
          activeStudentsList: res.activeStudentsList || [],
          inactiveStudentsList: res.inactiveStudentsList || [],
          activeStaffList: res.activeStaffList || [],
          inactiveStaffList: res.inactiveStaffList || [],
        };

        this.$set(this, "downloadStats", stats);

        // Store campus-wise data and SORT BY TOTAL DOWNLOADS (DESC) for correct ranking
        const campusData = res.campusWise || [];
        campusData.sort((a, b) => b.totalDownloads - a.totalDownloads);
        this.$set(this, "downloadItems", campusData);
  
        console.log("Download Stats Processed:", this.downloadStats);
        console.log("Campus Data Count:", this.downloadItems.length);
        console.log(
          "Top 3 Campuses:",
          this.downloadItems.slice(0, 3).map((c) => ({
            name: c.branch,
            downloads: c.totalDownloads,
          }))
        );
      } catch (error) {
        console.error("Error loading app download statistics:", error);
        this.downloadStats = null;
        this.downloadItems = [];
      } finally {
        this.downloadLoading = false;
      }
    },
    async loadAppStatsClassOptions() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        let arr = [];
        if (Array.isArray(res)) arr = res;
        else if (res && Array.isArray(res.data)) arr = res.data;
        this.appStatsClasses = arr
          .map((el) => el.cls || el)
          .filter((cls) => cls && cls.id != null);
      } catch (e) {
        console.error("Error loading classes for app stats:", e);
        this.appStatsClasses = [];
      }
    },
    async loadAppStatsDepartmentOptions() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "StaffDepartments?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        this.appStatsDepartments = Array.isArray(res)
          ? res
          : Array.isArray(res && res.data)
          ? res.data
          : [];
      } catch (e) {
        console.error("Error loading departments for app stats:", e);
        this.appStatsDepartments = [];
      }
    },
    normalizeAppStatsMultiFilter(val) {
      let selected = Array.isArray(val) ? val.slice() : [];
      if (!selected.length) return [0];
      if (selected.includes(0) && selected.length > 1) {
        const last = selected[selected.length - 1];
        if (last === 0) return [0];
        return selected.filter((id) => Number(id) !== 0);
      }
      if (selected.includes(0)) return [0];
      return selected;
    },
    onAppStatsClassFilterChange(val) {
      this.appStatsClassIDs = this.normalizeAppStatsMultiFilter(val);
      this.LoadAppDownloadStats();
    },
    onAppStatsDepartmentFilterChange(val) {
      this.appStatsDepartmentIDs = this.normalizeAppStatsMultiFilter(val);
      this.LoadAppDownloadStats();
    },
    async loadSessions() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data = response.data || response;
        this.sessions = Array.isArray(data) ? data : [];

        // Auto-fill current session
        const current = this.sessions.find(
          (s) => s.isCurrentSession || s.isCurrentSession === 1
        );
        if (current) {
          this.sessionID = current.id;
          this.LoadAppDownloadStats();
        }
        this.loadAppStatsClassOptions();
        this.loadAppStatsDepartmentOptions();
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },
    calculatePercentage(value, total) {
      if (total === 0) return 0;
      return ((value / total) * 100).toFixed(1);
    },
    getProgressVariant(percentage) {
      if (percentage >= 70) return "success";
      if (percentage >= 50) return "primary";
      if (percentage >= 30) return "warning";
      return "danger";
    },
    sortDownloadsByDirection(asc) {
      if (!this.downloadItems || this.downloadItems.length === 0) return;

      this.lastSortField = "totalDownloads";
      this.sortAscending = asc;

      this.downloadItems.sort((a, b) => {
        return asc
          ? a.totalDownloads - b.totalDownloads
          : b.totalDownloads - a.totalDownloads;
      });

      this.$toast.success(
        `Sorted by Downloads: ${
          asc ? "Lowest to Highest" : "Highest to Lowest"
        }`,
        {
          position: "top-right",
          duration: 1500,
        }
      );
    },
    sortCampusData(field) {
      if (!this.downloadItems || this.downloadItems.length === 0) return;

      // Toggle sort direction if same field
      if (this.lastSortField === field) {
        this.sortAscending = !this.sortAscending;
      } else {
        this.lastSortField = field;
        this.sortAscending = field === "branch" ? true : false; // Ascending for name, descending for numbers
      }

      this.downloadItems.sort((a, b) => {
        let valueA = a[field];
        let valueB = b[field];

        if (typeof valueA === "string") {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        }

        if (this.sortAscending) {
          return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
        } else {
          return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
        }
      });

      this.$toast.success(
        `Sorted by ${field.replace(/([A-Z])/g, " $1").trim()}`,
        {
          position: "top-right",
          duration: 1500,
        }
      );
    },
    openUserList(type = "student", status = "active") {
      if (!this.downloadStats) return;

      if (type === "student") {
        this.userListModal.activeTab = status === "active" ? 0 : 1;
      } else {
        this.userListModal.activeTab = status === "active" ? 2 : 3;
      }

      this.userListCurrentPage = 1;
      this.userListSearch = "";
      this.clearAppPendingSelection();
      this.resetAppPendingMsgComposer();
      this.appStatsOpeningKey = `${type}-${status}`;
      this.userListModal.loading = true;
      this.$bvModal.show("modal-user-list");
      this.$nextTick(() => {
        setTimeout(() => {
          this.userListModal.loading = false;
          this.appStatsOpeningKey = "";
        }, 450);
      });
    },
    hasAppDownloadContact(value) {
      const text = value == null ? "" : String(value).trim();
      return text !== "" && text !== "-" && text.toLowerCase() !== "null";
    },
    hasStudentAppInstalled(item) {
      if (!item || typeof item !== "object") return false;
      return ["deviceID_Student", "deviceID_Father", "deviceID_Mother"].some(
        (key) => this.hasAppDownloadContact(item[key])
      );
    },
    getStudentContacts(item) {
      if (!item || typeof item !== "object") return [];
      const pickPhone = (keys) => {
        for (const key of keys) {
          if (this.hasAppDownloadContact(item[key])) {
            return String(item[key]).trim();
          }
        }
        return "";
      };
      return [
        {
          key: "student",
          label: this.$t("Student"),
          phone: pickPhone([
            "phoneNumberStudent",
            "contact_student",
            "phoneNumber",
          ]),
          installed: this.hasAppDownloadContact(item.deviceID_Student),
        },
        {
          key: "father",
          label: this.$t("Father"),
          phone: pickPhone(["phoneNumberFather", "contact_father"]),
          installed: this.hasAppDownloadContact(item.deviceID_Father),
        },
        {
          key: "mother",
          label: this.$t("Mother"),
          phone: pickPhone(["phoneNumberMother", "contact_mother"]),
          installed: this.hasAppDownloadContact(item.deviceID_Mother),
        },
      ];
    },
    // Chat individual send uses localID → sIDs / tIDs on SendMessageSelected
    getAppDownloadRecipientId(item, isStaff = false) {
      if (!item || typeof item !== "object") return 0;
      if (isStaff) {
        return (
          Number(
            item.localID ??
              item.tID ??
              item.teacherID ??
              item.staffID ??
              item.id ??
              0
          ) || 0
        );
      }
      return (
        Number(item.localID ?? item.sID ?? item.studentID ?? item.id ?? 0) || 0
      );
    },
    getAppDownloadRecipientPhone(item, isStaff = false) {
      if (!item || typeof item !== "object") return "";
      if (!isStaff && item.__contactType) {
        const contact = this.getStudentContacts(item).find(
          (entry) => entry.key === item.__contactType
        );
        return contact ? contact.phone : "";
      }
      if (isStaff) {
        if (this.hasAppDownloadContact(item.phoneNumber)) {
          return String(item.phoneNumber).trim();
        }
        if (this.hasAppDownloadContact(item.contact_number)) {
          return String(item.contact_number).trim();
        }
        if (this.hasAppDownloadContact(item.contact)) {
          return String(item.contact).trim();
        }
        return "";
      }
      if (this.hasAppDownloadContact(item.phoneNumber)) {
        return String(item.phoneNumber).trim();
      }
      if (this.hasAppDownloadContact(item.contact_father)) {
        return String(item.contact_father).trim();
      }
      if (this.hasAppDownloadContact(item.contact_mother)) {
        return String(item.contact_mother).trim();
      }
      if (this.hasAppDownloadContact(item.contact_student)) {
        return String(item.contact_student).trim();
      }
      if (this.hasAppDownloadContact(item.contact)) {
        return String(item.contact).trim();
      }
      return "";
    },
    canMessageAppPendingItem(item) {
      const isStaff = this.userListModal.activeTab >= 2;
      if (isStaff || this.hasStudentAppInstalled(item)) {
        return this.getAppDownloadRecipientId(item, isStaff) > 0;
      }
      return !!this.getAppDownloadRecipientPhone(item, false);
    },
    appPendingRowKey(item, index) {
      const isStaff = this.userListModal.activeTab >= 2;
      const id = this.getAppDownloadRecipientId(item, isStaff);
      if (id > 0) return `${isStaff ? "t" : "s"}-${id}`;
      const phone = this.getAppDownloadRecipientPhone(item, isStaff);
      return `r-${index}-${item.grNo || ""}-${item.name || ""}-${phone}`;
    },
    isAppPendingSelected(item, index) {
      return !!this.appPendingSelection[this.appPendingRowKey(item, index)];
    },
    setAppPendingSelected(item, index, checked) {
      const key = this.appPendingRowKey(item, index);
      if (checked) {
        this.$set(this.appPendingSelection, key, item);
      } else {
        this.$delete(this.appPendingSelection, key);
      }
    },
    clearAppPendingSelection() {
      this.appPendingSelection = {};
    },
    toggleSelectAllAppPending(checked) {
      if (!checked) {
        this.clearAppPendingSelection();
        return;
      }
      const next = {};
      (this.filteredUserListItems || []).forEach((item, index) => {
        next[this.appPendingRowKey(item, index)] = item;
      });
      this.appPendingSelection = next;
    },
    appPendingRecipientLabel(item) {
      if (!item) return "";
      const isStaff = !!this.appPendingMsg.isStaff;
      const parts = [
        item.grNo ? `GR ${item.grNo}` : "",
        item.className
          ? [item.className, item.sectionName].filter(Boolean).join(" - ")
          : item.designation || "",
        item.__contactType
          ? String(item.__contactType).charAt(0).toUpperCase() +
            String(item.__contactType).slice(1)
          : "",
        this.getAppDownloadRecipientPhone(item, isStaff),
      ];
      return parts.filter(Boolean).join(" · ");
    },
    resetAppPendingMsgComposer() {
      this.appPendingMsg.selectedTemplateId = null;
      this.appPendingMsg.media = "";
      this.appPendingMsg.message = "";
      this.appPendingMsg.sending = false;
      this.appPendingMsg.visible = false;
      this.appPendingMsg.targets = [];
      this.appPendingMsg.isStaff = false;
      this.appPendingMsg.bulk = false;
    },
    async loadAppPendingMsgTemplates() {
      this.appPendingMsg.templatesLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "MessageTemplate?db=" +
            encodeURIComponent(this.$store.state.userData.db) +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];
        const withLabel = list
          .filter((el) => el != null && el.id != null && el.id !== "")
          .map((el) => ({
            ...el,
            title:
              (el.title && String(el.title).trim()) ||
              (el.name && String(el.name).trim()) ||
              `Template ${el.id}`,
          }));
        // Prefer manual templates (empty sendOn), same as Chat individual flow
        const manualOnly = withLabel.filter((el) => {
          const so = el.sendOn;
          if (so == null || so === undefined) return true;
          return String(so).trim() === "" || String(so).trim() === "Defaulters";
        });
        this.appPendingMsg.templates = manualOnly.length
          ? manualOnly
          : withLabel;
      } catch (e) {
        console.error("App pending MessageTemplate load:", e);
        this.appPendingMsg.templates = [];
      } finally {
        this.appPendingMsg.templatesLoading = false;
      }
    },
    onAppPendingTemplateSelected(templateId) {
      if (templateId == null || templateId === "") {
        this.appPendingMsg.message = "";
        this.appPendingMsg.media = "";
        return;
      }
      const t = this.appPendingMsg.templates.find(
        (el) => Number(el.id) === Number(templateId)
      );
      if (t) {
        this.appPendingMsg.message = t.message != null ? String(t.message) : "";
        this.appPendingMsg.media = t.media || "";
      }
    },
    async launchAppPendingMsgComposer({ targets, bulk }) {
      const list = Array.isArray(targets) ? targets.filter(Boolean) : [];
      if (!list.length) {
        this.$bvToast.toast(this.$t("No recipients selected."), {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.appPendingMsg.targets = list;
      this.appPendingMsg.isStaff = this.userListModal.activeTab >= 2;
      this.appPendingMsg.bulk = !!bulk;
      this.appPendingMsg.selectedTemplateId = null;
      this.appPendingMsg.media = "";
      this.appPendingMsg.message = "";

      // Close list modal first, then open Chat-style template composer
      this.$bvModal.hide("modal-user-list");
      await this.$nextTick();
      setTimeout(async () => {
        this.appPendingMsg.visible = true;
        if (!this.appPendingMsg.templates.length) {
          await this.loadAppPendingMsgTemplates();
        }
      }, 180);
    },
    async openAppPendingMsgForItem(item) {
      if (!this.isPendingUserListTab) return;
      if (!this.canMessageAppPendingItem(item)) {
        this.$bvToast.toast(
          this.$t("No phone number or linked ID for messaging."),
          {
            title: this.$t("Error"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      await this.launchAppPendingMsgComposer({ targets: [item], bulk: false });
    },
    async openAppPendingMsgForContact(item, contactType) {
      const contact = this.getStudentContacts(item).find(
        (entry) => entry.key === contactType
      );
      if (!contact || !contact.phone) {
        this.$bvToast.toast(this.$t("No phone number available for this contact."), {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }
      const target = {
        ...item,
        __contactType: contactType,
      };
      await this.launchAppPendingMsgComposer({ targets: [target], bulk: false });
    },
    async openAppPendingMsgForSelected() {
      if (!this.isPendingUserListTab) return;
      const items = this.appPendingSelectedItems;
      if (!items.length) {
        this.$bvToast.toast(this.$t("Select at least one recipient."), {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }
      const messageable = items.filter((item) =>
        this.canMessageAppPendingItem(item)
      );
      if (!messageable.length) {
        this.$bvToast.toast(
          this.$t("Selected records have no phone or linked ID."),
          {
            title: this.$t("Error"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      await this.launchAppPendingMsgComposer({
        targets: messageable,
        bulk: true,
      });
    },
    onAppPendingMsgSidebarHidden() {
      if (this.appPendingMsg.sending) return;
      this.appPendingMsg.selectedTemplateId = null;
      this.appPendingMsg.media = "";
      this.appPendingMsg.message = "";
      this.appPendingMsg.targets = [];
      this.appPendingMsg.bulk = false;
    },
    appPendingTemplateDropdownPosition(
      dropdownList,
      component,
      { width, top, left }
    ) {
      dropdownList.classList.add("app-pending-template-dropdown");
      dropdownList.style.top = top;
      dropdownList.style.left = left;
      dropdownList.style.width = width;
      dropdownList.style.zIndex = "11100";
    },
    mergeChatSendResponses(acc, res, fallbackStats = {}) {
      const next = normalizeChatSendApiResponse(res);
      if (!next || !isChatSendOk(next)) return acc;
      const stats = extractChatSendStats(next);
      const base = acc || {
        status: "success",
        backgroundProcessing: false,
      };
      const merged = {
        ...base,
        status: "success",
        backgroundProcessing:
          !!base.backgroundProcessing || !!next.backgroundProcessing,
      };
      const count = Number.isFinite(stats.count)
        ? stats.count
        : fallbackStats.count;
      const studentCount = Number.isFinite(stats.studentCount)
        ? stats.studentCount
        : fallbackStats.studentCount;
      if (Number.isFinite(count)) {
        merged.count = (Number(base.count) || 0) + count;
      }
      if (Number.isFinite(studentCount)) {
        merged.studentCount = (Number(base.studentCount) || 0) + studentCount;
      }
      return merged;
    },

    showAppPendingChatSendFeedback(res, fallbackMsg) {
      const processing = !!(res && res.backgroundProcessing);
      if (!isChatSendOk(res)) {
        this.$bvToast.toast(this.$t("Request failed. Please try again."), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        return false;
      }
      const text = formatChatBulkSendMessage(res, fallbackMsg, (s) =>
        this.$t(s)
      );
      this.$bvToast.toast(text, {
        title: processing ? this.$t("Processing") : this.$t("Success"),
        variant: processing ? "info" : "success",
        solid: true,
        autoHideDelay: 6000,
      });
      return true;
    },

    async sendAppPendingMessages(mode = "message") {
      const text = String(this.appPendingMsg.message || "").trim();
      if (!text) {
        this.$bvToast.toast(this.$t("Please enter message"), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      const meta = this.appPendingRecipientMeta;
      const canSend =
        mode === "notification"
          ? meta.ids.length > 0
          : meta.ids.length > 0 || meta.phones.length > 0;

      if (!canSend) {
        this.$bvToast.toast(
          this.$t("No valid recipients found for messaging."),
          {
            title: this.$t("Error"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      this.appPendingMsg.sending = true;
      try {
        const media = this.appPendingMsg.media || "";
        const campusID = this.$store.state.userData.cId;
        const userID = this.$store.state.userData.userID;
        const db = this.$store.state.userData.db;
        const postOpts = {
          context: this,
          token: this.$store.state.userData.token,
          returnFullResponse: true,
          ignoreTimeout: true,
        };
        let merged = null;
        const fallback =
          mode === "notification"
            ? this.$t("Notifications are being processed in the background.")
            : this.$t("Messages are being processed in the background.");

        if (mode === "notification") {
          postOpts.backgroundProcessingMessage = fallback;
          const res = await this.postObj({
            url:
              this.$store.state.domain +
              "Chat/SendNotificationSelected?db=" +
              db,
            body: {
              audio: "",
              media,
              message: text,
              sIDs: meta.isStaff ? [] : meta.ids,
              tIDs: meta.isStaff ? meta.ids : [],
              campusID,
              userID,
            },
            ...postOpts,
          });
          merged = this.mergeChatSendResponses(merged, res, {
            count: meta.ids.length,
            studentCount: meta.isStaff ? undefined : meta.ids.length,
          });
        } else {
          if (meta.ids.length) {
            postOpts.backgroundProcessingMessage = fallback;
            const res = await this.postObj({
              url:
                this.$store.state.domain + "Chat/SendMessageSelected?db=" + db,
              body: {
                audio: "",
                media,
                message: text,
                sIDs: meta.isStaff ? [] : meta.ids,
                tIDs: meta.isStaff ? meta.ids : [],
                campusID,
                userID,
              },
              ...postOpts,
            });
            merged = this.mergeChatSendResponses(merged, res, {
              count: meta.ids.length,
              studentCount: meta.isStaff ? undefined : meta.ids.length,
            });
          }
          if (meta.phones.length) {
            const waFallback = this.$t(
              "WhatsApp messages are being processed in the background."
            );
            const res = await this.postObj({
              url:
                this.$store.state.domain +
                "Chat/SendWhatsApp?db=" +
                db +
                "&cID=" +
                campusID,
              body: {
                media,
                message: text,
                contacts: meta.phones.join(", "),
                whatsapp: true,
                userID,
              },
              ...postOpts,
              backgroundProcessingMessage: waFallback,
            });
            merged = this.mergeChatSendResponses(merged, res, {
              count: meta.phones.length,
            });
          }
        }

        if (!this.showAppPendingChatSendFeedback(merged, fallback)) {
          return;
        }

        this.appPendingMsg.visible = false;
        this.appPendingMsg.selectedTemplateId = null;
        this.appPendingMsg.media = "";
        this.appPendingMsg.message = "";
        this.appPendingMsg.targets = [];
        this.appPendingMsg.bulk = false;
        this.clearAppPendingSelection();
      } catch (e) {
        console.error("App pending send error:", e);
      } finally {
        this.appPendingMsg.sending = false;
      }
    },
    onUserListFiltered(filteredItems) {
      // Handle count update if needed elsewhere
      this.userListCurrentPage = 1;
    },
    getRankClass(rank) {
      if (rank === 1) return "rank-gold";
      if (rank === 2) return "rank-silver";
      if (rank === 3) return "rank-bronze";
      return "rank-default";
    },
    getDownloadClass(downloads) {
      if (downloads > 200) return "download-excellent";
      if (downloads > 100) return "download-good";
      if (downloads > 50) return "download-fair";
      return "download-low";
    },
    getPerformanceClass(downloads) {
      if (downloads > 200) return "performance-excellent";
      if (downloads > 100) return "performance-good";
      return "performance-low";
    },
    getPerformanceIcon(downloads) {
      if (downloads > 200) return "CheckCircleIcon";
      if (downloads > 100) return "AlertCircleIcon";
      return "AlertTriangleIcon";
    },
    getPerformanceText(downloads) {
      if (downloads > 200) return "Excellent";
      if (downloads > 100) return "Good";
      return "Needs Attention";
    },
    getPercentageClass(percentage) {
      const pct = parseFloat(percentage);
      if (pct >= 70) return "percentage-high";
      if (pct >= 50) return "percentage-medium";
      if (pct >= 30) return "percentage-low";
      return "percentage-critical";
    },
    getSizeClass(strength) {
      if (strength > 500) return "size-large";
      if (strength > 300) return "size-medium";
      return "size-small";
    },
    getSizeLabel(strength) {
      if (strength > 500) return "Large Campus";
      if (strength > 300) return "Medium Campus";
      return "Small Campus";
    },
    getDownloadBadgeVariant(downloads) {
      if (downloads > 200) return "success";
      if (downloads > 100) return "primary";
      if (downloads > 50) return "info";
      return "secondary";
    },
    getPercentageBadgeVariant(percentage) {
      const pct = parseFloat(percentage);
      if (pct >= 70) return "success";
      if (pct >= 50) return "primary";
      if (pct >= 30) return "warning";
      return "danger";
    },
    getStrengthVariant(strength) {
      if (strength > 500) return "success";
      if (strength > 300) return "primary";
      return "warning";
    },

    setData() {
      this.weeklyData = {
        data: this.dash.collectionGraph,
        highestCollection: this.dash.highestCollection,
      };
      this.yearlyData = {
        data: this.dash.duesGraph,
        highestDues: this.dash.highestDues,
      };
      this.monthlyData = this.dash.collectionChart;
      this.monthlySeries = [
        this.dash.collectionChart.collection,
        this.dash.collectionChart.dues,
      ];
      // console.log(this.monthlySeries);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/dashboard-ecommerce.scss";
@import "@core/scss/vue/libs/chart-apex.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";

.max-height-300 {
  max-height: 300px;
  object-fit: contain;
}

#dashboard-ecommerce .dashboard-main {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0.35rem 0.5rem 2.5rem;
  overflow-x: clip;
}

@media (max-width: 767px) {
  #dashboard-ecommerce .dashboard-main {
    padding-left: 0.15rem;
    padding-right: 0.15rem;
  }
}

/* Apex tooltips — global chart SCSS can hide dark-theme text on white bg */
#dashboard-ecommerce .apexcharts-tooltip {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12) !important;
  color: #334155 !important;

  .apexcharts-tooltip-title {
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
    color: #1e293b !important;
    font-weight: 700 !important;
    margin-bottom: 0 !important;
  }

  .apexcharts-tooltip-text-label,
  .apexcharts-tooltip-text-value,
  .apexcharts-tooltip-y-group,
  .apexcharts-tooltip-z-group {
    color: #334155 !important;
  }

  &.apexcharts-theme-dark {
    background: #1e293b !important;
    border-color: #334155 !important;

    .apexcharts-tooltip-title {
      background: #0f172a !important;
      color: #f1f5f9 !important;
      border-color: #334155 !important;
    }

    .apexcharts-tooltip-text-label,
    .apexcharts-tooltip-text-value {
      color: #e2e8f0 !important;
    }
  }
}

#dashboard-ecommerce .dash-apex-tip {
  padding: 8px 12px;
  min-width: 140px;
}

#dashboard-ecommerce .dash-apex-tip__title {
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
  line-height: 1.3;
  max-width: 220px;
}

#dashboard-ecommerce .dash-apex-tip__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

#dashboard-ecommerce .dash-apex-tip__label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

#dashboard-ecommerce .dash-apex-tip__value {
  font-size: 12px;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

/* Calendar + house demographics — matched height row */
.dash-calendar-demo-row {
  align-items: stretch;

  > [class*="col"] {
    min-height: 600px;
  }

  .dash-calendar-col {
    min-width: 0;
    flex: 1 1 0;
    overflow: hidden;
    isolation: isolate;
    z-index: 1;
  }
}

/* Dashboard — head-office homework panel */
.ho-hw-panel__icon {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  color: #ea580c;
}

.ho-hw-panel__header {
  padding-bottom: 0.85rem !important;
}

.ho-hw-panel__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
}

.ho-hw-panel__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.ho-hw-date {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.4rem 0.85rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  color: #64748b;
  cursor: pointer;
}

.ho-hw-date__input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-width: 6.5rem;
  font-weight: 700;
  font-size: 0.85rem;
  color: #1e293b !important;
  cursor: pointer;
}

.ho-hw-show {
  display: inline-flex;
  align-items: center;
  height: 38px;
  border-radius: 12px !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding-inline: 1rem !important;
  white-space: nowrap;
}

.ho-hw-progress-wrap {
  padding: 0.85rem 1.15rem 1rem;
  border-bottom: 1px solid #eef2f7;
  background: #fff;
}

.ho-hw-progress__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.55rem;
}

.ho-hw-progress__title {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.ho-hw-progress__value {
  font-size: 0.82rem;
  font-weight: 600;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;

  strong {
    color: #0f172a;
    font-weight: 800;
  }
}

.ho-hw-progress {
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}

.ho-hw-progress__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #fdba74 0%, #fb923c 55%, #ea580c 100%);
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Stat tiles */
.ho-hw-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.6rem;
  padding: 0.9rem 1.15rem 1rem;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

@media (max-width: 1199px) {
  .ho-hw-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .ho-hw-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.ho-hw-stat {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.75rem 0.85rem;
  border-radius: 14px;
  border: 1px solid #eef2f7;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  min-width: 0;
  position: relative;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: #e2e8f0;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.07);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    border-radius: 3px;
  }

  &__top {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 7px;
    flex-shrink: 0;
  }

  &__label {
    font-size: 0.62rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.03em;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.1rem;
  }

  &__meter {
    position: relative;
    flex: 1;
    height: 4px;
    border-radius: 999px;
    background: #eef2f7;
    overflow: hidden;
    min-width: 0;
  }

  &__meter-fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    transition: width 0.45s cubic-bezier(0.22, 1, 0.36, 1);

    &--ok {
      background: linear-gradient(90deg, #34d399, #059669);
    }
    &--pub {
      background: linear-gradient(90deg, #7dd3fc, #0284c7);
    }
    &--pending {
      background: linear-gradient(90deg, #fdba74, #ea580c);
    }
    &--muted {
      background: linear-gradient(90deg, #cbd5e1, #94a3b8);
    }
  }

  &__pct {
    font-size: 0.62rem;
    font-weight: 800;
    color: #64748b;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__hint {
    font-size: 0.62rem;
    font-weight: 600;
    color: #b6c0cf;
    white-space: nowrap;
  }

  &--total::before {
    background: linear-gradient(180deg, #a5b4fc, #4f46e5);
  }
  &--ok::before {
    background: linear-gradient(180deg, #34d399, #059669);
  }
  &--published::before {
    background: linear-gradient(180deg, #38bdf8, #0284c7);
  }
  &--pending::before {
    background: linear-gradient(180deg, #fdba74, #ea580c);
  }
  &--muted::before {
    background: linear-gradient(180deg, #cbd5e1, #94a3b8);
  }

  &--total .ho-hw-stat__icon {
    background: #eef2ff;
    color: #4f46e5;
  }
  &--ok .ho-hw-stat__icon {
    background: #ecfdf5;
    color: #059669;
  }
  &--published .ho-hw-stat__icon {
    background: #e0f2fe;
    color: #0284c7;
  }
  &--pending .ho-hw-stat__icon {
    background: #fff7ed;
    color: #ea580c;
  }
  &--muted .ho-hw-stat__icon {
    background: #f1f5f9;
    color: #64748b;
  }
}

/* Search */
.ho-hw-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.85rem 1.15rem;
  padding: 0.45rem 0.7rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #c7d2fe;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
  }

  &__icon {
    display: inline-flex;
    color: #94a3b8;
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 0.84rem;
    font-weight: 600;
    color: #1e293b;

    &::placeholder {
      color: #a8b3c2;
      font-weight: 500;
    }
  }

  &__count {
    font-size: 0.72rem;
    font-weight: 700;
    color: #64748b;
    background: #f1f5f9;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    white-space: nowrap;
  }
}

/* Table */
.ho-hw-table-wrap {
  border-top: 1px solid #eef2f7;
}

.ho-hw-table {
  font-size: 0.84rem;
  margin-bottom: 0;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
    border-top: none !important;
    padding: 0.6rem 0.85rem !important;
    vertical-align: middle;
    box-shadow: 0 1px 0 #e2e8f0;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
  }

  tbody td {
    padding: 0.6rem 0.85rem !important;
    vertical-align: middle;
    border-color: #f1f5f9 !important;
  }

  tbody tr:nth-child(even) {
    background: #fafbfc;
  }

  tbody tr:hover {
    background: #fff7ed !important;
  }

  tbody tr td:first-child {
    box-shadow: inset 3px 0 0 transparent;
    transition: box-shadow 0.15s ease;
  }

  tbody tr:hover td:first-child {
    box-shadow: inset 3px 0 0 #fb923c;
  }
}

.ho-hw-th-campus,
.ho-hw-td-campus {
  min-width: 200px;
}

.ho-hw-th-actions,
.ho-hw-td-actions {
  width: 72px;
}

.ho-hw-campus {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: linear-gradient(135deg, #fdba74, #ea580c);
    flex-shrink: 0;
  }

  &__name {
    font-weight: 700;
    font-size: 0.82rem;
    color: #1e293b;
    word-break: break-word;
  }
}

/* Ratio + meter cell */
.ho-hw-metric {
  min-width: 8.5rem;
  max-width: 12rem;
  margin: 0 auto;
}

.ho-hw-metric__head {
  display: flex;
  align-items: baseline;
  gap: 0.28rem;
  margin-bottom: 0.3rem;
}

.ho-hw-metric__value {
  font-size: 0.9rem;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.ho-hw-metric__of {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

.ho-hw-metric__pct {
  margin-left: auto;
  font-size: 0.66rem;
  font-weight: 800;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-variant-numeric: tabular-nums;

  &--pub {
    background: #e0f2fe;
    color: #0369a1;
  }
}

.ho-hw-meter {
  display: block;
  height: 5px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}

.ho-hw-meter__fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.45s cubic-bezier(0.22, 1, 0.36, 1);

  &--assign {
    background: linear-gradient(90deg, #fdba74, #f59e0b);
  }

  &--publish {
    background: linear-gradient(90deg, #7dd3fc, #0284c7);
  }
}

.ho-hw-eye {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #ea580c;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #fff7ed;
    border-color: #fed7aa;
    color: #c2410c;
    transform: translateY(-1px);
  }
}

/* Table skeleton */
.ho-hw-skeleton {
  padding: 0.35rem 0.85rem 0.6rem;
}

.ho-hw-skeleton__row {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 0.9rem;
  padding: 0.7rem 0;
  border-bottom: 1px solid #f5f7fa;
}

.ho-hw-skeleton__campus,
.ho-hw-skeleton__bar {
  display: block;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 40%, #eef2f7 80%);
  background-size: 200% 100%;
  animation: dashHoHwSk 1.25s ease-in-out infinite;
}

.ho-hw-skeleton__campus {
  width: 58%;
  height: 0.95rem;
}

.ho-hw-skeleton__bar {
  width: 80%;
  height: 0.8rem;
  justify-self: center;
}

@keyframes dashHoHwSk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.ho-hw-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
}

/* Dashboard — head-office campus attendance panel */
.ho-att-panel__icon {
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  color: #4f46e5;
}

.ho-att-panel__header {
  padding-bottom: 0.85rem !important;
}

.ho-att-panel__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
}

.ho-att-panel__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.ho-att-date {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.4rem 0.85rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  color: #64748b;
  cursor: pointer;
}

.ho-att-date__input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-width: 6.5rem;
  font-weight: 700;
  font-size: 0.85rem;
  color: #1e293b !important;
  cursor: pointer;
}

.ho-att-campuses {
  min-width: 15rem;
  max-width: 22rem;
  flex: 1 1 15rem;
}

.ho-att-show {
  display: inline-flex;
  align-items: center;
  height: 38px;
  border-radius: 12px !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding-inline: 1rem !important;
  white-space: nowrap;
}

/* Overall campus completion */
.ho-att-progress-wrap {
  padding: 0.85rem 1.15rem 1rem;
  border-bottom: 1px solid #eef2f7;
  background: #fff;
}

.ho-att-progress__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.55rem;
}

.ho-att-progress__title {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.ho-att-progress__value {
  font-size: 0.82rem;
  font-weight: 600;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;

  strong {
    color: #0f172a;
    font-weight: 800;
  }
}

.ho-att-progress {
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}

.ho-att-progress__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #818cf8 0%, #6366f1 55%, #4f46e5 100%);
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Student / staff summary cards */
.ho-att-summaries {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
  padding: 0.9rem 1.15rem 1rem;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

@media (max-width: 767px) {
  .ho-att-summaries {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ho-att-summary {
  border: 1px solid #eef2f7;
  border-radius: 14px;
  background: #fff;
  padding: 0.7rem 0.8rem 0.75rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  min-width: 0;
}

.ho-att-summary__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.55rem;
}

.ho-att-summary__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 9px;
  flex-shrink: 0;

  &--std {
    background: #eef2ff;
    color: #4f46e5;
  }

  &--staff {
    background: #fff7ed;
    color: #ea580c;
  }
}

.ho-att-summary__title {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.ho-att-summary__total {
  margin-left: auto;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.ho-att-summary__metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.ho-att-metric {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  padding: 0.2rem 0.5rem 0.2rem 0.28rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;

  i {
    font-style: normal;
    font-size: 0.6rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.1rem 0.32rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.75);
  }

  &--ok {
    background: #dcfce7;
    color: #15803d;
  }
  &--bad {
    background: #fee2e2;
    color: #b91c1c;
  }
  &--info {
    background: #cffafe;
    color: #0e7490;
  }
  &--warn {
    background: #fef3c7;
    color: #b45309;
  }
  &--muted {
    background: #f1f5f9;
    color: #64748b;
  }
}

/* Campus table */
.ho-att-table-wrap {
  border-top: none;
}

.ho-att-table {
  font-size: 0.84rem;
  margin-bottom: 0;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
    border-top: none !important;
    padding: 0.6rem 0.85rem !important;
    vertical-align: middle;
    box-shadow: 0 1px 0 #e2e8f0;
  }

  tbody td {
    padding: 0.6rem 0.85rem !important;
    vertical-align: middle;
    border-color: #f1f5f9 !important;
  }

  tbody tr:nth-child(even) {
    background: #fafbfc;
  }

  tbody tr:hover {
    background: #f5f3ff !important;
  }

  tbody tr td:first-child {
    box-shadow: inset 3px 0 0 transparent;
    transition: box-shadow 0.15s ease;
  }

  tbody tr:hover td:first-child {
    box-shadow: inset 3px 0 0 #6366f1;
  }
}

.ho-att-th-group {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  span {
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
  }

  small {
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #b6c0cf;
  }
}

.ho-att-campus {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: linear-gradient(135deg, #818cf8, #4f46e5);
    flex-shrink: 0;
  }

  &__name {
    font-weight: 700;
    font-size: 0.82rem;
    color: #1e293b;
    word-break: break-word;
  }
}

.ho-att-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.ho-att-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.9rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;

  &--neutral {
    background: #f1f5f9;
    color: #475569;
  }
  &--ok {
    background: #dcfce7;
    color: #15803d;
  }
  &--bad {
    background: #fee2e2;
    color: #b91c1c;
  }
  &--info {
    background: #cffafe;
    color: #0e7490;
  }
  &--warn {
    background: #fef3c7;
    color: #b45309;
  }
  &--muted {
    background: #f8fafc;
    color: #94a3b8;
  }
}

.ho-att-eye {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #6366f1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #eef2ff;
    border-color: #c7d2fe;
    color: #4338ca;
    transform: translateY(-1px);
  }
}

.ho-att-busy {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
}

/* Table skeleton while the campus rows load */
.ho-att-skeleton {
  padding: 0.35rem 0.85rem 0.6rem;
}

.ho-att-skeleton__row {
  display: grid;
  grid-template-columns: minmax(0, 2.4fr) repeat(4, minmax(0, 1fr));
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f5f7fa;
}

.ho-att-skeleton__campus,
.ho-att-skeleton__num {
  display: block;
  height: 0.8rem;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 40%, #eef2f7 80%);
  background-size: 200% 100%;
  animation: dashHoAttSk 1.25s ease-in-out infinite;
}

.ho-att-skeleton__campus {
  width: 62%;
  height: 0.95rem;
}

.ho-att-skeleton__num {
  width: 70%;
  justify-self: center;
}

@keyframes dashHoAttSk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.ho-att-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
}

/* Dashboard — event calendar (replaces the activity stat cards).
   The calendar styles ship in a separate async chunk, so these selectors are
   intentionally more specific than the calendar's own rules. */
.dash-events-calendar.app-calendar {
  margin-bottom: 1rem;
  border-radius: 22px !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85) inset,
    0 14px 40px rgba(115, 103, 240, 0.08);

  &.dash-events-calendar--compact:not(.dash-events-calendar--mini) {
    margin-bottom: 0;
    border-radius: 18px !important;

    .full-calendar {
      padding: 0.65rem 0.85rem 0.85rem !important;
    }

    .full-calendar .fc-header-toolbar {
      margin-bottom: 0.65rem !important;
    }

    .full-calendar .fc .fc-view-harness {
      min-height: 0 !important;
      margin: 0;
    }

    .full-calendar .fc .fc-daygrid-day {
      min-height: 46px !important;
    }
  }

  &.dash-events-calendar--compact.dash-events-calendar--mini {
    margin-bottom: 0;
    border-radius: 22px !important;
    min-height: 520px;
    background: rgba(255, 255, 255, 0.62) !important;
    border: 1px solid rgba(115, 103, 240, 0.12) !important;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.85) inset,
      0 14px 40px rgba(115, 103, 240, 0.08) !important;
  }

  &.dash-events-calendar--mini {
    margin-bottom: 0;

    .full-calendar {
      padding: 0 !important;
    }

    .full-calendar .fc-view-harness {
      margin: 0 !important;
      min-height: 0 !important;
    }

    .full-calendar .fc-theme-standard td,
    .full-calendar .fc-theme-standard th,
    .full-calendar .fc-scrollgrid {
      border: none !important;
    }

    .full-calendar .fc .fc-daygrid-day-frame {
      min-height: 54px !important;
    }

    .full-calendar .fc .event-content {
      min-height: 0 !important;
      padding: 0 !important;
    }
  }

  &:not(.dash-events-calendar--mini) {
  .full-calendar {
    padding: 1.25rem !important;
  }

  .full-calendar .fc-header-toolbar {
    margin-bottom: 1.25rem !important;
  }

  .full-calendar .fc .fc-view-harness {
    min-height: 520px;
    margin: 0;
  }

  .full-calendar .fc .fc-daygrid-day {
    min-height: 84px !important;
  }

  .full-calendar .fc .event-content {
    padding: 6px 8px;
    min-height: 42px;
  }
  }

  @media (max-width: 767.98px) {
    &:not(.dash-events-calendar--mini) {
    .full-calendar {
      padding: 0.75rem !important;
    }

    .full-calendar .fc-header-toolbar .fc-toolbar-title {
      font-size: 1.15rem !important;
      margin-left: 0.25rem;
    }

    .full-calendar .fc .fc-view-harness {
      min-height: 420px;
    }

    .full-calendar .fc .fc-daygrid-day {
      min-height: 64px !important;
    }

    .full-calendar .fc .event-content {
      padding: 4px 6px;
      min-height: 40px;

      .event-type-badge {
        display: none;
      }
    }
    }
  }
}

/* Dashboard — welcome-adjacent panels (dues, charts) */
.dash-dues-panel--modern {
  border-radius: 22px !important;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.62) !important;
  backdrop-filter: blur(16px) saturate(1.15);
  -webkit-backdrop-filter: blur(16px) saturate(1.15);
  border: 1px solid rgba(115, 103, 240, 0.12) !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85) inset,
    0 14px 40px rgba(115, 103, 240, 0.08) !important;
}

.dash-dues-panel__header {
  padding-bottom: 0.85rem !important;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(255, 255, 255, 0) 100%
  ) !important;
}

.dash-dues-panel__body--modern {
  padding: 0 1rem 1.1rem 1.25rem !important;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  align-items: stretch;
}

.dash-panel__heading--dues {
  width: 100%;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 0.65rem;
}

.dash-panel__heading-main {
  flex: 1 1 auto;
  min-width: 0;
}

.dash-dues-total-pill--dock {
  margin-left: auto;
  flex-shrink: 0;
  text-align: right;
  min-width: 8.25rem;
  max-width: 12rem;
  padding: 0.45rem 0.85rem;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(254, 242, 242, 0.95) 0%,
    rgba(255, 255, 255, 0.88) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(239, 68, 68, 0.22);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 8px 22px rgba(239, 68, 68, 0.1);

  strong {
    font-size: 0.875rem;
    line-height: 1.25;
    word-break: break-word;
  }
}

.dash-dues-total-pill {
  text-align: right;
  padding: 0.4rem 0.75rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(239, 68, 68, 0.2);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.08);

  &__label {
    display: block;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #f87171;
  }

  strong {
    font-size: 0.95rem;
    font-weight: 800;
    color: #991b1b;
    font-variant-numeric: tabular-nums;
  }
}

.dash-dues-scroll {
  flex: 1 1 auto;
  min-height: 280px;
  max-height: none;
  overflow: visible;
  overscroll-behavior: contain;
  padding: 0.2rem 0.35rem 0.2rem 0.15rem;
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
}

.dash-dues-mosaic {
  width: 100%;
  max-width: 100%;
  margin-left: 0;
  margin-right: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem 0.75rem;
  align-content: start;
  flex: 1 1 auto;
}

.dash-dues-tile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  padding: 0.7rem 0.75rem 0.7rem 0.85rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.58);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 8px 22px rgba(115, 103, 240, 0.07);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  animation: dashDuesTileIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) var(--tile-delay, 0s)
    both;

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 1) inset,
      0 12px 28px rgba(115, 103, 240, 0.12);
  }
}

@keyframes dashDuesTileIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.dash-dues-tile__ring-wrap {
  position: relative;
  flex-shrink: 0;
  width: 3.35rem;
  height: 3.35rem;
  display: grid;
  place-items: center;
}

.dash-dues-tile__svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.dash-dues-tile__track {
  fill: none;
  stroke: rgba(235, 233, 241, 0.95);
  stroke-width: 4;
}

.dash-dues-tile__progress {
  --circ: 113.1;
  --pct: 0;
  fill: none;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-dasharray: var(--circ);
  stroke-dashoffset: var(--circ);
  filter: drop-shadow(0 0 4px rgba(115, 103, 240, 0.25));
  animation: dashDuesRingFill 1.35s cubic-bezier(0.22, 1, 0.36, 1) var(--ring-delay, 0s)
    forwards;
}

@keyframes dashDuesRingFill {
  to {
    stroke-dashoffset: calc(var(--circ) * (1 - var(--pct) / 100));
  }
}

.dash-dues-tile__ring-inner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 800;
  color: #2f2b3d;
  font-variant-numeric: tabular-nums;
}

.dash-dues-tile__body {
  min-width: 0;
  flex: 1;
  text-align: left;
  padding-right: 0.25rem;
}

.dash-dues-tile__ring {
  display: none;
}

.dash-dues-tile__title {
  margin: 0 0 0.25rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2f2b3d;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dash-dues-tile__amount {
  margin: 0 0 0.35rem;
  font-size: 0.875rem;
  font-weight: 800;
  color: #2f2b3d;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.dash-dues-tile__students {
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #7367f0;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  background: rgba(115, 103, 240, 0.1);
  border: 1px solid rgba(115, 103, 240, 0.12);
}

@media (max-width: 575px) {
  .dash-dues-mosaic {
    grid-template-columns: 1fr;
  }
}

/* legacy feed (unused) */
.dash-dues-feed {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.dash-dues-feed__item {
  padding: 1rem 1.05rem;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #fff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  }
}

.dash-dues-feed__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.65rem 1rem;
  margin-bottom: 0.65rem;
}

.dash-dues-feed__title-wrap {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
  flex: 1 1 12rem;
}

.dash-dues-feed__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.9);
}

.dash-dues-feed__title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.35;
}

.dash-dues-feed__nums {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.65rem 1rem;
}

.dash-dues-feed__students {
  font-size: 0.8125rem;
  font-weight: 800;
  color: #475569;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e2e8f0;
}

.dash-dues-feed__students-label {
  font-weight: 600;
  color: #94a3b8;
  margin-left: 0.15rem;
}

.dash-dues-feed__amount {
  font-size: 0.9375rem;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.dash-dues-feed__track {
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.dash-dues-feed__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.dash-dues-empty-modern {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

@media (max-width: 767px) {
  .dash-dues-panel__header .dash-panel__heading--dues,
  .dash-panel__heading--chart {
    flex-wrap: wrap;
  }

  .dash-dues-total-pill--dock,
  .dash-chart-summary--dock {
    width: 100%;
    max-width: none;
    margin-left: 0;
    text-align: left;
  }

  .dash-dues-mosaic {
    max-width: none;
    margin-left: 0;
  }

  .dash-dues-scroll {
    justify-content: stretch;
  }
}

@media (max-width: 767px) {
  .dash-dues-panel__header .dash-panel__heading {
    flex-wrap: wrap;
  }

  .dash-dues-total-pill {
    width: 100%;
    text-align: left;
    margin-top: 0.5rem;
  }
}

.dash-dues-row {
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  align-items: stretch;

  > .dash-dues-col {
    display: flex;
    flex-direction: column;
    margin-bottom: 1.25rem;
  }
}

.dash-dues-panel,
.dash-dues-chart-panel,
.dash-dues-chart-wrap {
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  max-height: none;
  overflow: hidden;
}

.dash-dues-panel__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dash-table-scroll {
  max-height: min(420px, 55vh);
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.35rem;

  &--dues {
    flex: 1 1 auto;
    min-height: 240px;
    max-height: none;
    height: 100%;
  }

  .table {
    margin-bottom: 0;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    box-shadow: 0 1px 0 #e2e8f0;
  }
}

.dash-panel {
  border-radius: 24px !important;
  border: 1px solid #e8ecf2 !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 16px 40px rgba(15, 23, 42, 0.06) !important;
  overflow: hidden;
  background: #fff;
}

.dash-charts-row {
  margin-top: 0.5rem;
  row-gap: 1rem;
}

.dash-charts-row--yearly {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
}

.dash-panel__header {
  padding: 1.15rem 1.35rem 0.85rem !important;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.dash-panel__heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dash-panel__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--dues {
    background: #fef2f2;
    color: #dc2626;
  }

  &--chart {
    background: #f1f5f9;
    color: #475569;
  }
}

.dash-panel__icon--collection {
  background: #f5f3ff;
  color: #7c3aed;
}

.dash-panel__icon--yearly {
  background: #ecfdf5;
  color: #16a34a;
}

.dash-panel__title {
  font-size: 1.05rem !important;
  font-weight: 800 !important;
  color: #1e293b !important;
}

.dash-panel__subtitle {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  margin-top: 0.1rem;
}

.dash-dues-table {
  font-size: 0.875rem;

  thead th {
    background: #f8fafc !important;
    border-bottom: 1px solid #e2e8f0 !important;
    border-top: none !important;
    padding: 0.85rem 1.15rem !important;
    vertical-align: middle;
  }

  tbody td {
    padding: 0.85rem 1.15rem !important;
    vertical-align: middle;
    border-color: #f1f5f9 !important;
    color: #334155;
  }

  tbody tr:hover {
    background: #f8fafc !important;
  }
}

.dash-dues-th {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #64748b;
}

.dash-dues-fee {
  font-weight: 700;
  color: #1e293b;
}

.dash-dues-count {
  display: inline-block;
  min-width: 2rem;
  text-align: center;
  font-weight: 800;
  color: #475569;
  background: #f1f5f9;
  padding: 0.2rem 0.55rem;
  border-radius: 8px;
}

.dash-dues-amount {
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.dash-dues-busy {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dash-dues-chart-panel .card-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0.5rem 0.75rem 1rem !important;
}

.dash-dues-chart-panel .dash-chart-viewport-inner {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.dash-chart-canvas {
  position: relative;
  min-height: 320px;
  height: 360px;
  width: 100%;
}

.scholarship-stats-card {
  row-gap: 1rem;

  .stats-card {
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    transition: all 0.3s ease-in-out;
    cursor: pointer;
    height: 100%;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 32px 0 rgba(34, 41, 47, 0.15);
    }
  }

  /* Scholarship summary — student status breakdown (single card, three counts) */
  .scholarship-status-card {
    display: flex;
    flex-direction: column;

    &__head {
      margin-bottom: 0.75rem;
    }

    &__metrics {
      margin-top: auto;
      padding-top: 0.25rem;
    }

    &__metric {
      position: relative;
      // padding-top: 0.35rem;
      // padding-bottom: 0.35rem;

      &:not(:last-child)::after {
        content: "";
        position: absolute;
        top: 15%;
        right: 0;
        height: 70%;
        width: 1px;
        background: rgba(255, 255, 255, 0.28);
      }
    }

    &__value {
      font-size: 1.55rem;
      font-weight: 700;
      line-height: 1.15;
      color: #fff;
      margin-bottom: 0.15rem;
    }

    &__gender {
      font-size: 1.25rem;
      font-weight: 700;
      line-height: 1.15;
      color: #fff;
      margin-bottom: 0.15rem;
    }

    &__label {
      display: block;
      font-size: 0.65rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: rgba(255, 255, 255, 0.88);
      margin-bottom: 0;
    }
  }
}

// House Colors Demographics (Elixir)
.hcd-card {
  border: none;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
  border-radius: 0.75rem;
  overflow: hidden;

  .hcd-card-header {
    border-bottom: 1px solid #f3f2f7;
    padding-bottom: 0.75rem;
  }

  .hcd-total-badge {
    font-size: 0.8rem;
    font-weight: 600;
    padding: 0.35rem 0.85rem;
  }
}

.hcd-top-row {
  margin-bottom: 0;
}

.hcd-stats-panel {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
}

.hcd-stat-row {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  background: #fff;
  border: 1.5px solid #ebe9f1;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: #d8d6de;
    box-shadow: 0 2px 10px rgba(115, 103, 240, 0.08);
  }
}

.hcd-stat-row-active {
  border-color: var(--house-color, #7367f0);
  background: linear-gradient(90deg, rgba(115, 103, 240, 0.06) 0%, #fff 100%);
  box-shadow: 0 2px 12px rgba(115, 103, 240, 0.12);
}

.hcd-stat-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--house-color, #7367f0);
  margin-top: 0.35rem;
  flex-shrink: 0;
}

.hcd-stat-body {
  flex: 1;
  min-width: 0;
}

.hcd-stat-name {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6e6b7b;
}

.hcd-stat-count {
  font-size: 1.15rem;
  font-weight: 800;
  color: #5e5873;
  line-height: 1;
}

.hcd-stat-bar {
  .progress-bar {
    background-color: var(--house-color, #7367f0) !important;
  }
}

.hcd-stat-pct {
  font-size: 0.72rem;
  font-weight: 600;
  color: #b9b9c3;
}

.hcd-chart-panel {
  background: #fafafe;
  border: 1px solid #ebe9f1;
  border-radius: 0.65rem;
  padding: 0.85rem 1rem 0.25rem;
  height: 100%;
  min-height: 200px;
}

.hcd-chart-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  gap: 0.5rem;
}

.hcd-chart-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #5e5873;
}

.hcd-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  color: #b9b9c3;
  font-size: 0.875rem;
}

.hcd-data-panel {
  border: 1px solid rgba(115, 103, 240, 0.15);
  border-radius: 0.65rem;
  overflow: hidden;
  background: #fff;
}

.hcd-data-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1rem;
  background: linear-gradient(90deg, #f3f0ff 0%, #fafafe 100%);
  border-bottom: 1px solid #ebe9f1;

  h6 {
    font-weight: 700;
    color: #5e5873;
    display: flex;
    align-items: center;
  }
}

.hcd-table-wrap {
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d8d6de;
    border-radius: 4px;
  }
}

.hcd-student-table {
  font-size: 0.82rem;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #7367f0 !important;
    color: #fff !important;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border: none !important;
    padding: 0.55rem 0.75rem;
    white-space: nowrap;
  }

  tbody td {
    padding: 0.45rem 0.75rem;
    vertical-align: middle;
    border-color: #f3f2f7;
  }

  &.table-striped tbody tr:nth-of-type(odd) {
    background-color: #fafafe;
  }

  &.table-hover tbody tr:hover {
    background-color: #f3f0ff;
  }
}

.hcd-gender-badge {
  font-size: 0.68rem;
}

.hcd-empty-state {
  text-align: center;
  color: #b9b9c3;
  font-size: 0.875rem;
  padding: 1.25rem;
}

// App Downloads Custom Styles
.app-downloads-card {
  overflow: hidden;

  .stats-card {
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    transition: all 0.3s ease-in-out;
    cursor: pointer;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 32px 0 rgba(34, 41, 47, 0.15);
    }
  }

  .bg-gradient-primary {
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  }

  .bg-gradient-success {
    background: linear-gradient(135deg, #28c76f 0%, #48da89 100%);
  }

  .bg-gradient-info {
    background: linear-gradient(135deg, #00cfe8 0%, #1ce7ff 100%);
  }

  .bg-gradient-warning {
    background: linear-gradient(135deg, #ff9f43 0%, #ffb976 100%);
  }

  // Campus Section Header
  .campus-section-header {
    background: linear-gradient(
      135deg,
      rgba(115, 103, 240, 0.08) 0%,
      rgba(115, 103, 240, 0.02) 100%
    );
    padding: 1.25rem;
    border-radius: 0.75rem;
    margin-bottom: 1.5rem;
    border: 1px solid rgba(115, 103, 240, 0.1);
    transition: all 0.3s ease;

    &:hover {
      border-color: rgba(115, 103, 240, 0.2);
      box-shadow: 0 4px 12px rgba(115, 103, 240, 0.1);
    }
  }

  // Search Input Wrapper
  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 250px;

    .search-icon {
      position: absolute;
      left: 0.85rem;
      color: #b9b9c3;
      pointer-events: none;
      z-index: 1;
      transition: color 0.2s ease;
    }

    .search-input {
      padding-left: 2.5rem;
      padding-right: 2.5rem;
      border-radius: 2rem;
      border: 1.5px solid #d8d6de;
      transition: all 0.3s ease;
      height: 38px;

      &:focus {
        border-color: #7367f0;
        box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.15);

        ~ .search-icon {
          color: #7367f0;
        }
      }

      &::placeholder {
        color: #b9b9c3;
        font-size: 0.9rem;
      }
    }

    .clear-btn {
      position: absolute;
      right: 0.25rem;
      padding: 0.25rem 0.5rem;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(115, 103, 240, 0.1);
        transform: rotate(90deg);
      }
    }
  }

  // Sort Dropdown
  .sort-dropdown {
    .btn {
      border-radius: 0.5rem;
      font-weight: 500;
      transition: all 0.3s ease;
      min-width: 80px;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(115, 103, 240, 0.2);
      }

      // Fix white text issue
      &.btn-outline-primary {
        color: #7367f0 !important;

        &:hover,
        &:focus,
        &:active,
        &.active,
        &.show {
          color: #7367f0 !important;
          background-color: rgba(115, 103, 240, 0.08);
          border-color: #7367f0;
        }

        // Ensure dropdown toggle doesn't turn white
        .feather-icon,
        span {
          color: inherit !important;
        }
      }
    }

    .dropdown-menu {
      border-radius: 0.5rem;
      border: none;
      box-shadow: 0 8px 24px rgba(34, 41, 47, 0.15);
      padding: 0.5rem;
      min-width: 200px;

      .dropdown-item {
        border-radius: 0.35rem;
        padding: 0.65rem 1rem;
        margin-bottom: 0.25rem;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        color: #6e6b7b;

        &:hover {
          background: linear-gradient(
            90deg,
            rgba(115, 103, 240, 0.1) 0%,
            rgba(115, 103, 240, 0.05) 100%
          );
          transform: translateX(5px);
          color: #7367f0;
        }

        &:active {
          background: rgba(115, 103, 240, 0.15);
          color: #7367f0;
        }
      }

      .dropdown-divider {
        margin: 0.5rem 0;
        border-color: rgba(115, 103, 240, 0.1);
      }
    }
  }

  .campus-download-table {
    thead {
      th {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-bottom: 2px solid #dee2e6 !important;
        font-weight: 700;
        color: #2c3e50;
        padding: 1rem 0.75rem;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
    }

    tbody {
      tr {
        transition: all 0.25s ease;
        border-bottom: 1px solid #f1f3f5;

        &:hover {
          background: linear-gradient(
            90deg,
            #f8f9fd 0%,
            transparent 100%
          ) !important;
          transform: translateX(3px);
          box-shadow: -3px 0 0 0 #7367f0;
        }

        td {
          padding: 1.25rem 0.75rem;
          vertical-align: middle;
        }
      }
    }

    // Professional Rank Badge
    .rank-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      font-weight: 700;
      transition: all 0.3s ease;
      cursor: default;

      &.rank-gold {
        background: linear-gradient(135deg, #f39c12 0%, #f1c40f 100%);
        color: #ffffff;
        box-shadow: 0 4px 15px rgba(243, 156, 18, 0.3);

        .feather-icon {
          color: #ffffff;
        }
      }

      &.rank-silver {
        background: linear-gradient(135deg, #95a5a6 0%, #bdc3c7 100%);
        color: #ffffff;
        box-shadow: 0 4px 15px rgba(149, 165, 166, 0.3);

        .feather-icon {
          color: #ffffff;
        }
      }

      &.rank-bronze {
        background: linear-gradient(135deg, #d35400 0%, #e67e22 100%);
        color: #ffffff;
        box-shadow: 0 4px 15px rgba(211, 84, 0, 0.3);

        .feather-icon {
          color: #ffffff;
        }
      }

      &.rank-default {
        background: #f8f9fa;
        color: #6c757d;
        border: 2px solid #e9ecef;
        font-size: 0.85rem;
      }

      &:hover {
        transform: scale(1.08);
      }
    }

    // Professional Campus Cell
    .campus-cell {
      .campus-icon-wrapper {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 0.75rem;

        .campus-icon {
          color: #ffffff;
        }
      }

      .campus-name {
        font-weight: 600;
        color: #2c3e50;
        font-size: 0.95rem;
        margin-bottom: 0.1rem;
      }

      .campus-subtitle {
        color: #95a5a6;
        font-size: 0.8rem;
      }
    }

    // Professional Download Cell
    .download-cell {
      .download-main {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.5rem;

        .download-icon-badge {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;

          &.download-excellent {
            background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
            color: #ffffff;
            box-shadow: 0 4px 12px rgba(40, 167, 69, 0.25);
          }

          &.download-good {
            background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
            color: #ffffff;
            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.25);
          }

          &.download-fair {
            background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
            color: #ffffff;
            box-shadow: 0 4px 12px rgba(23, 162, 184, 0.25);
          }

          &.download-low {
            background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
            color: #ffffff;
            box-shadow: 0 4px 12px rgba(108, 117, 125, 0.25);
          }
        }

        .download-info {
          .download-count {
            font-size: 1.35rem;
            font-weight: 700;
            color: #2c3e50;
            line-height: 1;
          }

          .download-label {
            font-size: 0.7rem;
            color: #95a5a6;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-top: 0.15rem;
          }
        }
      }

      .performance-tag {
        display: inline-flex;
        align-items: center;
        padding: 0.35rem 0.75rem;
        border-radius: 6px;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.3px;

        &.performance-excellent {
          background: #d4edda;
          color: #155724;
        }

        &.performance-good {
          background: #d1ecf1;
          color: #0c5460;
        }

        &.performance-low {
          background: #f8d7da;
          color: #721c24;
        }
      }
    }

    // Professional Progress Cell
    .progress-cell {
      min-width: 220px;

      .progress-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;

        .progress-stats {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          .stat-icon {
            width: 28px;
            height: 28px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;

            &.student-icon {
              background: #e3f2fd;
              color: #1976d2;
            }

            &.staff-icon {
              background: #fff3e0;
              color: #f57c00;
            }
          }

          .stat-numbers {
            display: flex;
            align-items: baseline;
            gap: 0.25rem;

            .current-value {
              font-size: 1rem;
              font-weight: 700;
              color: #2c3e50;
            }

            .separator {
              color: #bdc3c7;
              font-weight: 500;
            }

            .total-value {
              font-size: 0.9rem;
              color: #95a5a6;
              font-weight: 500;
            }
          }
        }

        .progress-percentage {
          font-size: 0.85rem;
          font-weight: 700;
          padding: 0.25rem 0.6rem;
          border-radius: 4px;

          &.percentage-high {
            background: #d4edda;
            color: #155724;
          }

          &.percentage-medium {
            background: #cce5ff;
            color: #004085;
          }

          &.percentage-low {
            background: #fff3cd;
            color: #856404;
          }

          &.percentage-critical {
            background: #f8d7da;
            color: #721c24;
          }
        }
      }

      .progress-bar-container {
        .modern-progress {
          border-radius: 10px;
          overflow: hidden;
          background: #e9ecef;

          .progress-bar {
            transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }
      }
    }

    // Professional Active Users Cell
    .active-users-cell {
      .active-total {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        margin-bottom: 0.6rem;

        .active-icon-wrapper {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          display: flex;
          align-items: center;
          justify-content: center;

          .active-icon {
            color: #ffffff;
          }
        }

        .active-count {
          font-size: 1.5rem;
          font-weight: 700;
          color: #28a745;
        }
      }

      .active-breakdown {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .breakdown-item {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.3rem 0.65rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;

          &.students {
            background: #e3f2fd;
            color: #1976d2;
          }

          &.staff {
            background: #fff3e0;
            color: #f57c00;
          }
        }

        .breakdown-separator {
          color: #dee2e6;
          font-weight: 600;
        }
      }
    }

    // Professional Strength Cell
    .strength-cell {
      .strength-main {
        display: inline-flex;
        align-items: center;
        gap: 0.65rem;
        padding: 0.5rem 0.85rem;
        border-radius: 8px;
        transition: all 0.3s ease;

        &.size-large {
          background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
          border: 1px solid #b1dfbb;
        }

        &.size-medium {
          background: linear-gradient(135deg, #cce5ff 0%, #b8daff 100%);
          border: 1px solid #9fcdff;
        }

        &.size-small {
          background: linear-gradient(135deg, #fff3cd 0%, #ffe8a1 100%);
          border: 1px solid #ffd966;
        }

        &:hover {
          transform: scale(1.03);
        }

        .strength-icon-wrapper {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;

          .feather-icon {
            color: #2c3e50;
          }
        }

        .strength-info {
          .strength-count {
            font-size: 1.1rem;
            font-weight: 700;
            color: #2c3e50;
            line-height: 1;
          }

          .strength-label {
            font-size: 0.7rem;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-top: 0.1rem;
          }
        }
      }
    }

    // Professional Empty State
    .empty-state {
      text-align: center;
      padding: 3rem 2rem;

      .empty-icon {
        color: #cbd5e0;
        margin-bottom: 1rem;
      }

      .empty-text {
        color: #6c757d;
        font-size: 1rem;
        font-weight: 500;
        margin-bottom: 0.5rem;
      }

      .empty-hint {
        color: #95a5a6;
        font-size: 0.85rem;
      }
    }
  }
}

.app-stats-filter-wrap {
  .app-stats-filter-select {
    min-width: 140px;
  }

  ::v-deep .vs__dropdown-toggle {
    min-height: 30px;
    background: rgba(255, 255, 255, 0.95);
    border: 0;
    border-radius: 8px;
    padding: 0 4px;
  }

  ::v-deep .vs__selected,
  ::v-deep .vs__search,
  ::v-deep .vs__placeholder {
    font-size: 0.72rem;
    margin: 0;
    padding: 2px 4px;
    color: #2d3436;
  }

  ::v-deep .vs__selected {
    background: #eef2ff;
    border: 0;
    border-radius: 6px;
  }

  ::v-deep .vs__actions {
    padding: 0 4px;
  }
}

// Stats Circle Buttons
.stats-circle-btn {
  width: 28px;
  height: 28px;
  background-color: white !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &--loading {
    cursor: wait;
    pointer-events: none;
  }

  .feather {
    stroke-width: 3;
  }
}

// Professional Insights Modal (Compact)
.professional-insights-modal {
  .modal-content {
    border: none;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    background-color: #fff;
  }
}

/* App Download Insights — denser contrast (not full dark mode) */
.app-download-insights-modal {
  .modal-dialog {
    width: min(94vw, 1440px) !important;
    max-width: min(94vw, 1440px) !important;
    height: calc(100vh - 40px);
    max-height: calc(100vh - 40px);
    margin: 20px auto !important;
    overflow: visible !important;
  }

  .modal-content,
  .app-download-insights-content {
    background: #f0eef6 !important;
    box-shadow: 0 18px 50px rgba(28, 24, 48, 0.28) !important;
    border: 1px solid #d5d0e4 !important;
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden !important;
  }

  .modal-body,
  .app-download-insights-body {
    background: #f0eef6;
    color: #1f1a33;
    padding: 0.75rem 1rem 0.85rem !important;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden !important;
  }

  .app-insights-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .user-list-scroll-view {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none !important;
    overflow-y: auto;
    overflow-x: auto;
  }

  .app-insights-loading-layer {
    position: absolute;
    inset: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: rgba(240, 238, 246, 0.82);
    backdrop-filter: blur(2px);
  }

  .app-insights-loading-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 20px 28px;
    border: 1px solid #d5d0e4;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 10px 28px rgba(28, 24, 48, 0.16);
    font-size: 12px;
    font-weight: 700;
    color: #4f46c8;
  }

  .app-insights-title {
    color: #1a152e !important;
    letter-spacing: -0.02em;
  }

  .app-insights-subtitle {
    color: #5c5675 !important;
  }

  .app-insights-close {
    color: #3d3758 !important;
    background: #e4e0f0 !important;

    &:hover {
      background: #d6d0e6 !important;
      color: #1a152e !important;
    }
  }

  .app-insights-toolbar {
    background: #e8e4f2 !important;
    border-color: #cfc8e0 !important;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45);
    overflow: visible !important;
    position: relative;
    z-index: 5;
  }

  .app-insights-filter {
    min-width: 280px;
    max-width: 360px;
    width: 320px;
    flex: 1 1 280px;
    position: relative;
    z-index: 20;
    overflow: visible !important;
  }

  .app-insights-filter-label {
    color: #5c5675;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .app-insights-filter-select {
    width: 100%;

    ::v-deep .vs__dropdown-toggle {
      background: #fff;
      border: 1px solid #d8d6de;
      border-radius: 0.357rem;
      min-height: 38px;
      height: auto;
      padding: 2px 6px;
    }

    ::v-deep .vs__selected-options {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 2px;
      padding: 2px 0;
      margin: 0;
    }

    ::v-deep .vs__selected {
      margin: 2px 4px 2px 0;
      padding: 0 0.4em;
      line-height: 1.4;
      font-size: 0.85rem;
      max-width: 100%;
    }

    ::v-deep .vs__search,
    ::v-deep .vs__search:focus {
      margin: 0;
      padding: 0 4px;
      line-height: 1.4;
      font-size: 0.85rem;
    }

    ::v-deep .vs__dropdown-menu {
      position: absolute !important;
      top: calc(100% + 2px) !important;
      left: 0 !important;
      width: 100% !important;
      min-width: 100% !important;
      z-index: 1055 !important;
      max-height: 240px;
      overflow-y: auto;
    }
  }

  .modal-content,
  .app-download-insights-content,
  .modal-body,
  .app-download-insights-body {
    overflow: visible !important;
  }

  .app-insights-search {
    background: transparent !important;
    color: #1f1a33 !important;
    font-weight: 600;

    &::placeholder {
      color: #7a7394 !important;
      opacity: 1;
    }
  }

  .app-insights-tab.btn-outline-primary {
    color: #3d3470 !important;
    border-color: #b8afd4 !important;
    background: #f7f5fc !important;
    font-weight: 700 !important;

    &:hover {
      background: #ebe6f8 !important;
      border-color: #7367f0 !important;
      color: #2d2460 !important;
    }
  }

  .app-insights-tab.btn-primary {
    background: #5b4fd6 !important;
    border-color: #5b4fd6 !important;
    color: #fff !important;
    box-shadow: 0 4px 12px rgba(91, 79, 214, 0.35);
  }

  .app-insights-meta {
    color: #4a4463 !important;
    font-weight: 600;

    strong {
      color: #4f46c8 !important;
    }
  }

  .app-insights-name {
    color: #1a152e !important;
  }

  .professional-table-compact {
    background: #fff;
    border: 1px solid #d5d0e4;
    border-radius: 10px;
    overflow: hidden;

    thead th {
      background-color: #ddd8eb !important;
      border-bottom: 1px solid #c4bdd6 !important;
      color: #2c2648 !important;

      span {
        color: #2c2648 !important;
        letter-spacing: 0.04em !important;
      }
    }

    tbody tr {
      background: #fff;

      td {
        border-bottom: 1px solid #e4e0ef !important;
        color: #2a2444 !important;
      }

      &:hover td {
        background: #f3f0fa !important;
      }
    }

    .text-muted {
      color: #5c5675 !important;
    }

    .text-primary {
      color: #4f46c8 !important;
    }
  }

  .app-insights-avatar.b-avatar {
    font-weight: 800 !important;
  }

  .app-pending-mail-btn {
    border-color: #8b82c9 !important;
    color: #4f46c8 !important;
    background: #efeefc !important;

    &:hover:not(:disabled) {
      background: #5b4fd6 !important;
      border-color: #5b4fd6 !important;
      color: #fff !important;
    }

    &:disabled {
      opacity: 0.4;
    }
  }

  .user-list-scroll-view {
    scrollbar-color: #5b4fd6 #d8d2e6;

    &::-webkit-scrollbar-track {
      background: #d8d2e6;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #5b4fd6;

      &:hover {
        background-color: #4a3fc4;
      }
    }

    &::-webkit-scrollbar-corner {
      background: #d8d2e6;
    }
  }

  .bg-white-transparent {
    background-color: rgba(240, 238, 246, 0.85);
  }
}

.status-dot-sm {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.professional-table-compact {
  thead th {
    background-color: #fbfbfd !important;
    border-bottom: 1px solid #eaebf1 !important;
    padding: 0.85rem 0.75rem !important;
  }

  tbody tr {
    td {
      padding: 0.75rem 0.75rem !important;
      border-bottom: 1px solid #f8f9fa;
    }
  }
}

.bg-white-transparent {
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
}

.border-radius-8 {
  border-radius: 8px !important;
}

.user-list-scroll-view {
  /* Taller viewport → proportionally longer thumb vs very long lists */
  max-height: min(65vh, 520px);
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-gutter: stable;

  /* Wider, easier-to-grab scrollbar (Firefox) */
  scrollbar-width: auto;
  scrollbar-color: rgba(115, 103, 240, 0.65) #e4e4ee;

  &::-webkit-scrollbar {
    width: 18px;
    height: 18px;
  }

  &::-webkit-scrollbar-track {
    background: #e4e4ee;
    border-radius: 12px;
    margin: 6px 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(115, 103, 240, 0.65);
    border-radius: 10px;
    /* Solid thumb fills full 18px lane — easier to see and drag */

    &:hover {
      background-color: rgba(115, 103, 240, 0.9);
    }

    &:active {
      background-color: rgba(92, 81, 210, 1);
    }
  }

  &::-webkit-scrollbar-corner {
    background: #e4e4ee;
  }
}

@keyframes glow {
  0%,
  100% {
    box-shadow: 0 0 5px rgba(115, 103, 240, 0.4);
  }

  50% {
    box-shadow: 0 0 20px rgba(115, 103, 240, 0.8);
  }
}

// Progress bar enhancements
.progress {
  background-color: rgba(255, 255, 255, 0.2);

  .progress-bar {
    transition: width 0.6s ease;
  }
}

// Avatar enhancements
.avatar {
  transition: all 0.3s ease;

  &:hover {
    transform: rotate(10deg) scale(1.1);
  }
}

// Badge enhancements
.badge {
  font-weight: 500;
  letter-spacing: 0.3px;
}
.session-filter-wrapper {
  position: relative;
  min-width: 150px;
}

.app-bundle-select-wrapper {
  position: relative;
  min-width: 190px;
  width: 190px;
}

.app-bundle-select .vs__dropdown-toggle {
  width: 100%;
}

.app-bundle-select .vs__selected-options {
  flex-wrap: nowrap;
  overflow: hidden;
}

.app-bundle-select .vs__selected {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-bundle-select.vs--open .vs__dropdown-toggle {
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

.session-select-compact .vs__dropdown-toggle {
  background: #fff;
  border: 1.5px solid #f0f0f5;
  border-radius: 20px;
  height: 38px;
  padding-left: 0.8rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.session-select-compact .vs__selected {
  font-size: 0.88rem;
  font-weight: 600;
  color: #2d1b69;
  margin: 0;
  padding: 0;
}

.session-select-compact .vs__actions {
  padding-right: 0.5rem;
}

.session-select-compact .vs__search::placeholder {
  font-size: 0.88rem;
  font-weight: 500;
  color: #b9b9c3;
}

.session-select-compact:hover .vs__dropdown-toggle {
  border-color: #7367f0;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.1);
}

.session-select-compact.vs--open .vs__dropdown-toggle {
  border-color: #7367f0;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

/* --- House Colors Demographics (HCD) Styling --- */
.hcd-card {
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f5;
  transition: all 0.3s ease;
}

.hcd-card--paired {
  margin: 0 !important;
  border-radius: 18px !important;
  border-color: #e8ecf4 !important;
  box-shadow: 0 6px 22px rgba(15, 23, 42, 0.05);
  min-height: 580px;
  max-height: 580px;
  display: flex;
  flex-direction: column;

  .hcd-card-header {
    padding: 1rem 1.1rem !important;
    flex-shrink: 0;
  }

  .hcd-card__title {
    font-size: 1.05rem;
  }

  .hcd-card__body {
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
    padding-top: 0.75rem !important;
    scrollbar-width: thin;
  }

  .hcd-stats-panel {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-right: 0;
  }

  .hcd-stat-row {
    flex: 1 1 calc(50% - 0.5rem);
    min-width: 8.5rem;
    padding: 0.5rem 0.65rem;
    margin: 0;
  }

  .hcd-stat-row:hover {
    transform: none;
  }

  .hcd-stat-row-active {
    transform: none;
  }

  .hcd-chart-panel {
    margin-top: 0.35rem;
    padding: 0.85rem 0.9rem 1rem;
    min-height: 260px;
  }

  .hcd-total-badge {
    font-size: 0.78rem;
    padding: 0.35rem 0.65rem;
  }

  .hcd-data-panel--paired {
    margin-top: 0.5rem !important;

    .hcd-data-head h6 {
      font-size: 0.82rem;
    }

    .hcd-table-wrap {
      max-height: 120px;
      overflow: auto;
    }
  }
}

/* Shared dashboard skeleton shimmer (calendar + house demographics) */
.dash-dash-skel-block {
  border-radius: 10px;
  background: linear-gradient(90deg, #eef2ff, #f8fafc, #eef2ff);
  background-size: 200% 100%;
  animation: dashDashSkelPulse 1.25s ease-in-out infinite;
}

@keyframes dashDashSkelPulse {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.hcd-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  min-height: 520px;

  &__houses {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  &__house {
    height: 4.25rem;
  }

  &__chart-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  &__chart-head {
    height: 2.25rem;
    width: 55%;
  }

  &__bars {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    flex: 1;
  }

  &__bar-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__bar-label {
    width: 3.5rem;
    height: 0.65rem;
    flex-shrink: 0;
  }

  &__bar-fill {
    height: 0.85rem;
    border-radius: 999px;
    max-width: 100%;
  }

  &__table {
    height: 5.5rem;
    border-radius: 12px;
  }
}

.hcd-chart-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.35rem 0;
  min-height: 120px;

  &__row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__label {
    width: 3.25rem;
    height: 0.6rem;
    flex-shrink: 0;
  }

  &__bar {
    height: 0.75rem;
    border-radius: 999px;
    animation-delay: calc(var(--i, 0) * 0.08s);
  }
}

.hcd-card:hover {
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
}

.hcd-card-header {
  border-bottom: 1px solid #f0f0f5;
  background-color: #fafafc;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 1.5rem 1.25rem !important;
}

.hcd-total-badge {
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  font-weight: 600;
  box-shadow: 0 4px 10px rgba(115, 103, 240, 0.2);
}

.hcd-stats-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-right: 1rem;
}

.hcd-stat-row {
  display: flex;
  align-items: flex-start;
  padding: 1.25rem 1rem;
  border-radius: 12px;
  border: 1px solid #eaebf0;
  border-left: 6px solid var(--house-color);
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.hcd-stat-row::before {
  display: none;
}

.hcd-stat-row:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

.hcd-stat-row-active {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: var(--house-color) !important;
  background: #fdfdfe;
  transform: scale(1.02);
}

.hcd-stat-row-active::before {
  display: none;
}

.hcd-stat-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--house-color);
  margin-top: 0.25rem;
  margin-right: 1rem;
  box-shadow: 0 0 8px var(--house-color);
  flex-shrink: 0;
}

.hcd-stat-body {
  flex-grow: 1;
}

.hcd-stat-name {
  font-weight: 700;
  color: #374151;
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.hcd-stat-count {
  font-weight: 700;
  font-size: 1.25rem;
  color: #111827;
}

.hcd-stat-bar {
  background-color: #f3f4f6;
  border-radius: 10px;
  overflow: hidden;
}

.hcd-stat-bar .progress-bar {
  background-color: var(--house-color) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hcd-stat-pct {
  display: block;
  text-align: right;
  font-weight: 600;
  color: #6b7280;
  margin-top: 0.25rem;
  font-size: 0.8rem;
}

.hcd-chart-panel {
  background-color: #fafafc;
  border-radius: 12px;
  padding: 1.5rem;
  height: 100%;
  border: 1px solid #eaebf0;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.hcd-chart-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.hcd-chart-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
}

.hcd-data-panel {
  border-radius: 12px;
  border: 1px solid #eaebf0;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.hcd-data-head {
  background: linear-gradient(
    135deg,
    rgba(115, 103, 240, 0.05) 0%,
    rgba(115, 103, 240, 0.1) 100%
  );
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eaebf0;
}

.hcd-data-head h6 {
  font-weight: 700;
  color: #2d3748;
}

.hcd-table-wrap {
  max-height: 400px;
  overflow-y: auto;
}

.hcd-student-table {
  border-collapse: collapse !important;
  border-spacing: 0 !important;
  background-color: transparent !important;
  width: 100%;
}

.hcd-student-table thead th {
  background-color: #ffffff !important;
  color: #2c3e50 !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border: none !important;
  border-bottom: 2px solid #f0f0f5 !important;
  padding: 1.2rem 1rem !important;
}

.hcd-student-table tbody tr {
  background-color: #ffffff !important;
  box-shadow: none !important;
  transition: background-color 0.2s ease !important;
}

/* Zebra striping to match the theme */
.hcd-student-table.table-striped tbody tr:nth-of-type(odd) {
  background-color: #fbfbfd !important;
}

.hcd-student-table.table-striped tbody tr:nth-of-type(even) {
  background-color: #ffffff !important;
}

.hcd-student-table tbody td {
  padding: 1rem 1rem !important;
  vertical-align: middle;
  color: #4b5563 !important;
  font-weight: 500;
  border: none !important;
  border-bottom: 1px solid #f8f9fa !important;
  background-color: transparent !important;
}

.hcd-student-table tbody tr:hover td {
  background-color: #f0f0f8 !important;
  color: #7367f0 !important;
}

.hcd-gender-badge {
  font-weight: 700 !important;
  padding: 0.45rem 0.85rem !important;
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  border-radius: 6px !important;
  letter-spacing: 0.5px !important;
}

.scholarship-skeleton-loader {
  background: linear-gradient(90deg, #f5f6fa 25%, #e9ecef 50%, #f5f6fa 75%);
  background-size: 200% 100%;
  animation: shimmerNew 1.5s infinite;
  border-radius: 8px;
  height: 60px;
}

@keyframes shimmerNew {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* --- Scholarship Stats Card Redesign --- */
.scholarship-card-new {
  background-color: #ffffff;
  border: 1.5px solid #f0f0f5;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  // box-shadow: 0 4px 12px 0 rgba(34, 41, 47, 0.1);
  box-shadow: 0px 5px 4px 1px rgba(34, 41, 47, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100% - 1rem);
}

.scholarship-card-new:hover {
  transform: translateY(-5px);
  // box-shadow: 0 8px 30px 0 rgba(34, 41, 47, 0.2);
}

.scholarship-card-new.border-top-primary {
  border-top: 4px solid #7367f0 !important;
}

.scholarship-card-new.border-top-success {
  border-top: 4px solid #28c76f !important;
}

.scholarship-card-new.border-top-warning {
  border-top: 4px solid #ff9f43 !important;
}

.scholarship-card-new.border-top-info {
  border-top: 4px solid #00cfe8 !important;
}

.scholarship-card-icon-container {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.scholarship-card-icon-container:hover {
  transform: scale(1.1);
}

.scholarship-card-icon-container.bg-light-primary {
  background-color: #f3f2fe;
  color: #7367f0;
}

.scholarship-card-icon-container.bg-light-success {
  background-color: #e8f9f0;
  color: #28c76f;
}

.scholarship-card-icon-container.bg-light-warning {
  background-color: #fff3e8;
  color: #ff9f43;
}

.scholarship-card-icon-container.bg-light-info {
  background-color: #e0f9fc;
  color: #00cfe8;
}

.scholarship-card-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #82868b;
  text-transform: uppercase;
}

.scholarship-card-value {
  font-size: 2.2rem;
  font-weight: 800;
  color: #2c2738;
  margin-top: 0.15rem;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.scholarship-card-divider {
  border: 0;
  border-top: 1.5px solid #f0f0f5;
  margin: 0.5rem 0;
}

.scholarship-card-table {
  box-shadow: 0px 5px 4px 1px rgba(34, 41, 47, 0.3) !important;
}
</style>

<style lang="scss">
/* append-to-body mounts menu on document.body */
.app-bundle-dropdown-menu.vs__dropdown-menu {
  z-index: 1025 !important;
  border-radius: 12px !important;
  border: 1px solid #e0d8ff !important;
  box-shadow: 0 10px 30px rgba(45, 27, 105, 0.15) !important;
  margin-top: 4px !important;
  padding: 0.25rem 0 !important;
  max-height: 220px !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  background: #fff !important;
}

/* Pending composer must sit above the user-list modal (z-index ~1050) */
.b-sidebar.app-pending-msg-sidebar,
#app-pending-msg-sidebar {
  z-index: 11050 !important;
}
.b-sidebar-backdrop:has(+ .b-sidebar.app-pending-msg-sidebar),
.b-sidebar-backdrop:has(+ #app-pending-msg-sidebar) {
  z-index: 11040 !important;
}
.app-pending-template-dropdown.vs__dropdown-menu {
  z-index: 11100 !important;
  border-radius: 12px !important;
  border: 1px solid #e0d8ff !important;
  box-shadow: 0 14px 42px rgba(45, 27, 105, 0.2) !important;
  max-height: 280px !important;
  overflow-y: auto !important;
  background: #fff !important;
}

.app-pending-mail-btn {
  width: 32px;
  height: 32px;
  padding: 0 !important;
}

.app-pending-row-checkbox {
  margin: 0;
}

.app-pending-recipient-card {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.55rem 0.65rem;
  border-radius: 10px;
  background: #f8f7fc;
  border: 1px solid #ece8f8;
}

.app-pending-recipient-card--bulk {
  align-items: flex-start;
}

.app-pending-recipient-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7367f0, #9e95f5);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.app-pending-recipient-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  max-height: 72px;
  overflow-y: auto;
}

.app-pending-recipient-chip {
  display: inline-block;
  max-width: 120px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e4def8;
  font-size: 11px;
  font-weight: 600;
  color: #5e5873;
}

.app-bundle-dropdown-menu .vs__dropdown-option {
  padding: 0.55rem 0.85rem !important;
  font-size: 0.88rem !important;
  font-weight: 600 !important;
  color: #2d1b69 !important;
  white-space: nowrap !important;
}

.app-bundle-dropdown-menu .vs__dropdown-option--highlight {
  background: #7367f0 !important;
  color: #fff !important;
}

/* App download insights: compact contact status cards */
.app-contact-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  min-width: 390px;
}

.app-contact-card {
  position: relative;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid #e7e3f3;
  border-radius: 8px;
  background: #fbfaff;

  &--installed {
    border-color: #cdebd7;
    background: #f6fcf8;
  }

  &--missing {
    border-color: #f5d5d5;
    background: #fff8f8;
  }

  &__top {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
  }

  &__dot {
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    border-radius: 50%;

    &--installed {
      background: #16a34a;
    }

    &--missing {
      background: #dc2626;
    }
  }

  &__role {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b6582;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__send {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    padding: 0 !important;
    color: #5b4fd6 !important;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }

  &__phone {
    display: block;
    margin-top: 3px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--installed {
      color: #15803d !important;
    }

    &--missing {
      color: #dc2626 !important;
    }
  }
}

.app-insights-pagination {
  .page-link {
    border-radius: 8px !important;
    font-size: 11px;
    font-weight: 700;
  }
}
</style>
